import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Form, Row, Col, Badge, Button, InputGroup, Card, Alert } from 'react-bootstrap';
import { getGeocode, getLatLng } from 'use-places-autocomplete';
import AddressAutocomplete from './AddressAutocomplete';
import RouteMap from './RouteMap';
import { getQuote, createRequester, updateRequester, createPassenger, updatePassenger, getPassengers, getRequesters, getAreas, getCostCenters, updateTrip, apiClient, getExchangeRates, getBases, getWhatsAppMessages, getRecommendedDrivers } from '../api';
import useAuthStore from '../../store/useAuthStore';
import SearchableSelect from './SearchableSelect';
import { toast } from 'react-toastify';
import { Home, Briefcase, Plus, Plane, User, Mail, Phone, Shield, Car, MapPin, Search, Clock, Users, X, Pencil, Info, MessageCircle, AlertTriangle, AlertCircle, CheckCircle, RefreshCw, Sparkles, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';

const STATUS_OPTIONS = {
    'draft': 'Pre-Reserva (Por Confirmar / Pagar)',
    'pending': 'Pendiente',
    'assigned': 'Asignado',
    'confirmed': 'Confirmado',
    'en_route': 'En Camino',
    'arrived': 'En Posición',
    'on_board': 'En Ruta',
    'completed': 'Finalizado',
    'cancelled': 'Cancelado',
    'cancelled_paid': 'Cancelado con Cargo',
    'no_show': 'No Show'
};

const formatWaitingMinutes = (minutes) => {
    if (!minutes) return "0 Min";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
        return mins > 0 ? `${hrs} Hrs ${mins} Min` : `${hrs} Hrs`;
    }
    return `${mins} Min`;
};

const TripModal = ({ 
    show, 
    onHide, 
    trip, 
    isEditing, 
    clients, 
    drivers, 
    vehicles, 
    vehicleTypes, 
    contracts,
    onSave,
    isLoaded,
    isLoading
}) => {
    const { user } = useAuthStore();
    // MEJORA: Comparación de rol insensible a mayúsculas para evitar errores de permiso
    const userRole = user?.role?.toLowerCase();
    const isAdmin = ['admin', 'gestor', 'operaciones'].includes(userRole);
    const canSeeInternalNotes = ['admin', 'gestor', 'operaciones', 'locutor'].includes(userRole);

    const [formData, setFormData] = useState({});

    const isParticular = useMemo(() => {
        if (!clients || !formData.client_id) return false;
        const selectedClientObj = clients.find(c => c.id === parseInt(formData.client_id));
        return selectedClientObj && selectedClientObj.segmentation === 'Particulares';
    }, [clients, formData.client_id]);

    const [isCreatingReturn, setIsCreatingReturn] = useState(false);
    const [pickupLocation, setPickupLocation] = useState(null);
    const [dropoffLocation, setDropoffLocation] = useState(null);
    const [routeDistanceKm, setRouteDistanceKm] = useState(0);
    const [baseDistanceKm, setBaseDistanceKm] = useState(0);
    const [routeDurationMin, setRouteDurationMin] = useState(0); 
    const [priceBreakdown, setPriceBreakdown] = useState(null);
    const [availableQuotes, setAvailableQuotes] = useState([]);
    const [loadingRoute, setLoadingRoute] = useState(false);
    const [loadingPricing, setLoadingPricing] = useState(false);
    
    const [airportTargetField, setAirportTargetField] = useState(null);
    const [showAirportModal, setShowAirportModal] = useState(false);
    
    const [generatingAiEmail, setGeneratingAiEmail] = useState(false);
    const [aiEmailData, setAiEmailData] = useState(null);
    const [showAiEmailModal, setShowAiEmailModal] = useState(false);

    const handleGenerateAiEmail = async () => {
        if (!formData.id) return;
        setGeneratingAiEmail(true);
        try {
            const response = await apiClient.post(`/transport/trips/${formData.id}/generate-ai-dispute-email`);
            setAiEmailData(response.data);
            setShowAiEmailModal(true);
            toast.success("Carta formal de disputa generada con éxito por la IA");
        } catch (err) {
            console.error("Error generando correo IA:", err);
            toast.error("No se pudo generar la carta formal. Intente nuevamente.");
        } finally {
            setGeneratingAiEmail(false);
        }
    };

    const handleCopyAiEmail = () => {
        if (!aiEmailData) return;
        const textToCopy = `ASUNTO: ${aiEmailData.subject}\n\n${aiEmailData.body}`;
        navigator.clipboard.writeText(textToCopy);
        toast.success("¡Asunto y cuerpo del correo copiados al portapapeles!");
    };
    
    const [contextPassengers, setContextPassengers] = useState([]);
    const [contextRequesters, setContextRequesters] = useState([]);
    const [contextAreas, setContextAreas] = useState([]);
    const [contextCostCenters, setContextCostCenters] = useState([]);
    const [exchangeRates, setExchangeRates] = useState([]);
    const [bases, setBases] = useState([]);
    const [recommendedDrivers, setRecommendedDrivers] = useState([]);
    
    // REFERENCIA PARA EVITAR SOBREESCRITURA DE PRECIO AL CARGAR EDICIÓN
    const initialEditLoadRef = useRef(false);
    const lastDetectedAddressRef = useRef({ pickup: '', dropoff: '' });

    // Modales de Creación Rápida
    const [showQuickReqModal, setShowQuickReqModal] = useState(false);
    const [forcedDriverPrompt, setForcedDriverPrompt] = useState(null);
    const [showQuickPaxModal, setShowQuickPaxModal] = useState(false);
    const [showQuickAreaModal, setShowQuickAreaModal] = useState(false);
    const [showQuickCCModal, setShowQuickCCModal] = useState(false);
    
    // Modal y Estado de Código de Autorización para particulares pagados (Webpay, Getnet, Mercado Pago)
    const [showAuthCodeModal, setShowAuthCodeModal] = useState(false);
    const [authCodeTemp, setAuthCodeTemp] = useState('');
    const [showExtraServices, setShowExtraServices] = useState(false);

    // Estado para confirmación de pago a chofer en estado "Cancelado con Cargo" (cancelled_paid)
    const [showDriverPaymentConfirm, setShowDriverPaymentConfirm] = useState(false);
    const [pendingSubmitData, setPendingSubmitData] = useState(null);
    const [customPenaltyPct, setCustomPenaltyPct] = useState('');
    const [submittingIncident, setSubmittingIncident] = useState(false);
    const [reversalPreview, setReversalPreview] = useState(null);
    const [loadingReversalPreview, setLoadingReversalPreview] = useState(false);
    const [reimbursementAmountInput, setReimbursementAmountInput] = useState('');
    const [createReimbursementToggle, setCreateReimbursementToggle] = useState(true);
    const [reversalNotesInput, setReversalNotesInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const [whatsappLogs, setWhatsappLogs] = useState([]);
    const [loadingWhatsappLogs, setLoadingWhatsappLogs] = useState(false);
    const [driverConflictWarning, setDriverConflictWarning] = useState(null);
    const [draggedStopIdx, setDraggedStopIdx] = useState(null);

    // Estados para Programación Recurrente (Bulk)
    const [isRepeatChecked, setIsRepeatChecked] = useState(false);
    const [repeatMode, setRepeatMode] = useState('weekly'); // 'weekly' | 'manual'
    const [repeatStartDate, setRepeatStartDate] = useState('');
    const [repeatEndDate, setRepeatEndDate] = useState('');
    const [repeatDays, setRepeatDays] = useState({ 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 0: false });
    const [manualDates, setManualDates] = useState(['']);

    const EXTRA_OPTIONS = [
        { id: 'baby_seat', name: '👶 Silla de Bebé / Booster' },
        { id: 'water', name: '💧 Botella de Agua Mineral' },
        { id: 'pet', name: '🐾 Mascota Pequeña' },
        { id: 'extra_luggage', name: '🧳 Maleta Extra' },
        { id: 'bilingual', name: '🗣️ Conductor Bilingüe' },
        { id: 'parada_intermedia', name: '📍 Parada Intermedia' }
    ];

    const [quickReq, setQuickReq] = useState({ id: null, full_name: '', email: '', phone: '', area_id: '', cost_center_id: '', home_address: '', office_address: '', has_portal_access: false, create_as_passenger: false });
    const [quickPax, setQuickPax] = useState({ id: null, full_name: '', email: '', phone: '', area_id: '', cost_center_id: '', home_address: '', office_address: '', has_portal_access: false });
    const [quickArea, setQuickArea] = useState({ name: '' });
    const [quickCC, setQuickCC] = useState({ code: '', name: '', file: '' });
    const [ccModalTarget, setCcModalTarget] = useState('trip');
    const [areaModalTarget, setAreaModalTarget] = useState('trip');
    
    const [isSameAsRequester, setIsSameAsRequester] = useState(false);
    const [paxSearchTerm, setPaxSearchTerm] = useState('');
    const [customVentaBase, setCustomVentaBase] = useState('');
    const [customCostoBase, setCustomCostoBase] = useState('');
    const [disabledRuleKeys, setDisabledRuleKeys] = useState([]);



    const CHILEAN_AIRPORTS = [
        { code: 'SCL', name: 'SCL - Aeropuerto SCL, Pudahuel, Chile', address: 'Aeropuerto - Aeropuerto SCL, Pudahuel, Chile.', lat: -33.39732166748097, lng: -70.79443028467028 },
        { code: 'CJC', name: 'Aeropuerto El Loa (CJC), Calama', address: 'Aeropuerto El Loa (CJC), Calama, Chile.', lat: -22.4981, lng: -68.9039 },
        { code: 'ANF', name: 'Aeropuerto Andrés Sabella (ANF), Antofagasta', address: 'Aeropuerto Andrés Sabella (ANF), Antofagasta, Chile.', lat: -23.4444, lng: -70.4451 },
        { code: 'CPO', name: 'Aeropuerto Desierto de Atacama (CPO), Copiapó', address: 'Aeropuerto Desierto de Atacama (CPO), Copiapó, Chile.', lat: -27.2611, lng: -70.7792 },
        { code: 'IQQ', name: 'Aeropuerto Diego Aracena (IQQ), Iquique', address: 'Aeropuerto Diego Aracena (IQQ), Iquique, Chile.', lat: -20.5353, lng: -70.1814 },
        { code: 'ARI', name: 'Aeropuerto Chacalluta (ARI), Arica', address: 'Aeropuerto Internacional Chacalluta (ARI), Arica, Chile.', lat: -18.3485, lng: -70.3386 },
        { code: 'LSC', name: 'Aeródromo La Florida (LSC), La Serena', address: 'Aeródromo La Florida (LSC), La Serena, Chile.', lat: -29.9161, lng: -71.2017 },
        { code: 'CCP', name: 'Aeropuerto Carriel Sur (CCP), Concepción', address: 'Aeropuerto Carriel Sur (CCP), Concepción, Chile.', lat: -36.7727, lng: -73.0631 },
        { code: 'PMC', name: 'Aeropuerto El Tepual (PMC), Puerto Montt', address: 'Aeropuerto El Tepual (PMC), Puerto Montt, Chile.', lat: -41.4389, lng: -73.0940 },
        { code: 'ZCO', name: 'Aeropuerto La Araucanía (ZCO), Temuco', address: 'Aeropuerto Internacional La Araucanía (ZCO), Freire, Araucanía, Chile.', lat: -38.9254, lng: -72.6517 },
        { code: 'MDZ', name: 'Aeropuerto El Plumerillo (MDZ), Mendoza', lat: -32.8317, lng: -68.7919 },
        { code: 'EZE', name: 'Aeropuerto Ezeiza (EZE), Buenos Aires', lat: -34.815, lng: -58.5358 },
        { code: 'AEP', name: 'Aeroparque Jorge Newbery (AEP), Buenos Aires', lat: -34.558, lng: -58.4164 },
        { code: 'PUJ', name: 'Aeropuerto Punta Cana (PUJ)', lat: 18.5674, lng: -68.3634 },
        { code: 'SDQ', name: 'Aeropuerto Las Américas (SDQ)', lat: 18.4297, lng: -69.6689 },
        { code: 'LIM', name: 'Aeropuerto Jorge Chávez (LIM)', lat: -12.0219, lng: -77.1143 },
        { code: 'BOG', name: 'Aeropuerto El Dorado (BOG)', lat: 4.7016, lng: -74.1469 }
    ];

    // Función auxiliar para detectar Aeropuerto (con límites de palabra para evitar falsos positivos como 'Margaritas' -> 'ari')
    const isAirportAddress = (address) => {
        if (!address || typeof address !== 'string') return false;
        const lower = address.toLowerCase();
        if (lower.includes('aeropuerto') || lower.includes('airport') || lower.includes('carriel sur') || lower.includes('el tepual') || lower.includes('chacalluta') || lower.includes('el plumerillo') || lower.includes('ezeiza') || lower.includes('pudahuel')) {
            return true;
        }
        const iataCodes = ['scl', 'anf', 'cjc', 'iqq', 'lsc', 'ccp', 'pmc', 'zco', 'ari', 'mdz', 'eze', 'aep', 'puj', 'sdq', 'lim', 'bog', 'cpo', 'araucania'];
        const iataRegex = new RegExp(`\\b(${iataCodes.join('|')})\\b`, 'i');
        return iataRegex.test(address);
    };

    const loadClientContext = useCallback(async (clientId) => {
        if (!clientId || clientId === '') {
            setContextPassengers([]);
            setContextRequesters([]);
            setContextAreas([]);
            setContextCostCenters([]);
            return;
        }

        try {
            console.log("🔍 Cargando contexto para cliente ID:", clientId);
            const pax = await getPassengers(clientId);
            const req = await getRequesters(clientId);
            const areas = await getAreas(clientId);
            const ccs = await getCostCenters(clientId);

            // RESTAURACIÓN: Combinar pasajeros del contexto con los que ya están en el viaje
            let mergedPax = Array.isArray(pax) ? [...pax] : [];
            if (trip?.additional_passengers) {
                trip.additional_passengers.forEach(ap => {
                    if (!mergedPax.find(p => p.id === ap.id)) mergedPax.push(ap);
                });
            }

            setContextPassengers(mergedPax);
            setContextRequesters(Array.isArray(req) ? req : []);
            setContextAreas(Array.isArray(areas) ? areas : []);
            setContextCostCenters(Array.isArray(ccs) ? ccs : []);

        } catch (err) {
            console.error("❌ Error cargando contexto del cliente:", err);
        }
    }, [trip]);

    // CARGAR TASAS DE CAMBIO Y BASES CADA VEZ QUE SE ABRE EL MODAL
    useEffect(() => {
        if (show) {
            const loadData = async () => {
                try {
                    const [rates, basesData] = await Promise.all([
                        getExchangeRates().catch(() => null),
                        getBases().catch(() => [])
                    ]);
                    if (rates) setExchangeRates(rates);
                    if (basesData) setBases(basesData);
                } catch (e) { 
                    console.warn("⚠️ Error cargando tasas o bases:", e); 
                }
            };
            loadData();
        }
    }, [show]);

    useEffect(() => {
        if (show && trip && trip.passenger_phone) {
            const fetchLogs = async () => {
                setLoadingWhatsappLogs(true);
                try {
                    const cleanPhone = trip.passenger_phone.replace(/[^\d]/g, "");
                    if (cleanPhone) {
                        const data = await getWhatsAppMessages(cleanPhone);
                        setWhatsappLogs(data || []);
                    }
                } catch (e) {
                    console.warn("Error al cargar historial de WhatsApp:", e);
                } finally {
                    setLoadingWhatsappLogs(false);
                }
            };
            fetchLogs();
        } else {
            setWhatsappLogs([]);
        }
    }, [show, trip]);

    // ASISTENTE DE EQUIDAD: Cargar conductores recomendados para Santiago (base_id = 1)
    useEffect(() => {
        if (!show) {
            setRecommendedDrivers([]);
            return;
        }

        const originBaseId = formData.origin_base_id 
            ? parseInt(formData.origin_base_id) 
            : (trip?.origin_base_id ? parseInt(trip.origin_base_id) : 1);

        if (originBaseId !== 1) {
            setRecommendedDrivers([]);
            return;
        }

        let isMounted = true;
        const fetchRecommendations = async () => {
            try {
                const params = { base_id: 1 };
                if (formData.scheduled_time) {
                    params.scheduled_time = formData.scheduled_time;
                }
                const res = await getRecommendedDrivers(params);
                if (isMounted && res) {
                    setRecommendedDrivers(res.all_recommendations || []);
                }
            } catch (err) {
                console.warn("⚠️ No se pudieron cargar recomendaciones de equidad en TripModal:", err);
            }
        };

        fetchRecommendations();
        return () => { isMounted = false; };
    }, [show, formData.origin_base_id, formData.scheduled_time, trip?.origin_base_id]);

    useEffect(() => {
        if (show && trip) {
            // BLOQUEAR ACTUALIZACIÓN DE PRECIO EN CARGA INICIAL DE EDICIÓN
            if (isEditing) {
                initialEditLoadRef.current = true;
                lastDetectedAddressRef.current = {
                    pickup: trip.pickup_address || '',
                    dropoff: trip.dropoff_address || ''
                };
            } else {
                initialEditLoadRef.current = false;
                lastDetectedAddressRef.current = { pickup: '', dropoff: '' };
            }

            // NORMALIZAR STATUS (Legacy fix)
            const safeTrip = { ...trip };
            if (!safeTrip.stops) safeTrip.stops = [];
            if (!safeTrip.file && safeTrip.client_file_number) safeTrip.file = safeTrip.client_file_number;
            if (trip.additional_passengers) safeTrip.additional_passenger_ids = trip.additional_passengers.map(p => p.id);
            if (!safeTrip.local_currency_code && safeTrip.origin_base?.currency_code) {
                safeTrip.local_currency_code = safeTrip.origin_base.currency_code;
            }
            
            if (safeTrip.scheduled_time) {
                // Convertir a formato local respetando la zona horaria del servicio (ej: Lima)
                let tripTz = safeTrip.origin_base?.timezone || safeTrip.timezone;
                if (!tripTz) {
                    if (safeTrip.local_currency_code === 'PEN') tripTz = 'America/Lima';
                    else if (safeTrip.local_currency_code === 'ARS') tripTz = 'America/Argentina/Mendoza';
                    else tripTz = 'America/Santiago';
                }
                const d = new Date(safeTrip.scheduled_time);
                // Truco para crear un Date con las horas locales de esa zona
                const tzDate = new Date(d.toLocaleString('en-US', { timeZone: tripTz }));
                const pad = (n) => n.toString().padStart(2, '0');
                const localISO = `${tzDate.getFullYear()}-${pad(tzDate.getMonth()+1)}-${pad(tzDate.getDate())}T${pad(tzDate.getHours())}:${pad(tzDate.getMinutes())}`;
                safeTrip.scheduled_time = localISO;
            }
            
            if (safeTrip.status) safeTrip.status = safeTrip.status.toLowerCase();
            
            // EXTRAER GASTO REEMBOLSABLE SI EXISTE
            const reimbursable = (safeTrip.extra_services || []).find(s => s.id === 'reimbursable_expense');
            if (reimbursable) {
                safeTrip.has_reimbursable_expense = true;
                safeTrip.reimbursable_expense_price = reimbursable.price;
                safeTrip.reimbursable_expense_cost = reimbursable.cost;
                safeTrip.extra_services = (safeTrip.extra_services || []).filter(s => s.id !== 'reimbursable_expense');
            }
            
            // RESET Y CARGA INICIAL
            setDisabledRuleKeys([]);
            setFormData(safeTrip);

            // Calcular base inicial considerando recargo si la fecha es fin de semana
            const tripVenta = parseFloat(safeTrip.final_price || safeTrip.estimated_price) || 0;
            const tripCosto = parseFloat(safeTrip.provider_cost_base) || 0;
            let isFdsTrip = false;
            if (safeTrip.scheduled_time) {
                const matchDate = safeTrip.scheduled_time.match(/^(\d{4})-(\d{2})-(\d{2})/);
                let day = -1;
                if (matchDate) {
                    day = new Date(parseInt(matchDate[1], 10), parseInt(matchDate[2], 10) - 1, parseInt(matchDate[3], 10)).getDay();
                } else {
                    day = new Date(safeTrip.scheduled_time).getDay();
                }
                if ((day === 0 || day === 6) && parseInt(safeTrip.client_id) === 45) {
                    isFdsTrip = true;
                }
            }
            const initialSurcharge = isFdsTrip ? 10 : 0;
            if (initialSurcharge > 0) {
                setCustomVentaBase(tripVenta > 0 ? Math.round(tripVenta / (1 + initialSurcharge / 100)) : '');
                setCustomCostoBase(tripCosto > 0 ? Math.round(tripCosto / (1 + initialSurcharge / 100)) : '');
            } else {
                setCustomVentaBase(tripVenta > 0 ? tripVenta : '');
                setCustomCostoBase(tripCosto > 0 ? tripCosto : '');
            }

            setCustomPenaltyPct(
                safeTrip.booking_incident_penalty_pct !== null && safeTrip.booking_incident_penalty_pct !== undefined 
                    ? safeTrip.booking_incident_penalty_pct 
                    : (safeTrip.booking_incident_type === 'DRIVER_SERVICE' ? 25 : 100)
            );
            setReversalPreview(null);
            setLoadingReversalPreview(false);
            setReimbursementAmountInput('');
            setCreateReimbursementToggle(true);
            setReversalNotesInput('');
            setPriceBreakdown(null);
            setAvailableQuotes([]); // LIMPIAR COTIZACIONES ANTERIORES
            setPaxSearchTerm('');
            setIsSameAsRequester(false);
            setLoadingRoute(false);
            setLoadingPricing(false);

            // Reset de programación recurrente
            setIsRepeatChecked(false);
            setRepeatMode('weekly');
            setRepeatStartDate('');
            setRepeatEndDate('');
            setRepeatDays({ 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 0: false });
            setManualDates(['']);

            if (trip.pickup_lat && trip.pickup_lng) setPickupLocation({ lat: trip.pickup_lat, lng: trip.pickup_lng });
            else setPickupLocation(null);
            
            if (trip.dropoff_lat && trip.dropoff_lng) setDropoffLocation({ lat: trip.dropoff_lat, lng: trip.dropoff_lng });
            else setDropoffLocation(null);
            
            setRouteDistanceKm(trip.distance_km || 0);
            setRouteDurationMin(trip.estimated_duration_minutes || 0);
            
            if (trip.client_id) loadClientContext(trip.client_id);
            else {
                setContextPassengers([]); setContextRequesters([]); setContextAreas([]); setContextCostCenters([]);
            }
        } else if (!show) {
            initialEditLoadRef.current = false;
            lastDetectedAddressRef.current = { pickup: '', dropoff: '' };
            setCustomVentaBase('');
            setCustomCostoBase('');
            setDisabledRuleKeys([]);
            setAvailableQuotes([]);
            setPriceBreakdown(null);
            setPickupLocation(null);
            setDropoffLocation(null);
            setRouteDistanceKm(0);
            setRouteDurationMin(0);
            setFormData({});
        }
    }, [show, trip, loadClientContext, isEditing]);

    const handleClientChange = (clientId) => {
        setFormData(prev => ({ 
            ...prev, 
            client_id: clientId, 
            requester_id: '', 
            area_id: '', 
            cost_center_id: '', 
            stops: [], 
            additional_passenger_ids: [] 
        }));
        if (clientId) loadClientContext(clientId);
    };

    // DETERMINAR REGLAS DE RECARGO DETECTADAS Y ACTIVAS
    const allAppliedRules = useMemo(() => {
        const currentQuote = availableQuotes.find(q => q.vehicle_type_id === parseInt(formData.requested_vehicle_type_id)) || availableQuotes[0];
        
        // Obtener la lista completa de reglas detectadas originalmente (NUNCA filtrada al desmarcar)
        const rawRules = (priceBreakdown?.detected_rules && priceBreakdown.detected_rules.length > 0)
            ? priceBreakdown.detected_rules
            : (currentQuote?.applied_rules && currentQuote.applied_rules.length > 0)
                ? currentQuote.applied_rules
                : (priceBreakdown?.original_rules && priceBreakdown.original_rules.length > 0)
                    ? priceBreakdown.original_rules
                    : (priceBreakdown?.applied_rules && priceBreakdown.applied_rules.length > 0)
                        ? priceBreakdown.applied_rules
                        : [];
        
        if (rawRules.length === 0 && formData.scheduled_time) {
            const match = formData.scheduled_time.match(/^(\d{4})-(\d{2})-(\d{2})/);
            let day = -1;
            if (match) {
                day = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10)).getDay();
            } else {
                day = new Date(formData.scheduled_time).getDay();
            }

            if (day === 0 || day === 6) {
                const cid = parseInt(formData.client_id);
                if (cid === 45) {
                    return [{ id: 4, name: 'Recargo FDS', value: 10, type: 'percentage', applies_to_cost: true }];
                }
            }
        }
        return rawRules.map(r => ({
            ...r,
            applies_to_cost: r.applies_to_cost !== undefined 
                ? r.applies_to_cost 
                : (!r.is_urgency && r.type === 'percentage' && !r.name?.toLowerCase().includes('urgencia') && !r.name?.toLowerCase().includes('hora'))
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceBreakdown?.detected_rules, priceBreakdown?.original_rules, availableQuotes, formData.requested_vehicle_type_id, formData.scheduled_time, formData.client_id]);

    const activeRules = useMemo(() => {
        return allAppliedRules.filter(r => {
            const key = r.id ? String(r.id) : (r.name || '');
            return !disabledRuleKeys.includes(key);
        });
    }, [allAppliedRules, disabledRuleKeys]);

    const totalActiveVentaPct = useMemo(() => {
        return activeRules.reduce((sum, r) => sum + (r.type === 'percentage' ? (parseFloat(r.value) || 0) : 0), 0);
    }, [activeRules]);

    const totalActiveCostoPct = useMemo(() => {
        return activeRules.filter(r => r.applies_to_cost).reduce((sum, r) => sum + (r.type === 'percentage' ? (parseFloat(r.value) || 0) : 0), 0);
    }, [activeRules]);

    const surchargePct = totalActiveVentaPct;
    const costSurchargePct = totalActiveCostoPct;
    const surchargeLabel = activeRules.length === 1 ? (activeRules[0].name?.replace(/recargo/i, '').trim() || 'Recargo') : 'Recargos';

    const handleVehicleSelect = (quote) => {
        const basePrice = quote.base_price || 0;
        const baseCost = quote.cost_base !== undefined ? quote.cost_base : (quote.cost_clp || 0);

        setCustomVentaBase(basePrice > 0 ? basePrice : '');
        setCustomCostoBase(baseCost > 0 ? baseCost : '');
        setDisabledRuleKeys([]);

        setFormData(prev => ({ 
            ...prev, 
            requested_vehicle_type_id: quote.vehicle_type_id,
            estimated_price: quote.final_price, 
            provider_cost_base: quote.cost_clp, 
            provider_cost_clp: quote.cost_clp,
            provider_cost_local: quote.cost_local, 
            driver_earnings: quote.cost_local, 
            local_currency_code: quote.local_currency_code || 'CLP',
            origin_base_id: quote.origin_base_id,
            provider_id: quote.provider_id || null,
            // RESET ALEJANDRO: Al cambiar de vehículo, reseteamos servicios adicionales
            extra_services: []
        }));
        setPriceBreakdown({ 
            source: quote.origin_zone_name, 
            base_price: quote.base_price, 
            provider_name: quote.provider_name,
            detected_rules: quote.applied_rules || [],
            applied_rules: quote.applied_rules || []
        });
    };


    const handleCostLocalChange = (localValue) => {
        const local = parseFloat(localValue) || 0;
        let clp = local;
        const currency = (formData.provider_currency_code || formData.local_currency_code || 'CLP').toUpperCase().trim();

        console.log("💰 [DEBUG] Ajustando Pago Local:", local, "Moneda Costo Proveedor:", currency);

        if (currency !== 'CLP' && exchangeRates.length > 0) {
            let effectiveRate = null;
            const direct = exchangeRates.find(r => 
                (r.currency_from.toUpperCase().trim() === currency && r.currency_to.toUpperCase().trim() === 'CLP') ||
                (r.currency_from.toUpperCase().trim() === 'CLP' && r.currency_to.toUpperCase().trim() === currency)
            );

            if (direct) {
                effectiveRate = direct.currency_from.toUpperCase().trim() === currency ? direct.rate : (1.0 / direct.rate);
            } else {
                // Triangulación vía USD
                const rateUSDtoLocal = exchangeRates.find(r => r.currency_from.toUpperCase().trim() === 'USD' && r.currency_to.toUpperCase().trim() === currency);
                const rateUSDtoCLP = exchangeRates.find(r => r.currency_from.toUpperCase().trim() === 'USD' && r.currency_to.toUpperCase().trim() === 'CLP');

                if (rateUSDtoLocal && rateUSDtoCLP) {
                    effectiveRate = rateUSDtoCLP.rate / rateUSDtoLocal.rate;
                    console.log("🌉 [DEBUG] Triangulación USD exitosa. Tasa calculada:", effectiveRate);
                }
            }

            if (effectiveRate) {
                clp = Math.round(local * effectiveRate);
                console.log("🎯 [DEBUG] Resultado conversión CLP (redondeado):", clp);
            } else {
                console.warn("❌ [DEBUG] No se pudo calcular la tasa para:", currency);
            }
        }

        setCustomCostoBase(clp > 0 ? clp : '');
        recalculatePricesWithRules(customVentaBase, clp, disabledRuleKeys, localValue);
    };

    // --- RECALCULO CENTRALIZADO DE PRECIOS Y COSTOS CON REGLAS ACTIVAS/DESMARCADAS ---
    const recalculatePricesWithRules = (baseVentaVal, baseCostoVal, disabledKeys = disabledRuleKeys, overrideLocalCost = undefined) => {
        const rawBaseVenta = parseFloat(baseVentaVal) || 0;
        const rawBaseCosto = parseFloat(baseCostoVal) || 0;

        const currentActiveRules = allAppliedRules.filter(r => {
            const key = r.id ? String(r.id) : (r.name || '');
            return !disabledKeys.includes(key);
        });

        let extraVenta = 0;
        currentActiveRules.forEach(r => {
            if (r.type === 'percentage') {
                extraVenta += Math.round(rawBaseVenta * ((parseFloat(r.value) || 0) / 100));
            } else {
                extraVenta += (parseFloat(r.value || r.adjustment) || 0);
            }
        });

        let extraCosto = 0;
        currentActiveRules.filter(r => r.applies_to_cost).forEach(r => {
            if (r.type === 'percentage') {
                extraCosto += Math.round(rawBaseCosto * ((parseFloat(r.value) || 0) / 100));
            } else {
                extraCosto += (parseFloat(r.cost_value || r.cost_adjustment_clp || 0) || 0);
            }
        });

        const finalEstimatedPrice = rawBaseVenta > 0 ? (rawBaseVenta + extraVenta) : 0;
        const finalCostClp = rawBaseCosto > 0 ? (rawBaseCosto + extraCosto) : 0;

        let finalLocalCost = finalCostClp;
        const currency = (formData.provider_currency_code || formData.local_currency_code || 'CLP').toUpperCase().trim();
        if (overrideLocalCost !== undefined) {
            finalLocalCost = overrideLocalCost;
        } else if (currency !== 'CLP' && exchangeRates.length > 0) {
            const direct = exchangeRates.find(r => 
                (r.currency_from.toUpperCase().trim() === currency && r.currency_to.toUpperCase().trim() === 'CLP') ||
                (r.currency_from.toUpperCase().trim() === 'CLP' && r.currency_to.toUpperCase().trim() === currency)
            );
            if (direct) {
                const effectiveRate = direct.currency_from.toUpperCase().trim() === currency ? direct.rate : (1.0 / direct.rate);
                finalLocalCost = Math.round(finalCostClp / effectiveRate);
            } else {
                const rateUSDtoLocal = exchangeRates.find(r => r.currency_from.toUpperCase().trim() === 'USD' && r.currency_to.toUpperCase().trim() === currency);
                const rateUSDtoCLP = exchangeRates.find(r => r.currency_from.toUpperCase().trim() === 'USD' && r.currency_to.toUpperCase().trim() === 'CLP');
                if (rateUSDtoLocal && rateUSDtoCLP) {
                    const effectiveRate = rateUSDtoCLP.rate / rateUSDtoLocal.rate;
                    finalLocalCost = Math.round(finalCostClp / effectiveRate);
                }
            }
        }

        setFormData(prev => ({
            ...prev,
            estimated_price: finalEstimatedPrice,
            final_price: finalEstimatedPrice,
            provider_cost_base: finalCostClp,
            provider_cost_clp: finalCostClp,
            provider_cost_local: finalLocalCost,
            driver_earnings: overrideLocalCost !== undefined ? (parseFloat(overrideLocalCost) || 0) : finalLocalCost
        }));

        setPriceBreakdown(prev => ({
            ...prev,
            base_price: rawBaseVenta,
            detected_rules: prev?.detected_rules || allAppliedRules,
            applied_rules: currentActiveRules.map(r => ({
                ...r,
                adjustment: r.type === 'percentage' ? Math.round(rawBaseVenta * ((parseFloat(r.value) || 0) / 100)) : (r.adjustment || r.value)
            }))
        }));
    };

    const handleVentaBaseChange = (val) => {
        setCustomVentaBase(val);
        recalculatePricesWithRules(val, customCostoBase, disabledRuleKeys);
    };

    const handleCostBaseManualChange = (val) => {
        setCustomCostoBase(val);
        recalculatePricesWithRules(customVentaBase, val, disabledRuleKeys);
    };

    const handleToggleRule = (ruleKey) => {
        const nextDisabled = disabledRuleKeys.includes(ruleKey)
            ? disabledRuleKeys.filter(k => k !== ruleKey)
            : [...disabledRuleKeys, ruleKey];
        setDisabledRuleKeys(nextDisabled);
        recalculatePricesWithRules(customVentaBase, customCostoBase, nextDisabled);
    };

    // COTIZACIONES FILTRADAS (Reactividad garantizada)
    const filteredQuotes = useMemo(() => {
        const pax = parseInt(formData.passenger_count) || 1;
        const suitcases = parseInt(formData.suitcases) || 0;
        return availableQuotes.filter(q => {
            const vType = vehicleTypes.find(vt => vt.id === q.vehicle_type_id);
            
            // Determinar pax fallbacks: Standard = 2, Executive = 2, People Carrier = 4
            let fallbackPax = vType?.capacity || 0;
            if (vType?.name) {
                const nameUpper = vType.name.toUpperCase();
                if (nameUpper === "STANDARD" || nameUpper === "EXECUTIVE") fallbackPax = 2;
                else if (nameUpper === "PEOPLE_CARRIER" || nameUpper === "PEOPLE CARRIER") fallbackPax = 4;
            }
            const maxPax = q.max_pax !== undefined && q.max_pax !== null ? q.max_pax : fallbackPax;

            // Determinar suitcases fallbacks: Standard = 2, Executive = 2, People Carrier = 4
            let fallbackSuitcases = 4;
            if (vType?.name) {
                const nameUpper = vType.name.toUpperCase();
                if (nameUpper === "STANDARD" || nameUpper === "EXECUTIVE") fallbackSuitcases = 2;
                else if (nameUpper === "PEOPLE_CARRIER" || nameUpper === "PEOPLE CARRIER") fallbackSuitcases = 4;
            }
            const maxSuitcases = q.max_suitcases !== undefined && q.max_suitcases !== null ? q.max_suitcases : fallbackSuitcases;

            return pax <= maxPax && suitcases <= maxSuitcases;
        });
    }, [availableQuotes, formData.passenger_count, formData.suitcases, vehicleTypes]);

    useEffect(() => {
        const calculatePrice = async () => {
            let activePickupLoc = pickupLocation || (formData.pickup_lat && formData.pickup_lng ? { lat: Number(formData.pickup_lat), lng: Number(formData.pickup_lng) } : null);
            let activeDropoffLoc = dropoffLocation || (formData.dropoff_lat && formData.dropoff_lng ? { lat: Number(formData.dropoff_lat), lng: Number(formData.dropoff_lng) } : null);

            if (!formData.client_id || !formData.pickup_address || !formData.dropoff_address) return;

            if (!activePickupLoc || !activeDropoffLoc) {
                if (window.google && window.google.maps) {
                    const geocoder = new window.google.maps.Geocoder();
                    if (!activePickupLoc && formData.pickup_address) {
                        try {
                            const resP = await new Promise((resolve) => geocoder.geocode({ address: formData.pickup_address }, (r, s) => resolve(s === 'OK' ? r : null)));
                            if (resP && resP[0]) {
                                const l = resP[0].geometry.location;
                                activePickupLoc = { lat: l.lat(), lng: l.lng() };
                                setPickupLocation(activePickupLoc);
                            }
                        } catch (e) { console.warn("Error geocodificando origen en pricing:", e); }
                    }
                    if (!activeDropoffLoc && formData.dropoff_address) {
                        try {
                            const resD = await new Promise((resolve) => geocoder.geocode({ address: formData.dropoff_address }, (r, s) => resolve(s === 'OK' ? r : null)));
                            if (resD && resD[0]) {
                                const l = resD[0].geometry.location;
                                activeDropoffLoc = { lat: l.lat(), lng: l.lng() };
                                setDropoffLocation(activeDropoffLoc);
                            }
                        } catch (e) { console.warn("Error geocodificando destino en pricing:", e); }
                    }
                }
            }

            if (!activePickupLoc || !activeDropoffLoc) return;

            const currentPickup = formData.pickup_address || '';
            const currentDropoff = formData.dropoff_address || '';
            const addressChanged = lastDetectedAddressRef.current.pickup !== currentPickup || lastDetectedAddressRef.current.dropoff !== currentDropoff;
            
            let addressesUnchanged = false;
            if (isEditing && trip) {
                const isTextSame = (formData.pickup_address || '').trim().toLowerCase() === (trip.pickup_address || '').trim().toLowerCase() &&
                                   (formData.dropoff_address || '').trim().toLowerCase() === (trip.dropoff_address || '').trim().toLowerCase();
                const isPickupSame = trip.pickup_lat != null && Math.abs(Number(activePickupLoc.lat) - Number(trip.pickup_lat)) < 0.005 && Math.abs(Number(activePickupLoc.lng) - Number(trip.pickup_lng)) < 0.005;
                const isDropoffSame = trip.dropoff_lat != null && Math.abs(Number(activeDropoffLoc.lat) - Number(trip.dropoff_lat)) < 0.005 && Math.abs(Number(activeDropoffLoc.lng) - Number(trip.dropoff_lng)) < 0.005;
                
                if (isTextSame || (isPickupSame && isDropoffSame) || !addressChanged) {
                    addressesUnchanged = true;
                }
            }

            if (isEditing && !routeDistanceKm && !trip?.distance_km) {
                console.log("⏳ [PRICING] Esperando distancia del mapa para recalcular...");
                return;
            }

            setLoadingPricing(true);
            try {
                const quotes = await getQuote({
                    client_id: parseInt(formData.client_id), 
                    pickup_lat: activePickupLoc.lat, 
                    pickup_lng: activePickupLoc.lng,
                    dropoff_lat: activeDropoffLoc.lat, 
                    dropoff_lng: activeDropoffLoc.lng,
                    scheduled_time: formData.scheduled_time || new Date().toISOString(), 
                    distance_km: routeDistanceKm || trip?.distance_km || 0,
                    passengers: parseInt(formData.passenger_count || 1),
                    suitcases: parseInt(formData.suitcases || 0),
                    pickup_address: formData.pickup_address,
                    dropoff_address: formData.dropoff_address
                });
                if (quotes?.length > 0) {
                    setAvailableQuotes(quotes);
                    
                    const routeCurrency = quotes.find(q => q.local_currency_code && q.local_currency_code !== 'CLP')?.local_currency_code || quotes[0].local_currency_code || 'CLP';
                    const routeBaseId = quotes[0].origin_base_id;
 
                    if (addressesUnchanged) {
                        console.log("🛑 [PRICING] Modo edición. Las direcciones no han cambiado. Cargando categorías y manteniendo precio guardado.");
                        const savedVenta = parseFloat(trip?.final_price !== null && trip?.final_price !== undefined && trip?.final_price !== '' ? trip.final_price : (trip?.estimated_price || formData.estimated_price)) || 0;
                        const savedCosto = parseFloat(
                            (trip?.provider_cost_base !== null && trip?.provider_cost_base !== undefined && trip?.provider_cost_base !== '' && Number(trip?.provider_cost_base) > 0) ? trip.provider_cost_base :
                            (trip?.provider_cost_clp !== null && trip?.provider_cost_clp !== undefined && trip?.provider_cost_clp !== '' && Number(trip?.provider_cost_clp) > 0) ? trip.provider_cost_clp :
                            (trip?.driver_earnings !== null && trip?.driver_earnings !== undefined && trip?.driver_earnings !== '' && Number(trip?.driver_earnings) > 0) ? trip.driver_earnings :
                            (formData.provider_cost_base || formData.provider_cost_clp || (savedVenta > 0 ? savedVenta : 0))
                        ) || 0;

                        const savedCostoLocal = parseFloat(
                            (trip?.provider_cost_local !== null && trip?.provider_cost_local !== undefined && trip?.provider_cost_local !== '' && Number(trip?.provider_cost_local) > 0) ? trip.provider_cost_local :
                            (trip?.driver_earnings !== null && trip?.driver_earnings !== undefined && trip?.driver_earnings !== '' && Number(trip?.driver_earnings) > 0) ? trip.driver_earnings :
                            (formData.provider_cost_local || 0)
                        ) || 0;

                        // Sincronizar availableQuotes para que la tarjeta del vehículo seleccionado refleje el valor guardado
                        const currentVehId = parseInt(formData.requested_vehicle_type_id);
                        const currentProvId = trip?.provider_id || formData.provider_id || null;
                        const adjustedQuotes = quotes.map(q => {
                            const isMatchingProvider = !currentProvId || !q.provider_id || Number(q.provider_id) === Number(currentProvId);
                            if (q.vehicle_type_id === currentVehId && isMatchingProvider && savedVenta > 0) {
                                const isForeign = (q.local_currency_code && q.local_currency_code !== 'CLP') || (trip?.provider_currency_code && trip.provider_currency_code !== 'CLP');
                                return {
                                    ...q,
                                    final_price: savedVenta,
                                    base_price: savedVenta,
                                    cost_clp: savedCosto > 0 ? savedCosto : q.cost_clp,
                                    cost_local: isForeign ? (savedCostoLocal > 0 ? savedCostoLocal : q.cost_local) : (savedCosto > 0 ? savedCosto : q.cost_local)
                                };
                            }
                            return q;
                        });
                        setAvailableQuotes(adjustedQuotes);

                        const matchExisting = adjustedQuotes.find(q => q.vehicle_type_id === currentVehId && (!currentProvId || Number(q.provider_id) === Number(currentProvId))) || adjustedQuotes.find(q => q.vehicle_type_id === currentVehId) || adjustedQuotes[0];

                        setFormData(prev => {
                            const targetBaseId = prev.origin_base_id;
                            const targetBase = bases.find(b => b.id === targetBaseId);
                            const finalCurrency = targetBase?.currency_code || routeCurrency || 'CLP';
                            return { 
                                ...prev, 
                                local_currency_code: finalCurrency,
                                requested_vehicle_type_id: prev.requested_vehicle_type_id || (matchExisting ? matchExisting.vehicle_type_id.toString() : prev.requested_vehicle_type_id)
                            };
                        });
                        if (matchExisting) {
                            setPriceBreakdown({ 
                                source: matchExisting.origin_zone_name, 
                                base_price: savedVenta > 0 ? savedVenta : matchExisting.base_price,
                                detected_rules: matchExisting.applied_rules || [],
                                applied_rules: matchExisting.applied_rules || []
                            });
                            // SOLO asignar cotización si el viaje no tenía un precio guardado previo
                            if (savedVenta <= 0 && matchExisting.base_price > 0) {
                                setCustomVentaBase(matchExisting.base_price);
                            }
                            const baseCost = matchExisting.cost_base !== undefined ? matchExisting.cost_base : matchExisting.cost_clp;
                            if (savedCosto <= 0 && baseCost > 0) {
                                setCustomCostoBase(baseCost);
                            }
                        }
                        initialEditLoadRef.current = false;
                        return;
                    }
 
                    const match = quotes.find(q => q.vehicle_type_id === parseInt(formData.requested_vehicle_type_id));
                    
                    // BLOQUEO CRÍTICO ALEJANDRO: Solo sobreescribir si NO es la carga inicial de edición
                    // O si el usuario cambió explícitamente algo (distancia > 0 y ya pasó el primer render)
                    if (initialEditLoadRef.current) {
                        console.log("🛑 [PRICING] Carga inicial detectada. Manteniendo valores de base de datos.");
                        initialEditLoadRef.current = false; 
                        return;
                    }
 
                    if (match) {
                        const baseCost = match.cost_base !== undefined ? match.cost_base : match.cost_clp;
                        setCustomVentaBase(match.base_price > 0 ? match.base_price : '');
                        setCustomCostoBase(baseCost > 0 ? baseCost : '');

                        setFormData(prev => {
                            const targetBaseId = addressChanged ? match.origin_base_id : prev.origin_base_id;
                            const targetBase = bases.find(b => b.id === targetBaseId);
                            const finalCurrency = targetBase?.currency_code || routeCurrency || 'CLP';
                            return { 
                                ...prev, 
                                estimated_price: match.final_price, 
                                provider_cost_base: match.cost_clp, 
                                provider_cost_clp: match.cost_clp,
                                provider_cost_local: match.cost_local, 
                                driver_earnings: match.cost_local, 
                                local_currency_code: finalCurrency,
                                origin_base_id: targetBaseId,
                                provider_id: match.provider_id || null
                            };
                        });
                        setPriceBreakdown({ 
                            source: match.origin_zone_name, 
                            base_price: match.base_price,
                            detected_rules: match.applied_rules || [],
                            applied_rules: match.applied_rules || []
                        });
                    } else {
                        setFormData(prev => {
                            const targetBaseId = addressChanged ? routeBaseId : prev.origin_base_id;
                            const targetBase = bases.find(b => b.id === targetBaseId);
                            const finalCurrency = targetBase?.currency_code || routeCurrency || 'CLP';
                            return { 
                                ...prev, 
                                local_currency_code: finalCurrency, 
                                origin_base_id: targetBaseId 
                            };
                        });
                    }

                    if (addressChanged) {
                        lastDetectedAddressRef.current = { pickup: currentPickup, dropoff: currentDropoff };
                    }
                } else {
                    // Fallback de detección por dirección o coordenadas si no hay cotización configurada
                    console.log("⚠️ [PRICING] No se encontraron tarifas configuradas. Ejecutando detección de base por ubicación...");
                    
                    let detectedCurrency = 'CLP';
                    let detectedBaseId = null;
                    
                    const pickupText = (formData.pickup_address || '').toLowerCase();
                    const dropoffText = (formData.dropoff_address || '').toLowerCase();
                    const fullText = `${pickupText} ${dropoffText}`;
                    
                    // Coordenadas
                    const lat = pickupLocation?.lat;
                    const lng = pickupLocation?.lng;
                    
                    // Uruguay (Montevideo / Carrasco / Punta del Este)
                    const isUY = fullText.includes('uruguay') || fullText.includes('montevideo') || 
                                 fullText.includes('carrasco') || fullText.includes('mvd') ||
                                 fullText.includes('punta del este');

                    // España (Barcelona / Madrid)
                    const isES = fullText.includes('españa') || fullText.includes('espana') || 
                                 fullText.includes('barcelona') || fullText.includes('bcn') || 
                                 fullText.includes('madrid') || fullText.includes('el prat') ||
                                 (lat && lng && lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5);

                    // 1. Punta Cana / República Dominicana
                    const isRD = (lat && lng && lat >= 17.5 && lat <= 20.5 && lng >= -72.5 && lng <= -67.5) ||
                                 fullText.includes('punta cana') || fullText.includes('dominicana') || 
                                 fullText.includes('dominican') || fullText.includes('puj') || 
                                 fullText.includes('sdq') || fullText.includes('santo domingo');
                                 
                    // 2. Argentina (Mendoza / Buenos Aires) - Se excluye Uruguay explícitamente para evitar falso positivo
                    const isAR = !isUY && ((lat && lng && lat >= -45.0 && lat <= -20.0 && lng >= -69.5 && lng <= -58.0) ||
                                 fullText.includes('argentina') || fullText.includes('mendoza') || 
                                 fullText.includes('buenos aires') || fullText.includes('eze') || 
                                 fullText.includes('aep') || fullText.includes('mdz') || 
                                 fullText.includes('plumerillo'));
                                 
                    // 3. Perú (Lima)
                    const isPE = fullText.includes('peru') || fullText.includes('perú') || 
                                 fullText.includes('lima') || fullText.includes('lim') || 
                                 fullText.includes('jorge chávez') || fullText.includes('jorge chavez');
                                 
                    // 4. Colombia
                    const isCO = fullText.includes('colombia') || fullText.includes('bogota') || 
                                 fullText.includes('bogotá') || fullText.includes('medellin') || 
                                 fullText.includes('medellín') || fullText.includes('bog') || 
                                 fullText.includes('mde');
                                 
                    // 5. Brasil
                    const isBR = fullText.includes('brasil') || fullText.includes('brazil') || 
                                 fullText.includes('sao paulo') || fullText.includes('rio de janeiro') || 
                                 fullText.includes('gru') || fullText.includes('gig');

                    if (isUY) {
                        const uyBase = bases.find(b => b.country_code === 'UY' || b.name?.toLowerCase().includes('montevideo') || b.name?.toLowerCase().includes('uruguay'));
                        detectedCurrency = uyBase?.currency_code || 'USD';
                        detectedBaseId = uyBase?.id || null;
                    } else if (isES) {
                        const esBase = bases.find(b => b.country_code === 'ES' || b.name?.toLowerCase().includes('barcelona') || b.name?.toLowerCase().includes('españa') || b.name?.toLowerCase().includes('espana'));
                        detectedCurrency = esBase?.currency_code || 'EUR';
                        detectedBaseId = esBase?.id || null;
                    } else if (isRD) {
                        detectedCurrency = 'USD';
                        detectedBaseId = (fullText.includes('santo domingo') || fullText.includes('sdq')) ? 6 : 2; // Base 6: SDQ, Base 2: PUJ
                    } else if (isAR) {
                        detectedCurrency = 'ARS';
                        detectedBaseId = (fullText.includes('mendoza') || fullText.includes('mdz') || fullText.includes('plumerillo')) ? 4 : 3; // Base 4: MDZ, Base 3: EZE/AEP
                    } else if (isPE) {
                        detectedCurrency = 'PEN';
                        detectedBaseId = 7; // Base 7: Lima
                    } else if (isCO) {
                        detectedCurrency = 'COP';
                        detectedBaseId = (fullText.includes('medellin') || fullText.includes('medellín')) ? 31 : 8; // Base 31: Medellín, Base 8: Colombia/BOG
                    } else if (isBR) {
                        detectedCurrency = 'BRL';
                        detectedBaseId = (fullText.includes('sao paulo') || fullText.includes('gru')) ? 33 : 32; // Base 33: Sao Paulo, Base 32: Rio
                    } else {
                        // Chile (Detección de bases regionales)
                        detectedCurrency = 'CLP';
                        if (fullText.includes('la serena') || fullText.includes('coquimbo') || fullText.includes('lsc')) {
                            detectedBaseId = 13; // CL - LA SERENA
                        } else if (fullText.includes('calama') || fullText.includes('cjc')) {
                            detectedBaseId = 27; // CL - Calama
                        } else if (fullText.includes('antofagasta') || fullText.includes('anf')) {
                            detectedBaseId = 11; // CL - ANTOFAGASTA
                        } else if (fullText.includes('copiapó') || fullText.includes('copiapo') || fullText.includes('cpo')) {
                            detectedBaseId = 12; // CL - COPIAPÓ
                        } else if (fullText.includes('iquique') || fullText.includes('iqq')) {
                            detectedBaseId = 10; // CL - IQUIQUE
                        } else if (fullText.includes('arica') || fullText.includes('ari')) {
                            detectedBaseId = 9; // CL - ARICA
                        } else if (fullText.includes('concepción') || fullText.includes('concepcion') || fullText.includes('talcahuano') || fullText.includes('ccp')) {
                            detectedBaseId = 18; // CL - CONCEPCIÓN
                        } else if (fullText.includes('temuco') || fullText.includes('zco') || fullText.includes('la araucanía') || fullText.includes('la araucania')) {
                            detectedBaseId = 19; // CL - TEMUCO
                        } else if (fullText.includes('puerto montt') || fullText.includes('puerto varas') || fullText.includes('pmc') || fullText.includes('tepual')) {
                            detectedBaseId = 21; // CL - PUERTO MONTT
                        } else {
                            detectedBaseId = 1; // CL - Santiago (Default)
                        }
                    }
                    
                    setFormData(prev => {
                        const targetBaseId = (addressChanged && detectedBaseId) ? detectedBaseId : prev.origin_base_id;
                        const targetBase = bases.find(b => b.id === targetBaseId);
                        const finalCurrency = targetBase?.currency_code || detectedCurrency || 'CLP';
                        return {
                            ...prev,
                            local_currency_code: finalCurrency,
                            origin_base_id: targetBaseId
                        };
                    });
                    
                    if (addressChanged) {
                        lastDetectedAddressRef.current = { pickup: currentPickup, dropoff: currentDropoff };
                    }
                    
                    setAvailableQuotes([]);
                    setPriceBreakdown(null);
                }
            } catch (error) { console.error(error); }
            finally { setLoadingPricing(false); }
        };
        calculatePrice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.client_id, pickupLocation, dropoffLocation, formData.pickup_lat, formData.pickup_lng, formData.dropoff_lat, formData.dropoff_lng, formData.requested_vehicle_type_id, formData.scheduled_time, routeDistanceKm, formData.external_reference, formData.id, isEditing, trip, formData.passenger_count, formData.suitcases, formData.pickup_address, formData.dropoff_address, bases]);

    const handleDistanceCalculated = (dist, dur) => {
        setRouteDistanceKm(dist); setRouteDurationMin(dur);
        if (!formData.stops || formData.stops.length === 0) {
            setBaseDistanceKm(dist);
        }
        setFormData(prev => ({ ...prev, distance_km: dist, estimated_duration_minutes: dur }));
        setLoadingRoute(false);
    };

    const suggestedStopPrice = useMemo(() => {
        if (!formData.stops || formData.stops.length === 0) return 0;
        if (baseDistanceKm === 0) return 5000; // Fallback
        const deviation = routeDistanceKm - baseDistanceKm;
        return Math.max(5000, Math.round(deviation * 1000));
    }, [formData.stops, routeDistanceKm, baseDistanceKm]);

    const handleStatusUpdate = async (newStatus) => {
        if (!window.confirm(`¿Cambiar estado a ${newStatus}?`)) return;
        try {
            const payload = { ...formData, status: newStatus.toLowerCase() };
            await updateTrip(formData.id, payload);
            onSave(payload); setFormData(payload);
        } catch (e) { alert('Error al actualizar estado'); }
    };

    const handleDisputeToggle = async (checked) => {
        try {
            setFormData(prev => ({ ...prev, booking_incident_dispute_sent: checked }));
            const updated = await updateTrip(formData.id, { booking_incident_dispute_sent: checked });
            if (onSave) onSave(updated);
            toast.success(checked ? "Respaldos marcados como enviados" : "Respaldos marcados como pendientes");
        } catch (error) {
            toast.error("Error al actualizar estado de disputa: " + (error.response?.data?.detail || error.message));
            setFormData(prev => ({ ...prev, booking_incident_dispute_sent: !checked }));
        }
    };

    const manualIncidentLock = useMemo(() => {
        if (!formData?.pricing_details) return null;
        try {
            const parsed = typeof formData.pricing_details === 'string'
                ? JSON.parse(formData.pricing_details)
                : formData.pricing_details;
            return parsed?.manual_incident_lock?.locked ? parsed.manual_incident_lock : null;
        } catch (e) {
            return null;
        }
    }, [formData?.pricing_details]);

    const formatIncidentCurrency = (val, curr = 'CLP') => {
        const num = Math.round(parseFloat(val) || 0);
        if (curr === 'PEN') return `S/. ${num.toLocaleString('es-PE')}`;
        if (curr === 'USD') return `US$ ${num.toLocaleString('en-US')}`;
        return `$${num.toLocaleString('es-CL')}`;
    };

    const handleOpenReversalPreview = async () => {
        if (!formData?.id) return;
        try {
            setLoadingReversalPreview(true);
            const res = await apiClient.get(`/booking/v2/incidents/reversal-preview/${formData.id}`);
            const data = res.data || {};
            setReversalPreview(data);
            setCreateReimbursementToggle(Boolean(data.needs_reimbursement_advance));
            setReimbursementAmountInput(String(Math.round(parseFloat(data.suggested_reimbursement) || 0)));
            setReversalNotesInput('');
        } catch (error) {
            toast.error("Error al calcular vista previa de reversa: " + (error.response?.data?.detail || error.message));
        } finally {
            setLoadingReversalPreview(false);
        }
    };

    const handleConfirmResolveWon = async () => {
        try {
            setSubmittingIncident(true);
            const reimburseAmt = createReimbursementToggle ? (parseFloat(reimbursementAmountInput) || 0) : 0;
            const params = {
                resolution: 'won',
                create_reimbursement: createReimbursementToggle,
                reimbursement_amount: reimburseAmt
            };
            if (reversalNotesInput && reversalNotesInput.trim()) {
                params.resolution_notes = reversalNotesInput.trim();
            }

            const res = await apiClient.post(`/booking/v2/incidents/confirm/${formData.id}`, null, { params });
            if (reversalPreview?.is_settled && createReimbursementToggle && reimburseAmt > 0) {
                toast.success(
                    `🏆 Reclamo GANADO y abono por +${formatIncidentCurrency(reimburseAmt, reversalPreview.currency_code)} creado para la próxima liquidación`
                );
            } else {
                toast.success("🏆 Reclamo marcado como GANADO (Sin penalización y blindado frente a API)");
            }
            setReversalPreview(null);
            if (res.data) {
                setFormData(res.data);
                if (onSave) onSave(res.data);
            }
        } catch (error) {
            toast.error("Error al resolver incidencia: " + (error.response?.data?.detail || error.message));
        } finally {
            setSubmittingIncident(false);
        }
    };

    const handleConfirmIncident = async () => {
        try {
            setSubmittingIncident(true);
            const pct = customPenaltyPct !== '' ? parseFloat(customPenaltyPct) : null;
            const res = await apiClient.post(`/booking/v2/incidents/confirm/${formData.id}`, null, {
                params: pct !== null ? { custom_penalty_pct: pct } : {}
            });
            toast.success("Incidencia confirmada y liquidación desbloqueada/descontada");
            if (res.data) {
                setFormData(res.data);
                if (onSave) onSave(res.data);
            }
        } catch (error) {
            toast.error("Error al confirmar incidencia: " + (error.response?.data?.detail || error.message));
        } finally {
            setSubmittingIncident(false);
        }
    };

    const handleSyncIncidents = async () => {
        try {
            setSubmittingIncident(true);
            const res = await apiClient.post('/booking/v2/incidents/sync');
            toast.success(`Sincronización completada: ${res.data.status}`);
            const refreshed = await apiClient.get(`/transport/trips/${formData.id}`);
            setFormData(refreshed.data);
            if (onSave) onSave(refreshed.data);
        } catch (error) {
            toast.error("Error al sincronizar incidencias: " + (error.response?.data?.detail || error.message));
        } finally {
            setSubmittingIncident(false);
        }
    };

    const handleResolvePassengerAlert = async () => {
        if (!window.confirm("¿Marcar la alerta de respuesta del pasajero como resuelta?")) {
            return;
        }
        try {
            await apiClient.put(`/transport/trips/${formData.id}/resolve-passenger-alert`);
            const updated = { ...formData, passenger_issue: false };
            setFormData(updated);
            if (onSave) onSave(updated);
            toast.success("Alerta de pasajero resuelta correctamente.");
        } catch (error) {
            console.error("Error al resolver alerta del pasajero:", error);
            toast.error("Error al resolver la alerta: " + (error.response?.data?.detail || error.message));
        }
    };

    const handleQuickCreateArea = async () => {
        if (!formData.client_id || !quickArea.name) return;
        try {
            const res = await apiClient.post('transport/areas/', { ...quickArea, client_id: parseInt(formData.client_id) });
            await loadClientContext(formData.client_id);
            if (areaModalTarget === 'trip') {
                setFormData(prev => ({ ...prev, area_id: res.data.id }));
            } else if (areaModalTarget === 'req') {
                setQuickReq(prev => ({ ...prev, area_id: res.data.id }));
            } else if (areaModalTarget === 'pax') {
                setQuickPax(prev => ({ ...prev, area_id: res.data.id }));
            }
            setShowQuickAreaModal(false); setQuickArea({ name: '' });
            toast.success("Área creada correctamente");
        } catch (e) { toast.error("Error al crear área"); }
    };

    const handleQuickCreateCC = async () => {
        if (!formData.client_id || !quickCC.code) return;
        try {
            const res = await apiClient.post('transport/cost-centers/', { ...quickCC, client_id: parseInt(formData.client_id) });
            await loadClientContext(formData.client_id);
            if (ccModalTarget === 'trip') {
                setFormData(prev => ({ 
                    ...prev, 
                    cost_center_id: res.data.id,
                    file: res.data.file || prev.file
                }));
            } else if (ccModalTarget === 'req') {
                setQuickReq(prev => ({ ...prev, cost_center_id: res.data.id }));
            } else if (ccModalTarget === 'pax') {
                setQuickPax(prev => ({ ...prev, cost_center_id: res.data.id }));
            }
            setShowQuickCCModal(false); setQuickCC({ code: '', name: '', file: '' });
            toast.success("Centro de costo creado correctamente");
        } catch (e) { toast.error("Error al crear centro de costo"); }
    };

    const handleQuickCreateRequester = async () => {
        try {
            const payload = { ...quickReq, client_id: formData.client_id };
            // Convertir strings vacíos a null para que Pydantic no reclame en el backend
            if (payload.area_id === '') payload.area_id = null;
            if (payload.cost_center_id === '') payload.cost_center_id = null;
            if (payload.home_address === '') payload.home_address = null;
            if (payload.office_address === '') payload.office_address = null;
            
            let res;
            if (quickReq.id) {
                res = await updateRequester(quickReq.id, payload);
                toast.success('Solicitante actualizado');
            } else {
                res = await createRequester(payload);
                toast.success('Solicitante creado');
            }

            if (quickReq.create_as_passenger) {
                const cleanEmail = (payload.email && payload.email.trim().toLowerCase()) || null;
                const cleanName = (payload.full_name && payload.full_name.trim().toLowerCase()) || '';

                const existingPax = contextPassengers.find(p => {
                    const pName = (p.full_name && p.full_name.trim().toLowerCase()) || '';
                    const pEmail = (p.email && p.email.trim().toLowerCase()) || null;
                    
                    const nameMatch = cleanName !== '' && pName === cleanName;
                    const emailMatch = cleanEmail !== null && pEmail !== null && pEmail === cleanEmail;
                    
                    return nameMatch || emailMatch;
                });

                const paxPayload = {
                    full_name: payload.full_name,
                    email: payload.email,
                    phone: payload.phone,
                    client_id: payload.client_id,
                    area_id: payload.area_id,
                    cost_center_id: payload.cost_center_id,
                    home_address: payload.home_address,
                    office_address: payload.office_address,
                    is_active: true
                };

                if (existingPax) {
                    await updatePassenger(existingPax.id, paxPayload);
                    toast.success('Pasajero frecuente actualizado automáticamente');
                } else {
                    await createPassenger(paxPayload);
                    toast.success('Pasajero frecuente creado automáticamente');
                }
            }

            await loadClientContext(formData.client_id);
            setFormData(prev => ({ ...prev, requester_id: res.id }));
            setShowQuickReqModal(false);
            setQuickReq({ id: null, full_name: '', email: '', phone: '', area_id: '', cost_center_id: '', home_address: '', office_address: '', has_portal_access: false, create_as_passenger: false });
        } catch (e) {
            toast.error(quickReq.id ? 'Error al actualizar solicitante' : 'Error al crear solicitante');
        }
    };

    const handleQuickCreatePassenger = async () => {
        try {
            const payload = { ...quickPax, client_id: formData.client_id };
            if (payload.area_id === '') payload.area_id = null;
            if (payload.cost_center_id === '') payload.cost_center_id = null;
            if (payload.home_address === '') payload.home_address = null;
            if (payload.office_address === '') payload.office_address = null;
            
            let res;
            if (quickPax.id) {
                res = await updatePassenger(quickPax.id, payload);
                toast.success('Pasajero actualizado');
            } else {
                res = await createPassenger(payload);
                toast.success('Pasajero creado');
            }
            
            await loadClientContext(formData.client_id);
            setFormData(prev => ({ 
                ...prev, 
                passenger_name: res.full_name, 
                passenger_phone: res.phone,
                area_id: res.area_id || prev.area_id || '',
                cost_center_id: res.cost_center_id || prev.cost_center_id || ''
            }));
            setShowQuickPaxModal(false);
            setQuickPax({ id: null, full_name: '', email: '', phone: '', area_id: '', cost_center_id: '', home_address: '', office_address: '', has_portal_access: false });
        } catch (e) { 
            alert(quickPax.id ? 'Error al actualizar pasajero' : 'Error al crear pasajero'); 
        }
    };

    const handleAddAreaPrompt = (target) => {
        setAreaModalTarget(target);
        setQuickArea({ name: '' });
        setShowQuickAreaModal(true);
    };

    const handleAddCCPrompt = (target) => {
        setCcModalTarget(target);
        setQuickCC({ code: '', name: '', file: '' });
        setShowQuickCCModal(true);
    };

    const handleAirportSelect = (airport) => {
        const { name, address, lat, lng } = airport;
        const finalAddress = address || name;
        setShowAirportModal(false);
        
        // ACTUALIZACIÓN ATÓMICA ALEJANDRO: Evitar race conditions y asegurar persistencia de coordenadas
        if (airportTargetField === 'pickup_address') {
            setPickupLocation({ lat, lng });
            setFormData(prev => ({ 
                ...prev, 
                pickup_address: finalAddress, 
                pickup_lat: lat, 
                pickup_lng: lng 
            }));
        } else {
            setDropoffLocation({ lat, lng });
            setFormData(prev => ({ 
                ...prev, 
                dropoff_address: finalAddress, 
                dropoff_lat: lat, 
                dropoff_lng: lng 
            }));
        }
    };

    const handleAddStop = () => {
        setFormData(prev => ({ ...prev, stops: [...(prev.stops || []), { address: '', lat: null, lng: null, passenger_name: '', passenger_phone: '' }] }));
    };

    const recalculatePassengerCount = (currentStops = formData.stops, currentAdditionalIds = formData.additional_passenger_ids, mainPaxName = formData.passenger_name) => {
        let count = 1; // Pasajero Principal (Origen)
        
        const originPassengerNames = new Set();
        if (mainPaxName && mainPaxName.trim()) {
            originPassengerNames.add(mainPaxName.trim().toLowerCase());
        }
        
        let uniqueAdditionalCount = 0;
        if (currentAdditionalIds) {
            currentAdditionalIds.forEach(id => {
                const p = contextPassengers.find(cp => cp.id === id);
                if (p) {
                    originPassengerNames.add(p.full_name.trim().toLowerCase());
                    if (p.full_name !== mainPaxName) {
                        uniqueAdditionalCount += 1;
                    }
                } else {
                    uniqueAdditionalCount += 1;
                }
            });
        }
        count += uniqueAdditionalCount;
        
        // Pasajeros en paradas intermedias:
        // Si el pasajero asignado a la parada YA venía en el Origen (originPassengerNames), es un desembarque (bajada) -> No incrementa la cantidad.
        // Si el pasajero es NUEVO (no venía desde el origen), es un nuevo abordaje (subida) -> Incrementa +1 (1 vez por persona).
        const newBoardingPassengers = new Set();
        if (currentStops) {
            currentStops.forEach(stop => {
                if (stop.passenger_name && stop.passenger_name.trim()) {
                    const cleanName = stop.passenger_name.trim().toLowerCase();
                    if (!originPassengerNames.has(cleanName)) {
                        newBoardingPassengers.add(cleanName);
                    }
                }
            });
        }
        count += newBoardingPassengers.size;
        return count;
    };

    const handleRemoveStop = (idx) => {
        const filteredStops = formData.stops.filter((_, i) => i !== idx);
        const newCount = recalculatePassengerCount(filteredStops, formData.additional_passenger_ids);
        setFormData(prev => ({ 
            ...prev, 
            stops: filteredStops,
            passenger_count: newCount
        }));
    };

    const handleStopAddressChange = (idx, address, lat, lng) => {
        const newStops = [...formData.stops];
        newStops[idx] = { ...newStops[idx], address, lat, lng };
        setFormData(prev => ({ ...prev, stops: newStops }));
    };

    const handleSelectStopFavoriteAddress = async (idx, address) => {
        if (!address) return;
        handleStopAddressChange(idx, address, null, null);
        try {
            const res = await getGeocode({ address });
            if (res && res.length > 0) {
                const { lat, lng } = await getLatLng(res[0]);
                if (lat !== 0 && lng !== 0) {
                    handleStopAddressChange(idx, address, lat, lng);
                }
            }
        } catch (e) {
            console.error("Error geocodificando dirección favorita de la parada:", e);
        }
    };

    const handleStopFieldChange = (idx, field, value) => {
        const newStops = [...formData.stops];
        newStops[idx] = { ...newStops[idx], [field]: value };
        
        let newCount = formData.passenger_count;
        if (field === 'passenger_name') {
            newCount = recalculatePassengerCount(newStops, formData.additional_passenger_ids);
        }
        
        setFormData(prev => ({ 
            ...prev, 
            stops: newStops,
            passenger_count: newCount
        }));
    };

    const optimizeStopsOrder = () => {
        if (!formData.stops || formData.stops.length <= 1 || !pickupLocation?.lat || !pickupLocation?.lng) {
            return;
        }

        const validStops = [];
        const invalidStops = [];
        formData.stops.forEach(s => {
            if (s.lat !== null && s.lat !== undefined && s.lng !== null && s.lng !== undefined) {
                validStops.push({ ...s });
            } else {
                invalidStops.push({ ...s });
            }
        });

        if (validStops.length === 0) return;

        const getDistance = (lat1, lng1, lat2, lng2) => {
            return Math.hypot(lat1 - lat2, lng1 - lng2);
        };

        const sortedStops = [];
        let currentLat = pickupLocation.lat;
        let currentLng = pickupLocation.lng;

        while (validStops.length > 0) {
            let closestIdx = 0;
            let minDist = getDistance(currentLat, currentLng, validStops[0].lat, validStops[0].lng);
            for (let i = 1; i < validStops.length; i++) {
                const dist = getDistance(currentLat, currentLng, validStops[i].lat, validStops[i].lng);
                if (dist < minDist) {
                    minDist = dist;
                    closestIdx = i;
                }
            }

            const closestStop = validStops.splice(closestIdx, 1)[0];
            sortedStops.push(closestStop);
            currentLat = closestStop.lat;
            currentLng = closestStop.lng;
        }

        const finalStops = [...sortedStops, ...invalidStops];
        setFormData(prev => ({ ...prev, stops: finalStops }));
        toast.success("Ruta de paradas optimizada logísticamente.");
    };

    const handleStopDragStart = (e, idx) => {
        if (isSettled) return;
        setDraggedStopIdx(idx);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", idx);
    };

    const handleStopDragOver = (e, idx) => {
        if (isSettled) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleStopDrop = (e, targetIdx) => {
        e.preventDefault();
        if (isSettled || draggedStopIdx === null || draggedStopIdx === targetIdx) return;

        const newStops = [...formData.stops];
        const [moved] = newStops.splice(draggedStopIdx, 1);
        newStops.splice(targetIdx, 0, moved);

        setFormData(prev => ({ ...prev, stops: newStops }));
        setDraggedStopIdx(null);
    };

    const handleMoveStop = (idx, direction) => {
        if (isSettled) return;
        const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= (formData.stops || []).length) return;
        const newStops = [...formData.stops];
        const [moved] = newStops.splice(idx, 1);
        newStops.splice(targetIdx, 0, moved);
        setFormData(prev => ({ ...prev, stops: newStops }));
    };



    const applySavedAddress = async (type, isPickup) => {
        const field = isPickup ? 'pickup_address' : 'dropoff_address';
        let address = '';
        
        const r = contextRequesters.find(req => req.id === parseInt(formData.requester_id));
        const p = contextPassengers.find(cp => cp.full_name === formData.passenger_name && cp.phone === formData.passenger_phone);
        
        if (type === 'HOME') {
            address = p?.home_address || r?.home_address;
        } else if (type === 'OFFICE') {
            address = p?.office_address || r?.office_address;
        }

        if (address) {
            setFormData(prev => ({ ...prev, [field]: address }));
            try {
                const res = await getGeocode({ address });
                const { lat, lng } = await getLatLng(res[0]);
                if (lat !== 0 && lng !== 0) {
                    if (isPickup) {
                        setPickupLocation({ lat, lng });
                        setFormData(prev => ({ ...prev, pickup_address: address, pickup_lat: lat, pickup_lng: lng }));
                    } else {
                        setDropoffLocation({ lat, lng });
                        setFormData(prev => ({ ...prev, dropoff_address: address, dropoff_lat: lat, dropoff_lng: lng }));
                    }
                }
            } catch (e) {
                console.error("Error geocodificando dirección guardada:", e);
            }
        }
    };

    const handleDriverPaymentChoice = async (choice) => {
        if (!pendingSubmitData) return;
        if (isSubmittingRef.current || isSubmitting) return;

        isSubmittingRef.current = true;
        setIsSubmitting(true);
        const { payload, shouldCreateReturn } = pendingSubmitData;

        // Modificar los costos y pago al chofer según la respuesta
        const updatedPayload = { ...payload };
        
        if (choice === 'none') {
            updatedPayload.provider_cost_base = 0;
            updatedPayload.provider_cost_clp = 0;
            updatedPayload.provider_cost_local = 0;
            updatedPayload.driver_earnings = 0;
        } else if (choice === 'half') {
            updatedPayload.provider_cost_base = Math.round((payload.provider_cost_base || 0) * 0.5);
            updatedPayload.provider_cost_clp = Math.round((payload.provider_cost_clp || 0) * 0.5);
            updatedPayload.provider_cost_local = Math.round((payload.provider_cost_local || 0) * 0.5);
            updatedPayload.driver_earnings = Math.round((payload.driver_earnings || 0) * 0.5);
        }
        // Si choice es 'full', se mantiene al 100% tal como venía

        // Cerrar modal de confirmación
        setShowDriverPaymentConfirm(false);
        setPendingSubmitData(null);

        // Continuar con el guardado (incluyendo verificación de duplicados)
        try {
            const checkRes = await apiClient.post('transport/trips/check-duplicates', {
                client_id: parseInt(updatedPayload.client_id),
                scheduled_time: updatedPayload.scheduled_time,
                passenger_name: updatedPayload.passenger_name,
                external_reference: updatedPayload.external_reference || null,
                aquivoy_id: updatedPayload.aquivoy_id || null,
                trip_id: isEditing ? trip.id : null,
                pickup_address: updatedPayload.pickup_address || null,
                dropoff_address: updatedPayload.dropoff_address || null,
                pickup_lat: updatedPayload.pickup_lat || null,
                pickup_lng: updatedPayload.pickup_lng || null
            });
            
            if (checkRes.data && checkRes.data.has_duplicate) {
                const confirmMsg = `${checkRes.data.reason}\n\n¿Está seguro de que desea crear/guardar esta reserva de todos modos?`;
                if (!window.confirm(confirmMsg)) {
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                    return;
                }
            }
        } catch (checkErr) {
            console.error("Error al verificar duplicados en el backend:", checkErr);
        }

        try {
            await onSave(updatedPayload, shouldCreateReturn);
        } catch (err) {
            console.error("Error al guardar viaje en onSave:", err);
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e, shouldCreateReturn = false) => {
        if (e) e.preventDefault();

        if (isSubmittingRef.current || isSubmitting) return;

        isSubmittingRef.current = true;
        setIsSubmitting(true);

        const abortSubmit = () => {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        };

        // Validación obligatoria de Solicitante
        if (!formData.requester_id) {
            toast.error("El campo 'Solicitante' es obligatorio.");
            abortSubmit();
            return;
        }

        // Si el usuario es locutora, no permitir crear o cambiar viajes a fechas del pasado (tolerancia 2 horas)
        if (userRole === 'locutor' && formData.scheduled_time) {
            let dateChanged = true;
            if (isEditing && trip && trip.scheduled_time) {
                const originalDate = new Date(trip.scheduled_time).getTime();
                const newDate = new Date(formData.scheduled_time).getTime();
                // Tolerancia de 60 segundos por diferencias de redondeo de milisegundos
                if (Math.abs(originalDate - newDate) < 60 * 1000) {
                    dateChanged = false;
                }
            }

            if (dateChanged) {
                const svcDate = new Date(formData.scheduled_time);
                const limitPast = new Date(Date.now() - 2 * 60 * 60 * 1000);
                if (svcDate < limitPast) {
                    toast.error("El perfil de locutoras no tiene permisos para crear o programar servicios con fecha/hora anterior a la actual (en el pasado).");
                    abortSubmit();
                    return;
                }
            }
        }
        
        // Validación obligatoria de código de autorización para Webpay, Getnet, Mercado Pago o DLocal
        if (isParticular && formData.is_paid && ['Webpay', 'Getnet', 'Mercado Pago', 'DLocal'].includes(formData.payment_method)) {
            if (!formData.payment_auth_code || !formData.payment_auth_code.trim()) {
                toast.error("El Código de autorización es obligatorio para pagos con Webpay, Getnet, Mercado Pago o DLocal.");
                abortSubmit();
                return;
            }
            if (formData.payment_method === 'DLocal') {
                if (!/^[a-zA-Z0-9_\-:|]+$/.test(formData.payment_auth_code.trim())) {
                    toast.error("El Código de autorización de dLocal contiene caracteres no válidos.");
                    abortSubmit();
                    return;
                }
            } else if (!/^\d+$/.test(formData.payment_auth_code.trim())) {
                toast.error("El Código de autorización solo debe contener números.");
                abortSubmit();
                return;
            }
        }

        // Validación de Documentos Clave de Conductor y Vehículo
        if (formData.driver_id || formData.vehicle_id) {
            try {
                const svcDateStr = formData.scheduled_time ? formData.scheduled_time.split("T")[0] : new Date().toISOString().split("T")[0];
                const valRes = await apiClient.get(`transport/drivers/${formData.driver_id || 0}/validate-for-service?service_date=${svcDateStr}&vehicle_id=${formData.vehicle_id || ''}`);
                if (valRes.data && !valRes.data.is_valid) {
                    if (!formData.force_driver_assignment) {
                        toast.error(valRes.data.error_message || "Documento clave vencido. No puedes seleccionar este conductor/vehículo.");
                        abortSubmit();
                        return;
                    }
                }
            } catch (err) {
                console.warn("⚠️ Error en validación previa de documentos:", err);
            }
        }

        // Validación de Capacidad Física del Móvil Asignado (BLOQUEO INFRANQUEABLE)
        if (formData.driver_id) {
            const assignedVehicle = vehicles.find(v => v.current_driver_id === parseInt(formData.driver_id) && v.is_active);
            if (assignedVehicle) {
                const vType = vehicleTypes.find(vt => vt.id === assignedVehicle.vehicle_type_id);
                const reqVT = vehicleTypes.find(vt => vt.id === parseInt(formData.requested_vehicle_type_id));
                const requiredCap = Math.max(reqVT?.capacity || 1, parseInt(formData.passenger_count || 1));
                if (vType && vType.capacity < requiredCap) {
                    toast.error(`🚫 Capacidad física insuficiente: El móvil [${assignedVehicle.internal_code}] tiene capacidad para ${vType.capacity} pasajeros, pero este viaje requiere ${requiredCap} pax. No es posible asignar este vehículo.`);
                    abortSubmit();
                    return;
                }
            }
        }

        const isPickupAirport = isAirportAddress(formData.pickup_address);
        const isServiceFarAhead = (() => {
            if (!formData.scheduled_time) return false;
            const now = new Date();
            const svcDate = new Date(formData.scheduled_time);
            const diffDays = (svcDate - now) / (1000 * 60 * 60 * 24);
            return diffDays > 2;
        })();
        const flightRequired = isPickupAirport && !isServiceFarAhead;

        if (flightRequired && !formData.flight_number?.trim()) {
            toast.error("El número de vuelo es obligatorio para traslados desde el aeropuerto (recogida en el aeropuerto) con menos de 2 días de anticipación.");
            abortSubmit();
            return;
        }
        
        let finalScheduledTime = formData.scheduled_time;
        let recurrenceDates = [];

        if (!isEditing && isRepeatChecked) {
            if (!formData.scheduled_time) {
                toast.error("Por favor configure la fecha y hora del servicio principal.");
                abortSubmit();
                return;
            }

            if (repeatMode === 'weekly') {
                if (!repeatStartDate || !repeatEndDate) {
                    toast.error("Por favor complete el rango de fechas para el patrón semanal.");
                    abortSubmit();
                    return;
                }
                const hasSelectedDays = Object.values(repeatDays).some(v => v);
                if (!hasSelectedDays) {
                    toast.error("Por favor seleccione al menos un día de la semana a repetir.");
                    abortSubmit();
                    return;
                }

                const start = new Date(repeatStartDate + 'T00:00:00');
                const end = new Date(repeatEndDate + 'T23:59:59');
                if (start > end) {
                    toast.error("La fecha de inicio no puede ser posterior a la de término.");
                    abortSubmit();
                    return;
                }

                const timePart = formData.scheduled_time.split('T')[1] || '08:00';
                const mainDatePart = formData.scheduled_time.split('T')[0];
                const allDates = [];
                let current = new Date(start);
                while (current <= end) {
                    const dayOfWeek = current.getDay(); // 0 = Dom, 1 = Lun, ..., 6 = Sab
                    if (repeatDays[dayOfWeek]) {
                        const year = current.getFullYear();
                        const month = String(current.getMonth() + 1).padStart(2, '0');
                        const day = String(current.getDate()).padStart(2, '0');
                        const dateStr = `${year}-${month}-${day}`;
                        if (dateStr !== mainDatePart) {
                            allDates.push(`${dateStr}T${timePart}`);
                        }
                    }
                    current.setDate(current.getDate() + 1);
                }

                allDates.sort();
                finalScheduledTime = formData.scheduled_time;
                recurrenceDates = allDates;
            } else if (repeatMode === 'manual') {
                const validManual = manualDates.filter(d => d && d.trim() !== '');
                if (validManual.length === 0) {
                    toast.error("Por favor ingrese al menos una fecha adicional en el panel manual.");
                    abortSubmit();
                    return;
                }
                validManual.sort();
                recurrenceDates = validManual;
            }
        }
        
        let finalStatus = formData.status?.toLowerCase() || 'pending';

        // Si es una creación (viaje nuevo, clonado o recurrente) no puede nacer finalizado ni en proceso
        if (!isEditing) {
            if (['completed', 'on_board', 'arrived'].includes(finalStatus)) {
                finalStatus = (formData.driver_id && formData.driver_id !== '') ? 'assigned' : 'pending';
            }
        }

        if ((!formData.driver_id || formData.driver_id === '') && !['cancelled', 'no_show', 'completed', 'cancelled_paid', 'anulado'].includes(finalStatus)) {
            finalStatus = 'pending';
        }
        if (formData.driver_id && formData.driver_id !== '' && finalStatus === 'pending') {
            finalStatus = 'assigned';
        }

        // REGLA ALEJANDRO: Si es una reserva pasada y se ingresan tiempos reales de inicio y fin,
        // marcar automáticamente como Finalizado para evitar alarmas innecesarias (excepto si es cancelada o anulada).
        if (formData.start_time && formData.end_time && !['cancelled', 'cancelled_paid', 'no_show', 'anulado'].includes(finalStatus)) {
            finalStatus = 'completed';
        }

        const finalExtras = [...(formData.extra_services || [])];
        if (formData.has_reimbursable_expense) {
            finalExtras.push({
                id: 'reimbursable_expense',
                name: 'Gasto Reembolsable',
                price: parseFloat(formData.reimbursable_expense_price) || 0,
                cost: parseFloat(formData.reimbursable_expense_cost) || 0
            });
        }

        const isCLP = !formData.local_currency_code || formData.local_currency_code === 'CLP';
        const estPrice = parseFloat(formData.estimated_price) || 0;
        let submitCostBase = parseFloat(formData.provider_cost_base) || 0;
        let submitCostClp = parseFloat(formData.provider_cost_clp) || 0;
        let submitCostLocal = parseFloat(formData.provider_cost_local) || 0;
        if (submitCostBase === 0 && estPrice > 0) {
            submitCostBase = estPrice;
            if (submitCostClp === 0) submitCostClp = estPrice;
            if (submitCostLocal === 0) submitCostLocal = estPrice;
        }

        const payload = {
            ...formData,
            estimated_price: estPrice,
            provider_cost_base: submitCostBase,
            provider_cost_clp: submitCostClp,
            provider_cost_local: submitCostLocal,
            driver_earnings: isCLP ? marginAnalysis.driverPayment : (submitCostLocal || 0),
            waiting_time_amount: parseFloat(formData.waiting_time_amount) || 0,
            waiting_time_provider_cost: parseFloat(formData.waiting_time_provider_cost) || 0,
            passenger_count: parseInt(formData.passenger_count) || 1,
            suitcases: parseInt(formData.suitcases) || 0,
            scheduled_time: finalScheduledTime,
            status: finalStatus,
            is_asap: formData.is_asap || false,
            is_disposal: formData.is_disposal || false,
            asap_status: (formData.is_asap && !formData.driver_id) ? 'searching' : 'idle',
            start_time: isEditing ? (formData.start_time || null) : null,
            end_time: isEditing ? (formData.end_time || null) : null,
            area_id: formData.area_id === '' ? null : parseInt(formData.area_id),
            cost_center_id: formData.cost_center_id === '' ? null : parseInt(formData.cost_center_id),
            driver_id: formData.driver_id === '' ? null : parseInt(formData.driver_id),
            provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
            requested_vehicle_type_id: formData.requested_vehicle_type_id === '' ? null : parseInt(formData.requested_vehicle_type_id),
            requester_id: formData.requester_id === '' ? null : parseInt(formData.requester_id),
            client_id: parseInt(formData.client_id),
            settlement_id: formData.settlement_id ? parseInt(formData.settlement_id) : null,
            extra_services: finalExtras,
            is_paid: formData.is_paid || false,
            payment_method: formData.payment_method || null,
            payment_auth_code: ['Webpay', 'Getnet', 'Mercado Pago', 'DLocal'].includes(formData.payment_method) ? formData.payment_auth_code : null,
            file: formData.file ? formData.file.trim() : null,
            external_reference: formData.external_reference ? formData.external_reference.trim() : null,
            flight_number: formData.flight_number ? formData.flight_number.trim() : null,
            oc_number: formData.oc_number ? formData.oc_number.trim() : null,
            recurrenceDates: recurrenceDates
        };

        delete payload.has_reimbursable_expense;
        delete payload.reimbursable_expense_cost;
        delete payload.reimbursable_expense_price;

        // Interceptar si el estado es Cancelado con Cargo para preguntar sobre el pago al conductor
        if (finalStatus === 'cancelled_paid') {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            setPendingSubmitData({ payload, shouldCreateReturn });
            setShowDriverPaymentConfirm(true);
            return;
        }

        // --- VERIFICACIÓN DE TRASLAPE / RESERVAS CERCANAS DEL MISMO CONDUCTOR ---
        if (payload.driver_id && !['cancelled', 'cancelled_paid', 'no_show', 'anulado', 'completed'].includes(finalStatus)) {
            try {
                const rawSched = String(payload.scheduled_time || '').trim();
                const normSched = rawSched.length === 16 ? `${rawSched}:00` : rawSched;
                let activeConflicts = driverConflictWarning;
                if (!activeConflicts) {
                    const confRes = await apiClient.post('transport/trips/check-driver-conflict', {
                        driver_id: parseInt(payload.driver_id),
                        vehicle_id: payload.vehicle_id ? parseInt(payload.vehicle_id) : null,
                        scheduled_time: normSched,
                        estimated_duration_minutes: parseInt(formData.estimated_duration_minutes) || 30,
                        trip_id: isEditing ? (trip?.id || null) : null,
                        origin_base_id: formData.origin_base_id ? parseInt(formData.origin_base_id) : (trip?.origin_base_id || null),
                        timezone: formData.origin_base?.timezone || formData.timezone || trip?.origin_base?.timezone || null,
                        flight_number: payload.flight_number || null,
                        pickup_address: payload.pickup_address || null,
                        pickup_lat: payload.pickup_lat ? parseFloat(payload.pickup_lat) : null,
                        pickup_lng: payload.pickup_lng ? parseFloat(payload.pickup_lng) : null,
                        dropoff_lat: payload.dropoff_lat ? parseFloat(payload.dropoff_lat) : null,
                        dropoff_lng: payload.dropoff_lng ? parseFloat(payload.dropoff_lng) : null,
                    });
                    if (confRes.data?.has_conflict && confRes.data.conflicts?.length > 0) {
                        activeConflicts = confRes.data.conflicts;
                        setDriverConflictWarning(activeConflicts);
                    }
                }
                if (activeConflicts && activeConflicts.length > 0) {
                    const lines = activeConflicts.map(
                        (c) => `• Reserva #${c.trip_id} (${c.scheduled_time} hrs) — ${c.passenger_name || 'Pasajero'}\n  ${c.conflict_reason || ''}`
                    ).join('\n');
                    const warnMsg = `⚠️ ¡ATENCIÓN: CONDUCTOR CON RESERVAS MUY CERCANAS!\n\nEl conductor seleccionado ya tiene ${activeConflicts.length} servicio(s) cercano(s):\n\n${lines}\n\n¿Desea guardar y mantener asignado a este conductor de todas formas?`;
                    if (!window.confirm(warnMsg)) {
                        isSubmittingRef.current = false;
                        setIsSubmitting(false);
                        return;
                    }
                }
            } catch (confErr) {
                console.error("Error verificando traslape de conductor al guardar:", confErr);
            }
        }

        // --- VERIFICACIÓN DE DUPLICADOS INTERACTIVA (Aviso para el locutor) ---
        try {
            const checkRes = await apiClient.post('transport/trips/check-duplicates', {
                client_id: parseInt(payload.client_id),
                scheduled_time: payload.scheduled_time,
                passenger_name: payload.passenger_name,
                external_reference: payload.external_reference || null,
                aquivoy_id: payload.aquivoy_id || null,
                trip_id: isEditing ? trip.id : null,
                pickup_address: payload.pickup_address || null,
                dropoff_address: payload.dropoff_address || null,
                pickup_lat: payload.pickup_lat || null,
                pickup_lng: payload.pickup_lng || null
            });
            
            if (checkRes.data && checkRes.data.has_duplicate) {
                const confirmMsg = `${checkRes.data.reason}\n\n¿Está seguro de que desea crear/guardar esta reserva de todos modos?`;
                if (!window.confirm(confirmMsg)) {
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                    return; // Detener guardado si el locutor cancela
                }
            }
        } catch (checkErr) {
            console.error("Error al verificar duplicados en el backend:", checkErr);
            // Si falla la red/API de chequeo, dejamos pasar el guardado principal para evitar bloqueos del servicio
        }

        try {
            await onSave(payload, shouldCreateReturn);
        } catch (err) {
            console.error("Error al guardar viaje en onSave:", err);
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }

        // --- LÓGICA DE RETORNO (ROUND TRIP) ---
        if (shouldCreateReturn) {
            // RECUPERACIÓN ROBUSTA: Prioridad absoluta al estado del MAPA
            const latOrig = pickupLocation?.lat || formData.pickup_lat;
            const lngOrig = pickupLocation?.lng || formData.pickup_lng;
            const latDest = dropoffLocation?.lat || formData.dropoff_lat;
            const lngDest = dropoffLocation?.lng || formData.dropoff_lng;
            const hasCoords = latOrig && lngOrig && latDest && lngDest;

            const returnData = {
                // CAMPOS QUE SE MANTIENEN (Datos del cliente/pax)
                client_id: parseInt(formData.client_id),
                requester_id: formData.requester_id ? parseInt(formData.requester_id) : null,
                area_id: formData.area_id ? parseInt(formData.area_id) : null,
                cost_center_id: formData.cost_center_id ? parseInt(formData.cost_center_id) : null,
                passenger_name: formData.passenger_name,
                passenger_phone: formData.passenger_phone,
                passenger_count: formData.passenger_count || 1,
                suitcases: parseInt(formData.suitcases || 0),
                requested_vehicle_type_id: formData.requested_vehicle_type_id ? parseInt(formData.requested_vehicle_type_id) : null,
                external_reference: formData.external_reference,
                notes: formData.notes,
                internal_notes: formData.internal_notes,
                additional_passenger_ids: formData.additional_passenger_ids || [],
                is_disposal: formData.is_disposal || false,

                // --- LIMPIEZA PARA EL NUEVO SERVICIO DE VUELTA ---
                id: undefined, 
                status: 'pending',
                admin_status: 'pending_review',
                scheduled_time: '', 
                flight_number: '',
                driver_id: '',
                vehicle_id: null,
                estimated_price: 0,
                provider_cost_base: 0,
                provider_cost_local: 0,
                driver_earnings: 0,
                start_time: null,
                end_time: null,
                gps_en_route_time: null,
                gps_arrived_time: null,
                validation_folio: null,
                settlement_id: null,
                client_invoice_id: null,
                
                // --- INVERSIÓN DE DIRECCIONES ---
                pickup_address: formData.dropoff_address,
                pickup_lat: hasCoords ? latDest : null,
                pickup_lng: hasCoords ? lngDest : null,
                dropoff_address: formData.pickup_address,
                dropoff_lat: hasCoords ? latOrig : null,
                dropoff_lng: hasCoords ? lngOrig : null,
                stops: [] 
            };

            // Transformar el modal en "Modo Creación de Retorno"
            setFormData(returnData);
            setIsCreatingReturn(true); // ESTO ES CLAVE
            
            if (hasCoords) {
                setPickupLocation({ lat: latDest, lng: lngDest });
                setDropoffLocation({ lat: latOrig, lng: lngOrig });
            } else {
                setPickupLocation(null);
                setDropoffLocation(null);
            }

            setAvailableQuotes([]);
            setPriceBreakdown(null);
            setRouteDistanceKm(0);
            setRouteDurationMin(0);
            
            toast.success("✅ Ida guardada. Ingrese los datos para el RETORNO.");
        } else {
            setIsCreatingReturn(false);
        }
    };



    const evaluatedDrivers = useMemo(() => {
        if (!Array.isArray(drivers)) return [];

        const tripDateStr = formData.scheduled_time ? formData.scheduled_time.split('T')[0] : null;
        const tripDate = formData.scheduled_time ? new Date(formData.scheduled_time) : null;

        // Base y Capacidad requerida
        const reqVehicleType = vehicleTypes.find(vt => vt.id === parseInt(formData.requested_vehicle_type_id));
        const requiredCapacity = Math.max(
            reqVehicleType ? reqVehicleType.capacity : 1,
            parseInt(formData.passenger_count || 1)
        );

        // ASISTENTE DE EQUIDAD: Categoría relevante para sugerencias
        const reqTypeName = reqVehicleType?.name?.toUpperCase()?.trim() || '';
        let targetCategory = null;
        if (['STANDARD', 'EXECUTIVE', 'LUXURY'].includes(reqTypeName)) {
            targetCategory = 'standard';
        } else if (['PEOPLE_CARRIER', 'LARGE_PEOPLE_CARRIER'].includes(reqTypeName)) {
            targetCategory = 'van';
        }

        const relevantRecs = targetCategory
            ? (recommendedDrivers || []).filter(r => r.category === targetCategory)
            : (recommendedDrivers || []);

        return drivers.map(d => {
            const vehicle = vehicles.find(v => v.current_driver_id === d.id && v.is_active);
            if (!d.has_active_session || !vehicle) return null;

            const reasons = [];

            // 1. Filtro por Base de Origen
            if (formData.origin_base_id) {
                const isBaseAllowed = vehicle.permitted_bases?.some(b => String(b.id) === String(formData.origin_base_id));
                if (!isBaseAllowed) {
                    reasons.push("Móvil no autorizado para base de origen");
                }
            }

            // 2. Filtro por Capacidad (BLOQUEO FÍSICO DURO)
            const vType = vehicleTypes.find(vt => vt.id === vehicle.vehicle_type_id);
            let hasCapacityBlock = false;
            let capacityDetail = '';
            if (vType && vType.capacity < requiredCapacity) {
                hasCapacityBlock = true;
                capacityDetail = `${requiredCapacity} pax req vs ${vType.capacity} móvil`;
                reasons.push(`Capacidad insuficiente (${capacityDetail})`);
            }

            // 3. Licencia de Conducir Vencida para la fecha
            if (tripDate && d.license_expiry) {
                const licExp = new Date(d.license_expiry + 'T23:59:59');
                if (licExp < tripDate) {
                    const [y, m, day] = d.license_expiry.split('-');
                    reasons.push(`Licencia vencida para esta fecha (Vence ${day}/${m}/${y})`);
                }
            }

            // 4. Períodos de Ausencia / Vacaciones
            if (tripDateStr && Array.isArray(d.unavailabilities)) {
                for (const u of d.unavailabilities) {
                    if (u.start_date <= tripDateStr && tripDateStr <= u.end_date) {
                        reasons.push(`De ${u.reason || 'Ausencia'} (${u.start_date} al ${u.end_date})`);
                    }
                }
            }

            // 5. Horario Semanal Habitual (con soporte para turnos nocturnos / trasnoche)
            if (tripDate && d.weekly_schedule && typeof d.weekly_schedule === 'object') {
                const jsDay = tripDate.getDay();
                const isoDay = jsDay === 0 ? "7" : String(jsDay);
                const dayConfig = d.weekly_schedule[isoDay];
                
                const tripHours = String(tripDate.getHours()).padStart(2, '0');
                const tripMins = String(tripDate.getMinutes()).padStart(2, '0');
                const tripTime = `${tripHours}:${tripMins}`;

                let isWorking = false;

                // 5.1 Evaluar turno del día actual
                if (dayConfig && dayConfig.active && dayConfig.start && dayConfig.end) {
                    const isOvernight = dayConfig.start > dayConfig.end;
                    const inTodayShift = isOvernight
                        ? (tripTime >= dayConfig.start || tripTime <= dayConfig.end)
                        : (tripTime >= dayConfig.start && tripTime <= dayConfig.end);
                    if (inTodayShift) {
                        isWorking = true;
                    }
                }

                // 5.2 Evaluar si el día anterior tuvo turno trasnoche que cubre la madrugada de hoy
                if (!isWorking) {
                    const prevJsDay = (jsDay + 6) % 7;
                    const prevIsoDay = prevJsDay === 0 ? "7" : String(prevJsDay);
                    const prevDayConfig = d.weekly_schedule[prevIsoDay];
                    if (prevDayConfig && prevDayConfig.active && prevDayConfig.start && prevDayConfig.end) {
                        if (prevDayConfig.start > prevDayConfig.end && tripTime <= prevDayConfig.end) {
                            isWorking = true;
                        }
                    }
                }

                if (!isWorking) {
                    if (dayConfig && !dayConfig.active) {
                        const dayNames = { "1": "Lunes", "2": "Martes", "3": "Miércoles", "4": "Jueves", "5": "Viernes", "6": "Sábado", "7": "Domingo" };
                        reasons.push(`Día libre habitual (${dayNames[isoDay] || ''})`);
                    } else if (dayConfig && dayConfig.start && dayConfig.end) {
                        reasons.push(`Fuera de jornada laboral (${dayConfig.start} a ${dayConfig.end})`);
                    }
                }
            }

            // 6. Documentos del Vehículo (SOAP / Revisión Técnica)
            if (tripDate && vehicle.technical_revision_expiry) {
                const revExp = new Date(vehicle.technical_revision_expiry + 'T23:59:59');
                if (revExp < tripDate) {
                    reasons.push("Revisión Técnica del móvil vencida");
                }
            }
            if (tripDate && vehicle.soap_expiry) {
                const soapExp = new Date(vehicle.soap_expiry + 'T23:59:59');
                if (soapExp < tripDate) {
                    reasons.push("SOAP del móvil vencido");
                }
            }

            // 7. Restricción Solo Booking
            let hasBookingOnlyBlock = false;
            if (d.only_booking && formData.client_id && parseInt(formData.client_id) !== 1) {
                hasBookingOnlyBlock = true;
                reasons.push("Conductor restringido: Solo habilitado para servicios de Booking");
            }

            const isAvailable = reasons.length === 0;
            const rec = relevantRecs.find(r => r.driver_id === d.id);
            const isRecommended = Boolean(rec && isAvailable && !hasCapacityBlock && !hasBookingOnlyBlock);

            return {
                id: d.id,
                full_name: d.full_name,
                internal_code: vehicle?.internal_code,
                plate: d.assigned_vehicle_plate || vehicle?.plate,
                is_available: isAvailable,
                has_capacity_block: hasCapacityBlock,
                has_booking_only_block: hasBookingOnlyBlock,
                has_hard_block: hasCapacityBlock || hasBookingOnlyBlock,
                capacity_detail: capacityDetail,
                unavailability_reason: reasons.join(' • '),
                reasons: reasons,
                is_forced: formData.force_driver_assignment && String(formData.driver_id) === String(d.id),
                is_recommended: isRecommended,
                recommendation_badge: rec?.recommendation_badge || '⭐ Sugerido',
                trips_last_30d: rec?.trips_last_30d,
                score_rank: rec?.score_rank ?? 9999
            };
        }).filter(Boolean).sort((a, b) => {
            const aHard = a.has_capacity_block || a.has_booking_only_block;
            const bHard = b.has_capacity_block || b.has_booking_only_block;
            if (aHard && !bHard) return 1;
            if (!aHard && bHard) return -1;
            if (a.is_recommended && !b.is_recommended) return -1;
            if (!a.is_recommended && b.is_recommended) return 1;
            if (a.is_recommended && b.is_recommended) {
                return (a.score_rank || 0) - (b.score_rank || 0);
            }
            if (a.is_available && !b.is_available) return -1;
            if (!a.is_available && b.is_available) return 1;
            return (a.full_name || '').localeCompare(b.full_name || '');
        });
    }, [drivers, vehicles, vehicleTypes, formData.scheduled_time, formData.origin_base_id, formData.requested_vehicle_type_id, formData.passenger_count, formData.force_driver_assignment, formData.driver_id, formData.client_id, recommendedDrivers]);

    // Efecto para calcular ganancias segun contrato del conductor (Solo en Chile/CLP)
    useEffect(() => {
        if (!formData.driver_id || (formData.local_currency_code && formData.local_currency_code !== 'CLP')) return;
        
        const driver = drivers.find(d => d.id === parseInt(formData.driver_id));
        if (driver?.contract) {
            let costoBase = parseFloat(formData.provider_cost_base) || 0;
            if (costoBase === 0 && parseFloat(formData.estimated_price) > 0) {
                costoBase = parseFloat(formData.estimated_price) || 0;
            }
            const waitCosto = parseFloat(formData.waiting_time_provider_cost) || 0;
            const totalExtras = (formData.extra_services || [])
                .filter(s => s.id !== 'reimbursable_expense')
                .reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
            const totalCostoParaComision = costoBase + waitCosto + totalExtras;
            
            const isHybridBooking = driver.provider?.hybrid_settlement && parseInt(formData.client_id) === 1;

            if (isHybridBooking) {
                // Proveedores híbridos (ej. RedGo): en Booking reciben 100% de la tarifa fija
                setFormData(prev => ({ 
                    ...prev, 
                    driver_earnings: totalCostoParaComision
                }));
            } else {
                const { type, percentage_1, percentage_2, threshold } = driver.contract;
                
                if (type === "VARIABLE") {
                    // El porcentaje y el umbral se basan en el Costo Total (Base + Espera)
                    const p = totalCostoParaComision < threshold ? percentage_1 : percentage_2;
                    const earnings = Math.round(totalCostoParaComision * (p / 100));
                    // Actualizamos SOLO las ganancias, dejamos el costo base intacto
                    setFormData(prev => ({ 
                        ...prev, 
                        driver_earnings: earnings
                    }));
                } else if (type === "FIJO") {
                    // Si es fijo, las ganancias son el 100% del costo total (Base + Espera)
                    setFormData(prev => ({ 
                        ...prev, 
                        driver_earnings: totalCostoParaComision
                    }));
                }
            }
        }
    }, [formData.driver_id, formData.client_id, formData.provider_cost_base, formData.estimated_price, formData.waiting_time_provider_cost, drivers, formData.local_currency_code, formData.extra_services]);

    // AUTO-DESELECCION SI EL VEHICULO DEL CHOFER TIENE MENOR CAPACIDAD QUE LA SOLICITADA
    useEffect(() => {
        const reqVehicleType = vehicleTypes.find(vt => vt.id === parseInt(formData.requested_vehicle_type_id));
        const requiredCapacity = Math.max(
            reqVehicleType ? reqVehicleType.capacity : 1,
            parseInt(formData.passenger_count || 1)
        );

        // Validar Conductor Seleccionado
        if (formData.driver_id) {
            const vehicle = vehicles.find(v => v.current_driver_id === parseInt(formData.driver_id) && v.is_active);
            if (vehicle) {
                const vType = vehicleTypes.find(vt => vt.id === vehicle.vehicle_type_id);
                if (vType && vType.capacity < requiredCapacity) {
                    setFormData(prev => ({ ...prev, driver_id: '', force_driver_assignment: false }));
                }
            }
        }
    }, [formData.passenger_count, vehicleTypes, vehicles, formData.requested_vehicle_type_id, formData.driver_id]);

    const currentStatus = formData.status?.toLowerCase() || '';
    const isSettled = !!trip?.settlement_id;
    const isLocked = isSettled || !!trip?.client_invoice_id || currentStatus === 'anulado';
    const isInProgress = ['on_board'].includes(currentStatus);
    
    const isLocutor = userRole === 'locutor';
    // Reglas de edición estrictas para Admin
    const canEditPrices = (isAdmin || isLocutor) && !isLocked;
    const canEditDriver = (isAdmin || isLocutor) && !isLocked && !isInProgress;

    // --- FILTRADO DE CLIENTES ACTIVOS ---
    // --- LÓGICA DE MARGEN Y TOTALES EN TIEMPO REAL ---
    const marginAnalysis = useMemo(() => {
        const baseVenta = parseFloat(formData.estimated_price) || 0;
        const totalExtras = (formData.extra_services || [])
            .filter(s => s.id !== 'reimbursable_expense')
            .reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
        const waitVenta = parseFloat(formData.waiting_time_amount) || 0;
        const reimbursableVenta = formData.has_reimbursable_expense ? (parseFloat(formData.reimbursable_expense_price) || 0) : 0;
        const reimbursableCosto = formData.has_reimbursable_expense ? (parseFloat(formData.reimbursable_expense_cost) || 0) : 0;
        
        const ventaTotal = baseVenta + totalExtras + waitVenta + reimbursableVenta;
        
        const baseCosto = parseFloat(formData.provider_cost_base) || 0;
        const waitCosto = parseFloat(formData.waiting_time_provider_cost) || 0;
        const driverId = parseInt(formData.driver_id);
        
        const totalCostoParaComision = baseCosto + waitCosto + totalExtras;
        
        let profit = 0;
        let marginPct = 0;
        let marginColor = 'text-white';
        let costoTotalConductor = 0;
        let utilidadAji = 0;

        if (ventaTotal > 0) {
            const driver = drivers.find(d => d.id === driverId);
            const isHybridBooking = driver?.provider?.hybrid_settlement && parseInt(formData.client_id) === 1;

            if (isHybridBooking) {
                costoTotalConductor = totalCostoParaComision + reimbursableCosto;
                utilidadAji = ventaTotal - costoTotalConductor;
            } else if (driver?.contract && driver.contract.type === 'VARIABLE') {
                const { percentage_1, percentage_2, threshold } = driver.contract;
                const pConductor = totalCostoParaComision < threshold ? percentage_1 : percentage_2;
                const costoConductorComisionable = totalCostoParaComision * (pConductor / 100);
                costoTotalConductor = costoConductorComisionable + reimbursableCosto;
                utilidadAji = ventaTotal - costoTotalConductor;
            } else if (driver?.contract && driver.contract.type === 'FIJO') {
                costoTotalConductor = totalCostoParaComision + reimbursableCosto;
                utilidadAji = ventaTotal - costoTotalConductor;
            } else {
                costoTotalConductor = baseCosto + waitCosto + reimbursableCosto;
                utilidadAji = ventaTotal - costoTotalConductor;
            }
            
            profit = Math.round(utilidadAji);
            marginPct = Math.round((utilidadAji / ventaTotal) * 100);

            marginColor = 'text-success'; 
            if (marginPct < 25) marginColor = 'text-danger';
            else if (marginPct < 35) marginColor = 'text-warning';
        }

        return {
            ventaTotal,
            totalExtras,
            waitVenta,
            waitCosto,
            reimbursableVenta,
            reimbursableCosto,
            profit,
            driverPayment: costoTotalConductor,
            pct: marginPct,
            color: marginColor
        };
    }, [formData.estimated_price, formData.extra_services, formData.provider_cost_base, formData.waiting_time_amount, formData.waiting_time_provider_cost, formData.driver_id, formData.client_id, formData.has_reimbursable_expense, formData.reimbursable_expense_cost, formData.reimbursable_expense_price, drivers]);

    // --- CHECK DE CONFLICTO / TRASLAPE DE HORARIOS DE CHOFER ---
    useEffect(() => {
        if (!show || !formData.driver_id || !formData.scheduled_time) {
            setDriverConflictWarning(null);
            return;
        }
        const currSt = (formData.status || '').toLowerCase();
        if (['cancelled', 'cancelled_paid', 'no_show', 'anulado', 'completed'].includes(currSt)) {
            setDriverConflictWarning(null);
            return;
        }

        const checkConflict = async () => {
            try {
                const rawSched = String(formData.scheduled_time).trim();
                const scheduledDateTimeStr = rawSched.length === 16 ? `${rawSched}:00` : rawSched;
                const res = await apiClient.post('transport/trips/check-driver-conflict', {
                    driver_id: parseInt(formData.driver_id),
                    vehicle_id: formData.vehicle_id ? parseInt(formData.vehicle_id) : null,
                    scheduled_time: scheduledDateTimeStr,
                    estimated_duration_minutes: parseInt(formData.estimated_duration_minutes) || 30,
                    trip_id: isEditing ? (trip?.id || null) : null,
                    origin_base_id: formData.origin_base_id ? parseInt(formData.origin_base_id) : (trip?.origin_base_id || null),
                    timezone: formData.origin_base?.timezone || formData.timezone || trip?.origin_base?.timezone || null,
                    flight_number: formData.flight_number || null,
                    pickup_address: formData.pickup_address || null,
                    pickup_lat: formData.pickup_lat ? parseFloat(formData.pickup_lat) : null,
                    pickup_lng: formData.pickup_lng ? parseFloat(formData.pickup_lng) : null,
                    dropoff_lat: formData.dropoff_lat ? parseFloat(formData.dropoff_lat) : null,
                    dropoff_lng: formData.dropoff_lng ? parseFloat(formData.dropoff_lng) : null
                });

                if (res.data?.has_conflict && res.data.conflicts?.length > 0) {
                    setDriverConflictWarning(res.data.conflicts);
                } else {
                    setDriverConflictWarning(null);
                }
            } catch (e) {
                console.error("Error al verificar conflicto de conductor:", e);
            }
        };

        const timer = setTimeout(checkConflict, 250);
        return () => clearTimeout(timer);
    }, [show, formData.driver_id, formData.vehicle_id, formData.status, formData.scheduled_time, formData.estimated_duration_minutes, formData.origin_base_id, formData.origin_base?.timezone, formData.timezone, formData.flight_number, formData.pickup_address, formData.pickup_lat, formData.pickup_lng, formData.dropoff_lat, formData.dropoff_lng, isEditing, trip]);

    // --- LÓGICA DE CALCULADORA INVERSA (FIJAR MARGEN %) ---
    const handleMarginChange = (targetPct) => {
        const m = parseFloat(targetPct) || 0;
        if (m >= 100) return;

        const costoTotal = parseFloat(formData.provider_cost_base) || 0;

        const factorMargen = 1 - (m / 100);
        const nuevaVentaTotal = Math.round(costoTotal / factorMargen);
        
        // Restamos los extras actuales para fijar el PRECIO BASE del vehículo
        const totalExtras = (formData.extra_services || []).reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
        const nuevoPrecioVehiculo = Math.max(0, nuevaVentaTotal - totalExtras);
        
        setFormData(prev => ({
            ...prev,
            estimated_price: nuevoPrecioVehiculo,
            final_price: nuevoPrecioVehiculo
        }));

        const pct = surchargePct;
        if (pct > 0) {
            setCustomVentaBase(Math.round(nuevoPrecioVehiculo / (1 + pct / 100)));
        } else {
            setCustomVentaBase(nuevoPrecioVehiculo);
        }
    };

    const activeClients = useMemo(() => {

        if (!clients) return [];
        return clients.filter(c => {
            // Mostrar si está activo
            if (c.is_active) return true;
            // O mostrar si es el cliente del viaje que estamos editando (para no romper la vista)
            if (isEditing && formData.client_id === c.id) return true;
            return false;
        });
    }, [clients, isEditing, formData.client_id]);



    const selectedPax = useMemo(() => {
        if (!contextPassengers || !formData.passenger_name) return null;
        return contextPassengers.find(p => p.full_name === formData.passenger_name && p.phone === formData.passenger_phone);
    }, [contextPassengers, formData.passenger_name, formData.passenger_phone]);

    const selectedRequester = useMemo(() => {
        if (!contextRequesters || !formData.requester_id) return null;
        return contextRequesters.find(r => r.id === parseInt(formData.requester_id));
    }, [contextRequesters, formData.requester_id]);

    return (
        <>
        <Modal show={show} onHide={onHide} size="xl" backdrop="static">
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton className={isCreatingReturn ? "bg-primary bg-opacity-10" : "bg-light"}>
                    <Modal.Title className="fw-bold">
                        {isCreatingReturn ? (
                            <span className="text-primary">🔄 Creando Nuevo Servicio de Retorno</span>
                        ) : (
                            isEditing ? `Editando Servicio #${formData.id}` : 'Nuevo Servicio de Transporte'
                        )}
                    </Modal.Title>
                </Modal.Header>
                
                {(isSettled || trip?.client_invoice_id) && (
                    <div className="bg-light p-2 px-4 border-bottom d-flex align-items-center gap-2 flex-wrap">
                        <Shield size={18} className="text-muted" />
                        <span className="small fw-bold text-muted">Estado Administrativo:</span>
                        
                        {isSettled && (
                            <Badge bg="warning" text="dark" className="p-2">
                                🧾 Liquidación #LIQ-{String(trip?.settlement_id || formData.settlement_id).padStart(5, '0')}
                            </Badge>
                        )}
                        
                        {trip?.client_invoice && (() => {
                            const inv = trip.client_invoice;
                            const isPaid = inv.status === 'paid';
                            const invoiceDocs = inv.documents?.filter(doc => doc.type === 'FACTURA') || [];
                            const folios = invoiceDocs.map(doc => doc.folio).join(', ');
                            
                            return (
                                <>
                                    <Badge bg={isPaid ? "success" : "info"} className="p-2">
                                        {isPaid ? `✅ Prefactura #${inv.internal_number || inv.id} (Pagada)` : `📄 Prefactura #${inv.internal_number || inv.id}`}
                                    </Badge>
                                    {invoiceDocs.length > 0 && (
                                        <Badge bg="secondary" className="p-2">
                                            💰 {invoiceDocs.length === 1 ? 'Factura' : 'Facturas'} #{folios}
                                        </Badge>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}

                <Modal.Body>
                    <Row>
                        <Col md={8} className="border-end pe-4">
                            {/* SECCIÓN 1: CLIENTE Y SOLICITANTE */}
                            <Row className="mb-3 g-3">
                                <Col md={4}>
                                    <Form.Label className="small fw-bold">Estado (Admin)</Form.Label>
                                    <Form.Select 
                                        value={currentStatus || 'pending'} 
                                        onChange={e => {
                                            const newStatus = e.target.value;
                                            if (newStatus === 'cancelled') {
                                                setFormData({
                                                    ...formData,
                                                    status: newStatus,
                                                    estimated_price: 0,
                                                    final_price: 0,
                                                    provider_cost_base: 0,
                                                    provider_cost_local: 0,
                                                    provider_cost_clp: 0,
                                                    driver_earnings: 0,
                                                    waiting_time_amount: 0,
                                                    waiting_time_provider_cost: 0
                                                });
                                            } else {
                                                setFormData({...formData, status: newStatus});
                                            }
                                        }} 
                                        className="fw-bold text-primary border-primary shadow-sm" 
                                        disabled={isSettled || (isEditing && ['completed', 'cancelled', 'no_show', 'cancelled_paid', 'anulado'].includes(currentStatus) && !(isAdmin && currentStatus !== 'anulado'))}
                                    >
                                        {Object.entries(STATUS_OPTIONS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={4}>
                                    <Form.Label className="small fw-bold">Cliente Empresa <span className="text-danger">*</span></Form.Label>
                                    <SearchableSelect 
                                        options={activeClients} 
                                        value={formData.client_id || ''} 
                                        onChange={val => handleClientChange(val)} 
                                        disabled={isSettled || (isEditing && !canEditPrices)} 
                                        placeholder="Seleccione Cliente..."
                                    />
                                    {isParticular && (
                                         <div className="mt-2 d-flex align-items-center gap-2">
                                             <Form.Check 
                                                 type="checkbox"
                                                 id="is-paid-checkbox"
                                                 label="Pagado"
                                                 checked={formData.is_paid || false}
                                                 onChange={e => {
                                                     const checked = e.target.checked;
                                                     setFormData(prev => ({
                                                         ...prev,
                                                         is_paid: checked,
                                                         status: (checked && (prev.status || '').toLowerCase() === 'draft') ? 'pending' : prev.status
                                                     }));
                                                 }}
                                                 disabled={isSettled || (isEditing && !canEditPrices)}
                                             />
                                             {formData.is_paid && (
                                                 <div className="d-flex align-items-center gap-2">
                                                     <Form.Select
                                                         size="sm"
                                                         style={{ width: 'auto' }}
                                                         value={formData.payment_method || ''}
                                                         onChange={e => {
                                                             const val = e.target.value;
                                                             if (['Webpay', 'Getnet', 'Mercado Pago', 'DLocal'].includes(val)) {
                                                                 setFormData({...formData, payment_method: val});
                                                                 setAuthCodeTemp(formData.payment_auth_code || '');
                                                                 setShowAuthCodeModal(true);
                                                             } else {
                                                                 setFormData({...formData, payment_method: val, payment_auth_code: null});
                                                             }
                                                         }}
                                                         disabled={isSettled || (isEditing && !canEditPrices)}
                                                     >
                                                         <option value="">Forma de Pago...</option>
                                                         <option value="Efectivo">Efectivo</option>
                                                         <option value="Transferencia">Transferencia</option>
                                                         <option value="Webpay">Webpay</option>
                                                         <option value="Getnet">Getnet</option>
                                                         <option value="Mercado Pago">Mercado Pago</option>
                                                         <option value="DLocal">DLocal</option>
                                                     </Form.Select>
                                                     {['Webpay', 'Getnet', 'Mercado Pago', 'DLocal'].includes(formData.payment_method) && formData.payment_auth_code && (
                                                         <Badge 
                                                             bg="info" 
                                                             style={{ cursor: (isSettled || (isEditing && !canEditPrices)) ? 'default' : 'pointer', fontSize: '0.75rem', padding: '0.35em 0.65em' }}
                                                             onClick={() => {
                                                                 if (!(isSettled || (isEditing && !canEditPrices))) {
                                                                     setAuthCodeTemp(formData.payment_auth_code || '');
                                                                     setShowAuthCodeModal(true);
                                                                 }
                                                             }}
                                                             title="Haga clic para editar código de autorización"
                                                         >
                                                             Cód: {formData.payment_auth_code}
                                                         </Badge>
                                                     )}
                                                 </div>
                                             )}
                                         </div>
                                     )}
                                </Col>
                                <Col md={4}>
                                    <Form.Label className="small fw-bold">Solicitante <span className="text-danger">*</span></Form.Label>
                                    <div className="d-flex gap-1 shadow-sm rounded">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <SearchableSelect 
                                                options={contextRequesters} 
                                                value={formData.requester_id || ''} 
                                                onChange={val => setFormData({...formData, requester_id: val})} 
                                                disabled={isSettled || !formData.client_id} 
                                                placeholder="Seleccione..."
                                            />
                                        </div>
                                        <Button variant="primary" onClick={() => {
                                            setQuickReq({ id: null, full_name: '', email: '', phone: '', area_id: '', cost_center_id: '', home_address: '', office_address: '', has_portal_access: false, create_as_passenger: false });
                                            setShowQuickReqModal(true);
                                        }} disabled={isSettled || !formData.client_id} style={{borderRadius: 0}} title="Agregar Solicitante"><Plus size={16}/></Button>
                                        <Button 
                                            variant="outline-primary" 
                                            onClick={() => {
                                                if (selectedRequester) {
                                                    setQuickReq({
                                                        id: selectedRequester.id,
                                                        full_name: selectedRequester.full_name || '',
                                                        email: selectedRequester.email || '',
                                                        phone: selectedRequester.phone || '',
                                                        area_id: selectedRequester.area_id || '',
                                                        cost_center_id: selectedRequester.cost_center_id || '',
                                                        home_address: selectedRequester.home_address || '',
                                                        office_address: selectedRequester.office_address || '',
                                                        has_portal_access: selectedRequester.has_portal_access || false,
                                                        create_as_passenger: false
                                                    });
                                                    setShowQuickReqModal(true);
                                                }
                                            }} 
                                            disabled={isSettled || !selectedRequester} 
                                            style={{borderRadius: 0}}
                                            title="Editar Solicitante"
                                        >
                                            <Pencil size={16}/>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {/* SECCIÓN 2: PASAJERO Y ACOMPAÑANTES */}
                            <Row className="mb-3 g-3">
                                <Col md={5}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Form.Label className="small fw-bold mb-0">Pasajero Principal</Form.Label>
                                        <Form.Check type="checkbox" label="Es Solicitante" className="small" checked={isSameAsRequester} disabled={isSettled} onChange={e => {
                                            setIsSameAsRequester(e.target.checked);
                                            if (e.target.checked && formData.requester_id) {
                                                const r = contextRequesters.find(req => req.id === parseInt(formData.requester_id));
                                                if (r) {
                                                    const p = contextPassengers.find(cp => cp.full_name === r.full_name);
                                                    let newAdditionalIds = formData.additional_passenger_ids || [];
                                                    if (p) {
                                                        newAdditionalIds = newAdditionalIds.filter(id => id !== p.id);
                                                    }
                                                    const newCount = recalculatePassengerCount(formData.stops, newAdditionalIds, r.full_name);
                                                    setFormData({
                                                        ...formData,
                                                        passenger_name: r.full_name,
                                                        passenger_phone: r.phone,
                                                        additional_passenger_ids: newAdditionalIds,
                                                        passenger_count: newCount
                                                    });
                                                }
                                            }
                                        }}/>
                                    </div>
                                    <div className="d-flex gap-1 shadow-sm rounded">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <SearchableSelect 
                                                options={contextPassengers} 
                                                value="" 
                                                onChange={val => {
                                                    const p = contextPassengers.find(cp => cp.id === parseInt(val));
                                                    if (p) {
                                                        let newAdditionalIds = formData.additional_passenger_ids || [];
                                                        newAdditionalIds = newAdditionalIds.filter(id => id !== p.id);
                                                        const newCount = recalculatePassengerCount(formData.stops, newAdditionalIds, p.full_name);
                                                        setFormData(prev => ({
                                                            ...prev, 
                                                            passenger_name: p.full_name, 
                                                            passenger_phone: p.phone || '',
                                                            area_id: p.area_id || prev.area_id || '',
                                                            cost_center_id: p.cost_center_id || prev.cost_center_id || '',
                                                            additional_passenger_ids: newAdditionalIds,
                                                            passenger_count: newCount
                                                        }));
                                                    }
                                                }} 
                                                disabled={isSettled || !formData.client_id} 
                                                placeholder="Pax Frecuente..."
                                            />
                                        </div>
                                        <Button variant="primary" onClick={() => { setQuickPax({ id: null, full_name: '', email: '', phone: '', area_id: '', cost_center_id: '', home_address: '', office_address: '', has_portal_access: false }); setShowQuickPaxModal(true); }} disabled={isSettled || !formData.client_id} style={{borderRadius: 0}}><Plus size={16}/></Button>
                                        <Button 
                                            variant="outline-primary" 
                                            onClick={() => {
                                                setQuickPax({
                                                    id: selectedPax.id,
                                                    full_name: selectedPax.full_name || '',
                                                    email: selectedPax.email || '',
                                                    phone: selectedPax.phone || '',
                                                    area_id: selectedPax.area_id || '',
                                                    cost_center_id: selectedPax.cost_center_id || '',
                                                    home_address: selectedPax.home_address || '',
                                                    office_address: selectedPax.office_address || '',
                                                    has_portal_access: selectedPax.has_portal_access || false
                                                });
                                                setShowQuickPaxModal(true);
                                            }} 
                                            disabled={isSettled || !selectedPax} 
                                            style={{borderRadius: 0}}
                                        >
                                            <Pencil size={16}/>
                                        </Button>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <Form.Label className="small fw-bold">Nombre en Pantalla <span className="text-danger">*</span></Form.Label>
                                    <Form.Control 
                                        required 
                                        value={formData.passenger_name || ''} 
                                        disabled={isSettled} 
                                        onChange={e => {
                                            const newName = e.target.value;
                                            const newCount = recalculatePassengerCount(formData.stops, formData.additional_passenger_ids, newName);
                                            setFormData({
                                                ...formData,
                                                passenger_name: newName,
                                                passenger_count: newCount
                                            });
                                        }} 
                                        className="shadow-sm"
                                    />
                                </Col>
                                <Col md={3}><Form.Label className="small fw-bold">Teléfono</Form.Label><Form.Control value={formData.passenger_phone || ''} disabled={isSettled} onChange={e => setFormData({...formData, passenger_phone: e.target.value})} className="shadow-sm"/></Col>
                            </Row>

                            {/* ACOMPAÑANTES ADICIONALES */}
                            <div className="mb-3 p-3 border rounded bg-white shadow-sm">
                                <Form.Label className="small fw-bold text-primary"><Users size={14} className="me-1"/> Acompañantes Adicionales (Opcional)</Form.Label>
                                <InputGroup size="sm" className="mb-2">
                                    <InputGroup.Text className="bg-white"><Search size={14} className="text-muted"/></InputGroup.Text>
                                    <Form.Control placeholder="Buscar acompañante..." value={paxSearchTerm} onChange={e => setPaxSearchTerm(e.target.value)} disabled={isSettled || !formData.client_id}/>
                                </InputGroup>
                                <div className="p-2 bg-light bg-opacity-50 rounded border mb-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                    {contextPassengers
                                        .filter(p => p.full_name !== formData.passenger_name)
                                        .filter(p => {
                                            const isChecked = formData.additional_passenger_ids?.includes(p.id);
                                            const matchesSearch = paxSearchTerm && p.full_name.toLowerCase().includes(paxSearchTerm.toLowerCase());
                                            return isChecked || matchesSearch;
                                        })
                                        .map(p => {
                                            const isChecked = formData.additional_passenger_ids?.includes(p.id);
                                            return (
                                                <div key={p.id} className="d-flex align-items-center mb-1">
                                                    <Form.Check 
                                                        type="checkbox"
                                                        id={`modal-pax-check-${p.id}`}
                                                        label={p.phone ? `${p.full_name} (${p.phone})` : p.full_name}
                                                        checked={isChecked}
                                                        disabled={isSettled}
                                                        onChange={() => {
                                                            if (isSettled) return;
                                                            const newAdditionalIds = isChecked
                                                                ? formData.additional_passenger_ids.filter(id => id !== p.id)
                                                                : [...(formData.additional_passenger_ids || []), p.id];
                                                            const newCount = recalculatePassengerCount(formData.stops, newAdditionalIds);
                                                            setFormData({
                                                                ...formData,
                                                                additional_passenger_ids: newAdditionalIds,
                                                                passenger_count: newCount
                                                            });
                                                        }}
                                                        className="small mb-0"
                                                    />
                                                </div>
                                            );
                                        })
                                    }
                                    {contextPassengers
                                        .filter(p => p.full_name !== formData.passenger_name)
                                        .filter(p => {
                                            const isChecked = formData.additional_passenger_ids?.includes(p.id);
                                            const matchesSearch = paxSearchTerm && p.full_name.toLowerCase().includes(paxSearchTerm.toLowerCase());
                                            return isChecked || matchesSearch;
                                        }).length === 0 && (
                                            <div className="text-muted small text-center py-2">
                                                {paxSearchTerm ? "No se encontraron resultados" : "Busca acompañantes por su nombre para agregarlos."}
                                            </div>
                                        )
                                    }
                                </div>
                                <div className="small text-primary">Total Pasajeros: {recalculatePassengerCount(formData.stops, formData.additional_passenger_ids)}</div>
                            </div>

                            {/* SECCIÓN 3: RUTA (ORIGEN -> PARADAS -> DESTINO) */}
                            <Card className="mb-3 border-0 bg-light bg-opacity-50 p-2">
                                <Row className="mb-2">
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold">
                                            <MapPin size={14} className="me-1 text-success"/>
                                            {isAirportAddress(formData.pickup_address) && <Plane size={14} className="me-1 text-info"/>}
                                            Punto de Recogida (Origen) <span className="text-danger">*</span>
                                        </Form.Label>
                                        <InputGroup className="shadow-sm"><div className="flex-grow-1"><AddressAutocomplete value={formData.pickup_address || ''} disabled={isSettled} onChange={v => { setFormData({...formData, pickup_address: v}); setLoadingRoute(true); }} onSelect={p => {setFormData({...formData, pickup_address: p.address, pickup_lat: p.lat, pickup_lng: p.lng}); setPickupLocation({lat:p.lat, lng:p.lng});}}/></div><Button variant="outline-secondary" onClick={() => applySavedAddress('HOME', true)} disabled={isSettled || (!formData.requester_id && !selectedPax)} title="Casa"><Home size={16}/></Button><Button variant="outline-secondary" onClick={() => applySavedAddress('OFFICE', true)} disabled={isSettled || (!formData.requester_id && !selectedPax)} title="Oficina"><Briefcase size={16}/></Button><Button variant="outline-secondary" onClick={() => { setAirportTargetField('pickup_address'); setShowAirportModal(true); }} disabled={isSettled}><Plane size={16}/></Button></InputGroup>
                                    </Col>
                                </Row>
                                
                                {formData.stops && formData.stops.length > 1 && !isSettled && (
                                    <div className="alert alert-light border py-1 px-2 mb-2 small text-muted d-flex align-items-center gap-1" style={{ fontSize: '11px' }}>
                                        <GripVertical size={14} className="text-info" />
                                        <span><strong>Reordenar paradas:</strong> Arrastra usando el icono <GripVertical size={12}/> o usa las flechas <ArrowUp size={11}/> <ArrowDown size={11}/> para cambiar la secuencia de la ruta.</span>
                                    </div>
                                )}
                                
                                {(formData.stops || []).map((stop, idx) => {
                                    const selectedPaxInStop = contextPassengers.find(cp => {
                                        if (stop.passenger_id && String(cp.id) === String(stop.passenger_id)) return true;
                                        if (stop.passenger_name && cp.full_name && cp.full_name.trim().toLowerCase() === stop.passenger_name.trim().toLowerCase()) return true;
                                        return false;
                                    });

                                    return (
                                        <div 
                                            key={idx} 
                                            draggable={!isSettled}
                                            onDragStart={e => handleStopDragStart(e, idx)}
                                            onDragOver={e => handleStopDragOver(e, idx)}
                                            onDrop={e => handleStopDrop(e, idx)}
                                            onDragEnd={() => setDraggedStopIdx(null)}
                                            className={`mb-3 p-3 border rounded bg-white shadow-sm border-start border-4 border-info transition-all ${
                                                draggedStopIdx === idx ? 'opacity-50 border-primary bg-light' : ''
                                            }`}
                                        >
                                            <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                                                <div className="d-flex align-items-center gap-2">
                                                    {!isSettled && (
                                                        <div 
                                                            className="text-muted d-flex align-items-center p-1 rounded hover-bg-light"
                                                            title="Mantén presionado y arrastra para reordenar"
                                                            style={{ cursor: 'grab' }}
                                                        >
                                                            <GripVertical size={16} className="text-info" />
                                                        </div>
                                                    )}
                                                    <Form.Label className="small fw-bold text-info mb-0 d-flex align-items-center gap-1">
                                                        <MapPin size={14}/> Parada Intermedia {idx+1}
                                                    </Form.Label>

                                                    {formData.stops.length > 1 && !isSettled && (
                                                        <div className="d-flex gap-1 ms-2">
                                                            <Button 
                                                                variant="outline-secondary" 
                                                                size="sm" 
                                                                className="py-0 px-1 border-0" 
                                                                disabled={idx === 0}
                                                                onClick={() => handleMoveStop(idx, 'up')}
                                                                title="Mover Arriba"
                                                            >
                                                                <ArrowUp size={12} />
                                                            </Button>
                                                            <Button 
                                                                variant="outline-secondary" 
                                                                size="sm" 
                                                                className="py-0 px-1 border-0" 
                                                                disabled={idx === formData.stops.length - 1}
                                                                onClick={() => handleMoveStop(idx, 'down')}
                                                                title="Mover Abajo"
                                                            >
                                                                <ArrowDown size={12} />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveStop(idx)} disabled={isSettled} className="py-0 px-2 border-0" title="Eliminar parada">
                                                    <X size={16} />
                                                </Button>
                                            </div>
                                            
                                            {/* DIRECCIÓN DE LA PARADA */}
                                            <div className="mb-2">
                                                <AddressAutocomplete 
                                                    value={stop.address || ''} 
                                                    disabled={isSettled} 
                                                    onChange={v => handleStopAddressChange(idx, v, null, null)} 
                                                    onSelect={p => handleStopAddressChange(idx, p.address, p.lat, p.lng)}
                                                />
                                            </div>

                                            {/* SECCIÓN DEL PASAJERO EN PARADA */}
                                            <div className="p-2 bg-light bg-opacity-70 rounded border mb-1">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <span className="small fw-bold text-secondary d-flex align-items-center gap-1" style={{ fontSize: '11px' }}>
                                                        <User size={12}/> Pasajero en esta Parada <span className="text-muted fw-normal">(Opcional)</span>
                                                    </span>
                                                    {selectedPaxInStop && (selectedPaxInStop.home_address || selectedPaxInStop.office_address) && (
                                                        <div className="d-flex gap-1 align-items-center">
                                                            <span className="text-muted me-1" style={{ fontSize: '10px' }}>📍 Favoritos:</span>
                                                            {selectedPaxInStop.home_address && (
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    className="py-0 px-2 text-nowrap fw-bold"
                                                                    style={{ fontSize: '10px' }}
                                                                    disabled={isSettled}
                                                                    onClick={() => handleSelectStopFavoriteAddress(idx, selectedPaxInStop.home_address)}
                                                                    title={`Usar Casa: ${selectedPaxInStop.home_address}`}
                                                                >
                                                                    🏠 Casa
                                                                </Button>
                                                            )}
                                                            {selectedPaxInStop.office_address && (
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    className="py-0 px-2 text-nowrap fw-bold"
                                                                    style={{ fontSize: '10px' }}
                                                                    disabled={isSettled}
                                                                    onClick={() => handleSelectStopFavoriteAddress(idx, selectedPaxInStop.office_address)}
                                                                    title={`Usar Oficina: ${selectedPaxInStop.office_address}`}
                                                                >
                                                                    🏢 Oficina
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                <Row className="g-2 align-items-center">
                                                    {/* Selector de Pasajero Frecuente */}
                                                    {contextPassengers && contextPassengers.length > 0 && (
                                                        <Col md={12} className="mb-1">
                                                            <SearchableSelect 
                                                                options={contextPassengers} 
                                                                value={selectedPaxInStop ? selectedPaxInStop.id : ""} 
                                                                onChange={val => {
                                                                    const p = contextPassengers.find(cp => cp.id === parseInt(val));
                                                                    const newStops = [...formData.stops];
                                                                    if (p) {
                                                                        newStops[idx] = { 
                                                                            ...newStops[idx], 
                                                                            passenger_id: p.id,
                                                                            passenger_name: p.full_name, 
                                                                            passenger_phone: p.phone || '' 
                                                                        };
                                                                    } else {
                                                                        newStops[idx] = { 
                                                                            ...newStops[idx], 
                                                                            passenger_id: null,
                                                                            passenger_name: '', 
                                                                            passenger_phone: '' 
                                                                        };
                                                                    }
                                                                    const newCount = recalculatePassengerCount(newStops, formData.additional_passenger_ids);
                                                                    setFormData(prev => ({ ...prev, stops: newStops, passenger_count: newCount }));
                                                                }} 
                                                                disabled={isSettled || !formData.client_id} 
                                                                placeholder="👥 Seleccionar Pasajero Frecuente..."
                                                            />
                                                        </Col>
                                                    )}

                                                    {/* Nombre y Teléfono editables */}
                                                    <Col md={7}>
                                                        <Form.Control 
                                                            size="sm" 
                                                            placeholder="Nombre del Pasajero" 
                                                            value={stop.passenger_name || ''} 
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                const newStops = [...formData.stops];
                                                                const matchingPax = contextPassengers.find(cp => cp.full_name && cp.full_name.trim().toLowerCase() === val.trim().toLowerCase());
                                                                newStops[idx] = { 
                                                                    ...newStops[idx], 
                                                                    passenger_name: val,
                                                                    passenger_id: matchingPax ? matchingPax.id : null
                                                                };
                                                                const newCount = recalculatePassengerCount(newStops, formData.additional_passenger_ids);
                                                                setFormData(prev => ({ ...prev, stops: newStops, passenger_count: newCount }));
                                                            }}
                                                            disabled={isSettled}
                                                            className="bg-white"
                                                        />
                                                    </Col>
                                                    <Col md={5}>
                                                        <Form.Control 
                                                            size="sm" 
                                                            placeholder="Teléfono" 
                                                            value={stop.passenger_phone || ''} 
                                                            onChange={e => handleStopFieldChange(idx, 'passenger_phone', e.target.value)}
                                                            disabled={isSettled}
                                                            className="bg-white"
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="mb-2 d-flex justify-content-between align-items-center">
                                    <div>
                                        {formData.stops && formData.stops.length > 1 && (
                                            <Button 
                                                variant="outline-info" 
                                                size="sm" 
                                                className="py-1 px-2 fw-bold d-flex align-items-center gap-1"
                                                onClick={optimizeStopsOrder}
                                                disabled={isSettled}
                                                style={{ fontSize: '11px' }}
                                            >
                                                <MapPin size={12}/> Optimizar Ruta de Paradas
                                            </Button>
                                        )}
                                    </div>
                                    <Button variant="link" size="sm" className="text-decoration-none p-0" disabled={isSettled} onClick={handleAddStop}>+ Añadir parada intermedia</Button>
                                </div>

                                {suggestedStopPrice > 0 && (
                                    <div className="alert alert-info py-1 px-2 mb-2 d-flex align-items-center justify-content-between" style={{fontSize: '11px'}}>
                                        <div>
                                            <Info size={14} className="me-1"/>
                                            <strong>Sugerencia de cobro:</strong> Se detectó un desvío. Se sugiere cobrar <strong>${suggestedStopPrice.toLocaleString()}</strong> por la parada intermedia.
                                        </div>
                                        {!formData.extra_services?.find(s => s.id === 'parada_intermedia') && (
                                            <Button variant="link" size="sm" className="p-0 ms-2 text-decoration-none fw-bold" style={{fontSize: '11px'}} onClick={() => {
                                                let newList = [...(formData.extra_services || [])];
                                                newList.push({ id: 'parada_intermedia', name: '📍 Parada Intermedia', price: suggestedStopPrice });
                                                setFormData(prev => ({...prev, extra_services: newList}));
                                                setShowExtraServices(true);
                                            }}>
                                                [Cobrar ahora]
                                            </Button>
                                        )}
                                    </div>
                                )}

                                <Row className="mb-2">
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold">
                                            <MapPin size={14} className="me-1 text-danger"/>
                                            {isAirportAddress(formData.dropoff_address) && <Plane size={14} className="me-1 text-info"/>}
                                            Punto de Destino <span className="text-danger">*</span> Final
                                        </Form.Label>
                                        <InputGroup className="shadow-sm"><div className="flex-grow-1"><AddressAutocomplete value={formData.dropoff_address || ''} disabled={isSettled} onChange={v => { setFormData({...formData, dropoff_address: v}); setLoadingRoute(true); }} onSelect={p => {setFormData({...formData, dropoff_address: p.address, dropoff_lat: p.lat, dropoff_lng: p.lng}); setDropoffLocation({lat:p.lat, lng:p.lng});}}/></div><Button variant="outline-secondary" onClick={() => applySavedAddress('HOME', false)} disabled={isSettled || (!formData.requester_id && !selectedPax)} title="Casa"><Home size={16}/></Button><Button variant="outline-secondary" onClick={() => applySavedAddress('OFFICE', false)} disabled={isSettled || (!formData.requester_id && !selectedPax)} title="Oficina"><Briefcase size={16}/></Button><Button variant="outline-secondary" onClick={() => { setAirportTargetField('dropoff_address'); setShowAirportModal(true); }} disabled={isSettled}><Plane size={16}/></Button></InputGroup>
                                        {isAirportAddress(formData.dropoff_address) && (
                                            <Alert variant="warning" className="py-2 px-3 small mt-2 mb-0 shadow-sm">
                                                ℹ️ Recuerda que para vuelos nacionales, debes estar con 2 horas de antelación, para vuelos internacionales, 3 horas.
                                            </Alert>
                                        )}
                                    </Col>
                                </Row>

                                <Row className="mb-2 align-items-center mt-3 pt-3 border-top">
                                    <Col md={12}>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <Form.Label className="small fw-bold text-dark mb-0">
                                                🏢 Base de Operaciones
                                            </Form.Label>
                                            <div style={{ width: '250px' }}>
                                                <Form.Select
                                                    size="sm"
                                                    value={formData.origin_base_id || ''}
                                                    onChange={e => {
                                                        const val = e.target.value ? parseInt(e.target.value) : null;
                                                        const selectedBase = bases.find(b => b.id === val);
                                                        const newCurrency = selectedBase?.currency_code || 'CLP';
                                                        setFormData(prev => ({ 
                                                            ...prev, 
                                                            origin_base_id: val,
                                                            local_currency_code: newCurrency
                                                        }));
                                                    }}
                                                    disabled={isSettled}
                                                    className="fw-bold text-primary border-primary shadow-sm"
                                                >
                                                    <option value="">-- Autodetectar Base --</option>
                                                    {bases.map(b => (
                                                        <option key={b.id} value={b.id}>
                                                            {b.name} ({b.currency_code})
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>

                            {/* SECCIÓN 4: TIEMPO Y VEHÍCULO */}
                            <Row className="mb-3 g-3">
                                <Col xl={4} lg={5} md={12}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Form.Label className="small fw-bold text-danger mb-0"><Clock size={14} className="me-1"/> Fecha y Hora <span className="text-danger">*</span></Form.Label>
                                        <Button 
                                            variant={formData.is_asap ? "danger" : "outline-danger"} 
                                            size="sm" 
                                            className="py-0 px-2 fw-bold" 
                                            style={{fontSize:'10px'}}
                                            disabled={isSettled}
                                            onClick={() => setFormData({...formData, is_asap: !formData.is_asap})}
                                        >
                                            {formData.is_asap ? "⚡ ASAP ACTIVO" : "⚡ MARCAR ASAP"}
                                        </Button>
                                    </div>
                                    <div className="d-flex align-items-center gap-1 flex-wrap flex-sm-nowrap">
                                        <Form.Control 
                                            type="date" 
                                            required 
                                            size="sm"
                                            value={formData.scheduled_time ? formData.scheduled_time.split('T')[0] : ''} 
                                            disabled={isSettled}
                                            onChange={e => {
                                                const newDate = e.target.value;
                                                const timePart = formData.scheduled_time ? (formData.scheduled_time.split('T')[1] || '08:00') : '08:00';
                                                const newTime = `${newDate}T${timePart}`;
                                                const isAsapAuto = newTime && (new Date(newTime) - new Date()) < (60 * 60 * 1000);
                                                setFormData({...formData, scheduled_time: newTime, is_asap: isAsapAuto || formData.is_asap});
                                            }} 
                                            className={`shadow-sm fw-bold flex-grow-1 ${formData.is_asap ? 'border-danger bg-danger bg-opacity-10' : 'border-danger'}`}
                                            style={{ fontSize: '0.8rem', minWidth: '110px', height: '31px', padding: '0.2rem 0.4rem' }}
                                        />
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <Form.Select 
                                                value={formData.scheduled_time ? (formData.scheduled_time.split('T')[1]?.split(':')[0] || '08') : '08'}
                                                disabled={isSettled}
                                                size="sm"
                                                onChange={e => {
                                                    const hr = e.target.value;
                                                    const datePart = formData.scheduled_time ? (formData.scheduled_time.split('T')[0] || new Date().toISOString().split('T')[0]) : new Date().toISOString().split('T')[0];
                                                    const minutePart = formData.scheduled_time ? (formData.scheduled_time.split('T')[1]?.split(':')[1] || '00') : '00';
                                                    const newTime = `${datePart}T${hr}:${minutePart}`;
                                                    const isAsapAuto = newTime && (new Date(newTime) - new Date()) < (60 * 60 * 1000);
                                                    setFormData({...formData, scheduled_time: newTime, is_asap: isAsapAuto || formData.is_asap});
                                                }}
                                                className="shadow-sm fw-bold px-1 text-center"
                                                style={{ fontSize: '0.8rem', width: '48px', height: '31px', padding: '0.2rem 0.3rem', backgroundImage: 'none' }}
                                            >
                                                {Array.from({ length: 24 }).map((_, i) => {
                                                    const val = String(i).padStart(2, '0');
                                                    return <option key={val} value={val}>{val}</option>;
                                                })}
                                            </Form.Select>
                                            <span className="fw-bold text-muted" style={{ padding: '0 2px' }}>:</span>
                                            <Form.Select 
                                                value={formData.scheduled_time ? (formData.scheduled_time.split('T')[1]?.split(':')[1] || '00') : '00'}
                                                disabled={isSettled}
                                                size="sm"
                                                onChange={e => {
                                                    const min = e.target.value;
                                                    const datePart = formData.scheduled_time ? (formData.scheduled_time.split('T')[0] || new Date().toISOString().split('T')[0]) : new Date().toISOString().split('T')[0];
                                                    const hourPart = formData.scheduled_time ? (formData.scheduled_time.split('T')[1]?.split(':')[0] || '08') : '08';
                                                    const newTime = `${datePart}T${hourPart}:${min}`;
                                                    const isAsapAuto = newTime && (new Date(newTime) - new Date()) < (60 * 60 * 1000);
                                                    setFormData({...formData, scheduled_time: newTime, is_asap: isAsapAuto || formData.is_asap});
                                                }}
                                                className="shadow-sm fw-bold px-1 text-center"
                                                style={{ fontSize: '0.8rem', width: '48px', height: '31px', padding: '0.2rem 0.3rem', backgroundImage: 'none' }}
                                            >
                                                {Array.from({ length: 60 }).map((_, i) => {
                                                    const val = String(i).padStart(2, '0');
                                                    return <option key={val} value={val}>{val}</option>;
                                                })}
                                            </Form.Select>
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <Form.Check 
                                            type="checkbox"
                                            id="is-disposal-checkbox"
                                            label={<span className="fw-bold text-warning-emphasis">⏱️ Servicio a Disposición</span>}
                                            checked={formData.is_disposal || false}
                                            disabled={isSettled}
                                            onChange={e => setFormData(prev => ({...prev, is_disposal: e.target.checked}))}
                                        />
                                    </div>
                                </Col>
                                
                                {!isEditing && (
                                    <Col md={12} className="my-2">
                                        <Form.Check 
                                            type="checkbox"
                                            id="repeat-trip-checkbox"
                                            label={<span className="fw-bold text-primary">🔁 Programación Recurrente (Crear múltiples servicios)</span>}
                                            checked={isRepeatChecked}
                                            onChange={e => setIsRepeatChecked(e.target.checked)}
                                            disabled={isSettled}
                                        />
                                        {isRepeatChecked && (
                                            <Card className="border-primary border-opacity-25 bg-light p-3 mt-2 rounded shadow-sm">
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <span className="fw-bold text-primary" style={{ fontSize: '13px' }}>Configuración de Recurrencia</span>
                                                    <div className="btn-group btn-group-sm" role="group">
                                                        <button 
                                                            type="button" 
                                                            className={`btn py-1 px-3 ${repeatMode === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`} 
                                                            onClick={() => setRepeatMode('weekly')}
                                                        >
                                                            Patrón Semanal
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            className={`btn py-1 px-3 ${repeatMode === 'manual' ? 'btn-primary' : 'btn-outline-primary'}`} 
                                                            onClick={() => setRepeatMode('manual')}
                                                        >
                                                            Fechas Manuales
                                                        </button>
                                                    </div>
                                                </div>

                                                {repeatMode === 'weekly' ? (
                                                    <div>
                                                        <Row className="g-2 mb-3">
                                                            <Col md={6}>
                                                                <Form.Label className="small fw-bold text-muted">Fecha Inicio</Form.Label>
                                                                <Form.Control 
                                                                    type="date" 
                                                                    size="sm" 
                                                                    value={repeatStartDate}
                                                                    onChange={e => setRepeatStartDate(e.target.value)}
                                                                    required={isRepeatChecked && repeatMode === 'weekly'}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Form.Label className="small fw-bold text-muted">Fecha Fin</Form.Label>
                                                                <Form.Control 
                                                                    type="date" 
                                                                    size="sm" 
                                                                    value={repeatEndDate}
                                                                    onChange={e => setRepeatEndDate(e.target.value)}
                                                                    required={isRepeatChecked && repeatMode === 'weekly'}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Form.Label className="small fw-bold text-muted d-block mb-2">Días a repetir (se usará la hora del servicio principal)</Form.Label>
                                                        <div className="d-flex flex-wrap gap-2 justify-content-between bg-white p-2 rounded border">
                                                            {[
                                                                { label: 'Lu', val: 1 },
                                                                { label: 'Ma', val: 2 },
                                                                { label: 'Mi', val: 3 },
                                                                { label: 'Ju', val: 4 },
                                                                { label: 'Vi', val: 5 },
                                                                { label: 'Sá', val: 6 },
                                                                { label: 'Do', val: 0 }
                                                            ].map(day => (
                                                                <Form.Check 
                                                                    key={day.val}
                                                                    type="checkbox"
                                                                    id={`day-${day.val}`}
                                                                    label={day.label}
                                                                    checked={repeatDays[day.val]}
                                                                    onChange={e => setRepeatDays({ ...repeatDays, [day.val]: e.target.checked })}
                                                                    inline
                                                                    className="fw-bold"
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <Form.Label className="small fw-bold text-muted d-block mb-2">Agregar fechas y horas específicas</Form.Label>
                                                        <div className="d-flex flex-column gap-2 mb-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                                            {manualDates.map((date, idx) => (
                                                                <div key={idx} className="d-flex gap-2 align-items-center">
                                                                    <Form.Control 
                                                                        type="datetime-local" 
                                                                        size="sm" 
                                                                        value={date}
                                                                        onChange={e => {
                                                                            const newDates = [...manualDates];
                                                                            newDates[idx] = e.target.value;
                                                                            setManualDates(newDates);
                                                                        }}
                                                                        required={isRepeatChecked && repeatMode === 'manual'}
                                                                        className="flex-grow-1"
                                                                    />
                                                                    {manualDates.length > 1 && (
                                                                        <Button 
                                                                            variant="outline-danger" 
                                                                            size="sm" 
                                                                            onClick={() => {
                                                                                const newDates = manualDates.filter((_, i) => i !== idx);
                                                                                setManualDates(newDates);
                                                                            }}
                                                                        >
                                                                            <X size={14} />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            size="sm" 
                                                            className="w-100 fw-bold"
                                                            onClick={() => setManualDates([...manualDates, ''])}
                                                        >
                                                            <Plus size={14} className="me-1" /> Añadir Fecha y Hora
                                                        </Button>
                                                    </div>
                                                )}
                                            </Card>
                                        )}
                                    </Col>
                                )}

                                <Col xl={8} lg={7} md={12}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Form.Label className="small fw-bold mb-0">Vehículo y Precio <span className="text-danger">*</span> {(loadingPricing || loadingRoute) && <span className="text-primary ms-2 italic small">Calculando...</span>}</Form.Label>
                                        <div style={{ width: '190px' }}>
                                            <Form.Select 
                                                size="sm"
                                                value={formData.requested_vehicle_type_id || ''}
                                                disabled={isSettled}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        requested_vehicle_type_id: val ? parseInt(val) : ''
                                                    }));
                                                }}
                                                className="py-0 px-2 small border-primary text-primary"
                                                style={{ fontSize: '11px', height: '26px' }}
                                            >
                                                <option value="">Selección manual de tipo...</option>
                                                {vehicleTypes?.map(vt => (
                                                     <option key={vt.id} value={vt.id}>{vt.name}</option>
                                                ))}
                                            </Form.Select>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2 overflow-auto pb-2" style={{scrollbarWidth:'thin'}}>
                                        {filteredQuotes.length > 0 ? filteredQuotes.map(q => {
                                            const isSelected = parseInt(formData.requested_vehicle_type_id) === q.vehicle_type_id &&
                                                (formData.provider_id ? parseInt(formData.provider_id) === q.provider_id : !q.provider_id);
                                            
                                            // Calcular margen estimado
                                            const venta = Math.round(q.final_price || 0);
                                            const costoCLP = Math.round(q.cost_clp || 0);
                                            const margenCLP = venta - costoCLP;

                                            return (
                                                <Card 
                                                    key={`${q.vehicle_type_id}-${q.provider_id || 'general'}`}
                                                    className={`flex-shrink-0 cursor-pointer shadow-sm border-2 ${isSelected ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`}
                                                    style={{width: '160px', opacity: isSettled ? 0.7 : 1}}
                                                    onClick={() => !isSettled && handleVehicleSelect(q)}
                                                >
                                                    <Card.Body className="p-2 text-center d-flex flex-column justify-content-between" style={{ minHeight: '135px' }}>
                                                        <div>
                                                            <Car size={24} className={isSelected ? 'text-primary' : 'text-muted'} />
                                                            <div className="fw-bold small mt-1 text-truncate" title={q.provider_name ? `${q.vehicle_name} (${q.provider_name})` : q.vehicle_name}>
                                                                {q.vehicle_name}
                                                                {q.provider_name && <span className="d-block text-primary small text-truncate" style={{fontSize: '9px', fontWeight: 'normal'}}>({q.provider_name})</span>}
                                                            </div>
                                                            <div className="text-muted mb-1" style={{fontSize:'10px'}}>
                                                                 👤 {(() => {
                                                                     if (q.max_pax !== undefined && q.max_pax !== null) return q.max_pax;
                                                                     const vt = vehicleTypes.find(v => v.id === q.vehicle_type_id);
                                                                     if (!vt) return '-';
                                                                     const name = (vt.name || '').toUpperCase();
                                                                     if (name === "STANDARD" || name === "EXECUTIVE") return 2;
                                                                     if (name.includes("PEOPLE_CARRIER") || name.includes("PEOPLE CARRIER")) return 4;
                                                                     return vt.capacity || '-';
                                                                 })()} | 🧳 {(() => {
                                                                     if (q.max_suitcases !== undefined && q.max_suitcases !== null) return q.max_suitcases;
                                                                     const vt = vehicleTypes.find(v => v.id === q.vehicle_type_id);
                                                                     const name = (vt?.name || q.vehicle_name || '').toUpperCase();
                                                                     if (name === "STANDARD" || name === "EXECUTIVE") return 2;
                                                                     if (name.includes("PEOPLE_CARRIER") || name.includes("PEOPLE CARRIER")) return 4;
                                                                     return 4;
                                                                 })()}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold text-dark" style={{ fontSize: '11px' }}>Venta: ${venta.toLocaleString()}</div>
                                                            {q.cost_local > 0 && (
                                                                <div className="text-muted" style={{fontSize: '9px', lineHeight: '1.1'}}>
                                                                    Costo: ${Math.round(q.cost_local).toLocaleString()} {q.local_currency_code}
                                                                    {q.local_currency_code !== 'CLP' && <span className="d-block">(${costoCLP.toLocaleString()} CLP)</span>}
                                                                    <span className="d-block text-success fw-bold mt-1">
                                                                        Margen: ${margenCLP.toLocaleString()} CLP
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        }) : (
                                            <div className="p-3 text-muted small border rounded w-100 bg-light">
                                                {availableQuotes.length > 0 ? `No hay vehículos disponibles para la capacidad solicitada (${formData.passenger_count || 1} pax, ${formData.suitcases || 0} maletas)` : "Ingrese origen/destino para ver precios..."}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col md={2}><Form.Label className="small fw-bold"><Plane size={14} className="me-1"/> N° Vuelo</Form.Label><Form.Control placeholder="" value={formData.flight_number || ''} disabled={isSettled} onChange={e => setFormData({...formData, flight_number: e.target.value})} className="shadow-sm"/></Col>
                                <Col md={3}><Form.Label className="small fw-bold">Motivo del Viaje</Form.Label><Form.Control placeholder="" value={formData.trip_reason || ''} disabled={isSettled} onChange={e => setFormData({...formData, trip_reason: e.target.value})} className="shadow-sm"/></Col>
                                <Col md={3}><Form.Label className="small fw-bold text-info">📌 Solicitud AQUIVOY</Form.Label><Form.Control placeholder="" value={formData.aquivoy_id || ''} disabled={isSettled} onChange={e => setFormData({...formData, aquivoy_id: e.target.value})} className="shadow-sm border-info"/></Col>
                                <Col md={2}><Form.Label className="small fw-bold">Pax Total</Form.Label><Form.Control type="number" min="1" value={formData.passenger_count || 1} disabled={isSettled} onChange={e => setFormData({...formData, passenger_count: e.target.value})} className="shadow-sm"/></Col>
                                <Col md={2}><Form.Label className="small fw-bold">Maletas</Form.Label><Form.Control type="number" min="0" value={formData.suitcases || 0} disabled={isSettled} onChange={e => setFormData({...formData, suitcases: e.target.value})} className="shadow-sm"/></Col>
                            </Row>

                            {/* SECCIÓN 5: CLASIFICACIÓN CORPORATIVA */}
                            <Row className="mb-3 g-3">
                                <Col md={6}>
                                    <Form.Label className="small fw-bold">Área Corporativa</Form.Label>
                                    <div className="d-flex gap-1 shadow-sm rounded">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <SearchableSelect 
                                                options={contextAreas
                                                    .filter(a => a.is_active !== false || String(a.id) === String(formData.area_id))
                                                    .map(a => ({ id: a.id, name: a.name + (a.is_active === false ? " (Inactivo)" : "") }))
                                                } 
                                                value={formData.area_id || ''} 
                                                onChange={val => setFormData({...formData, area_id: val})} 
                                                disabled={isSettled || !formData.client_id} 
                                                placeholder="Seleccione..."
                                            />
                                        </div>
                                        <Button variant="outline-secondary" onClick={() => handleAddAreaPrompt('trip')} disabled={isSettled || !formData.client_id} style={{borderRadius: 0}}><Plus size={14}/></Button>
                                    </div>
                                    {formData.area_label && !formData.area_id && (
                                        <div className="text-muted small mt-1 ps-1">
                                            ⚠️ Área Importada (Texto): <strong>{formData.area_label}</strong>
                                        </div>
                                    )}
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="small fw-bold">Centro de Costo</Form.Label>
                                    <div className="d-flex gap-1 shadow-sm rounded">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <SearchableSelect 
                                                options={contextCostCenters
                                                    .filter(cc => cc.is_active !== false || String(cc.id) === String(formData.cost_center_id))
                                                    .map(cc => ({ id: cc.id, name: `${cc.code}${cc.name ? ` - ${cc.name}` : ''}` + (cc.is_active === false ? " (Inactivo)" : "") }))
                                                } 
                                                value={formData.cost_center_id || ''} 
                                                onChange={val => {
                                                    const selectedCC = contextCostCenters.find(cc => String(cc.id) === String(val));
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        cost_center_id: val,
                                                        file: (selectedCC && selectedCC.file) ? selectedCC.file : prev.file
                                                    }));
                                                }} 
                                                disabled={isSettled || !formData.client_id} 
                                                placeholder="Seleccione..."
                                            />
                                        </div>
                                        <Button variant="outline-secondary" onClick={() => handleAddCCPrompt('trip')} disabled={isSettled || !formData.client_id} style={{borderRadius: 0}}><Plus size={14}/></Button>
                                    </div>
                                    {formData.cost_center_label && !formData.cost_center_id && (
                                        <div className="text-muted small mt-1 ps-1">
                                            ⚠️ CC Importado (Texto): <strong>{formData.cost_center_label}</strong>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                            <Row className="mb-3 g-3">
                                <Col md={6}>
                                    <Form.Label className="small fw-bold text-primary">Orden de Servicio / OS</Form.Label>
                                    <Form.Control placeholder="" value={formData.external_reference || ''} disabled={isSettled} onChange={e => setFormData({...formData, external_reference: e.target.value})} onBlur={e => setFormData(prev => ({...prev, external_reference: prev.external_reference ? prev.external_reference.trim() : ''}))} className="shadow-sm border-primary"/>
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="small fw-bold text-success">File</Form.Label>
                                    <Form.Control placeholder="N° de File" value={formData.file || ''} disabled={isSettled} onChange={e => setFormData({...formData, file: e.target.value})} onBlur={e => setFormData(prev => ({...prev, file: prev.file ? prev.file.trim() : ''}))} className="shadow-sm border-success"/>
                                </Col>
                            </Row>

                            {/* SECCIÓN 6: OBSERVACIONES */}
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Label className="small fw-bold">Observaciones Generales</Form.Label>
                                    <Form.Control as="textarea" rows={2} placeholder="Escribir aquí..." value={formData.notes || ''} disabled={isSettled} onChange={e => setFormData({...formData, notes: e.target.value})} className="shadow-sm"/>
                                </Col>
                            </Row>
                            {canSeeInternalNotes && (
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold text-danger">Observaciones Internas (Solo Locutoras/Admin)</Form.Label>
                                        <Form.Control 
                                            as="textarea" 
                                            rows={2} 
                                            placeholder="Observación oculta para el chofer..." 
                                            value={formData.internal_notes || ''} 
                                            disabled={isSettled} 
                                            onChange={e => setFormData({...formData, internal_notes: e.target.value})} 
                                            className="shadow-sm border-danger"
                                        />
                                    </Col>
                                </Row>
                            )}
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Label className="small fw-bold">Observaciones del Conductor</Form.Label>
                                    <Form.Control as="textarea" rows={2} readOnly value={formData.driver_notes || ''} className="shadow-sm bg-light"/>
                                </Col>
                            </Row>

                            {/* ALERTA DE INCIDENCIA DE RESPUESTA DE PASAJERO */}
                            {formData.passenger_issue && (
                                <Alert variant="danger" className="d-flex justify-content-between align-items-center mb-3 shadow-sm py-2 px-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <AlertTriangle size={18} className="text-danger flex-shrink-0" />
                                        <div>
                                            <strong className="d-block small" style={{ fontSize: '0.85rem' }}>¡Alerta de Respuesta de Pasajero!</strong>
                                            <span className="small text-muted" style={{ fontSize: '0.75rem' }}>El pasajero ha reportado un inconveniente o respondido con una alerta.</span>
                                        </div>
                                    </div>
                                    <Button 
                                        variant="danger" 
                                        size="sm"
                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                        onClick={handleResolvePassengerAlert}
                                    >
                                        Resolver Alerta
                                    </Button>
                                </Alert>
                            )}

                            {/* BITÁCORA DE COMUNICACIÓN WHATSAPP */}
                            <Card className="mb-3 border-success bg-success bg-opacity-10 p-3 rounded-3 shadow-sm">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="small fw-bold text-success-emphasis d-flex align-items-center mb-0">
                                        <MessageCircle size={16} className="me-2"/> BITÁCORA DE WHATSAPP (PASAJERO)
                                    </Form.Label>
                                    {loadingWhatsappLogs && <span className="spinner-border spinner-border-sm text-success" role="status"></span>}
                                </div>
                                
                                {whatsappLogs.length === 0 ? (
                                    <div className="text-muted small italic p-2 border rounded bg-white bg-opacity-50">
                                        No hay registros de envío o respuestas para este pasajero.
                                    </div>
                                ) : (
                                    <div className="border rounded bg-white bg-opacity-75 p-2 d-flex flex-column" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {whatsappLogs.map((msg, index) => {
                                            const isOutbound = msg.direction === 'outbound';
                                            const bubbleClass = isOutbound 
                                                ? "bg-success text-white align-self-end ms-auto text-end" 
                                                : "bg-light text-dark align-self-start me-auto text-start";
                                            const timeStr = msg.created_at ? new Date(msg.created_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : '';
                                            const dateStr = msg.created_at ? new Date(msg.created_at).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' }) : '';
                                            const rawContent = msg.content || '';
                                            const mediaMatch = rawContent.match(/\[MEDIA_ID:([^\]]+)\]/);
                                            const mediaId = mediaMatch ? mediaMatch[1] : null;
                                            const mapUrlMatch = rawContent.match(/\[MAP_URL:([^\r\n]+)\]/);
                                            const mapUrl = mapUrlMatch ? mapUrlMatch[1].replace(/\[/g, '%5B').replace(/\]/g, '%5D') : null;
                                            const baseUrl = apiClient.defaults.baseURL || '';
                                            const mediaUrl = mediaId ? `${baseUrl}/transport/whatsapp/media/${mediaId}` : mapUrl;
                                            const cleanContent = rawContent
                                                .replace(/\[MEDIA_ID:[^\]]+\]\s*/g, '')
                                                .replace(/\[MAP_URL:[^\r\n]+\]\s*/g, '')
                                                .replace(/\[Imagen Mapa de Ruta\]\s*/g, '')
                                                .replace(/\[AI_OCR\][\s\S]*?\[\/AI_OCR\]\s*/g, '')
                                                .trim();
                                            return (
                                                <div key={index} className={`d-flex flex-column mb-2 p-2 rounded-3 shadow-sm ${bubbleClass}`} style={{ maxWidth: '85%', fontSize: '0.8rem', width: 'fit-content' }}>
                                                    <div className="fw-semibold small text-opacity-75" style={{ fontSize: '0.7rem' }}>
                                                        {isOutbound ? 'EmiGo' : (msg.passenger_name || 'Pasajero')} • {dateStr} {timeStr}
                                                    </div>
                                                    {mediaUrl && (
                                                        <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="my-1 d-block">
                                                            <img
                                                                src={mediaUrl}
                                                                alt="Imagen WhatsApp"
                                                                loading="lazy"
                                                                style={{ maxWidth: '200px', maxHeight: '220px', objectFit: 'cover', borderRadius: '6px', display: 'block' }}
                                                            />
                                                        </a>
                                                    )}
                                                    {cleanContent && (
                                                        <div className="text-start" style={{ whiteSpace: 'pre-line' }}>{cleanContent}</div>
                                                    )}
                                                    {msg.status && isOutbound && (
                                                        <div className="text-end small mt-1 text-opacity-50" style={{ fontSize: '0.65rem' }}>
                                                            {msg.status === 'read' ? '✔️ Leído' : msg.status === 'delivered' ? '✔️ Entregado' : msg.status === 'failed' ? `❌ Falló: ${msg.error_message || ''}` : '✔️ Enviado'}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Card>

                            {/* SECCIÓN 7: TIEMPOS REALES */}
                            {isAdmin && formData.scheduled_time && (new Date(formData.scheduled_time) < new Date()) && (
                                <Card className="mb-3 border-warning bg-warning bg-opacity-10 p-3 rounded-3 shadow-sm">
                                    <Form.Label className="small fw-bold text-warning-emphasis d-flex align-items-center">
                                        <Clock size={16} className="me-2"/> TIEMPOS REALES DE EJECUCIÓN (SERVICIO PASADO)
                                    </Form.Label>
                                    <Row className="g-3 mt-1">
                                        <Col md={6}>
                                            <Form.Label className="small text-muted">Inicio Real (Pickup)</Form.Label>
                                            <Form.Control 
                                                type="datetime-local" 
                                                value={formData.start_time || ''} 
                                                disabled={isSettled}
                                                onChange={e => setFormData({...formData, start_time: e.target.value})} 
                                                className="shadow-sm border-warning"
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label className="small text-muted">Término Real (Dropoff)</Form.Label>
                                            <Form.Control 
                                                type="datetime-local" 
                                                value={formData.end_time || ''} 
                                                disabled={isSettled}
                                                onChange={e => setFormData({...formData, end_time: e.target.value})} 
                                                className="shadow-sm border-warning"
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            )}

                            {/* SECCIÓN 8: TIEMPO DE ESPERA (NUEVO) */}
                            {(isAdmin || isLocutor) && (formData.status === 'on_board' || formData.status === 'completed' || formData.status === 'arrived') && (
                                <Card className="mb-3 border-primary bg-primary bg-opacity-10 p-3 rounded-3 shadow-sm">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="small fw-bold text-primary d-flex align-items-center mb-0">
                                            <Clock size={16} className="me-2"/> GESTIÓN DE TIEMPO DE ESPERA
                                        </Form.Label>
                                        <Form.Check 
                                            type="switch"
                                            label={<small className="fw-bold">Cobro Manual / Fix</small>}
                                            checked={formData.waiting_time_manual_fix || false}
                                            onChange={e => setFormData({...formData, waiting_time_manual_fix: e.target.checked})}
                                        />
                                    </div>
                                    <Row className="g-2">
                                        <Col md={3}>
                                            <Form.Label className="small text-muted mb-1">
                                                Minutos de Espera {formData.waiting_time_minutes > 0 && `(${formatWaitingMinutes(formData.waiting_time_minutes)})`}
                                            </Form.Label>
                                            <Form.Control 
                                                type="number" size="sm"
                                                value={formData.waiting_time_minutes || 0}
                                                onChange={e => setFormData({
                                                    ...formData, 
                                                    waiting_time_minutes: parseInt(e.target.value || 0),
                                                    waiting_time_manual_fix: true 
                                                })}
                                                disabled={isSettled}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Label className="small text-muted mb-1">Monto Venta Extra ($)</Form.Label>
                                            <Form.Control 
                                                type="number" size="sm"
                                                value={formData.waiting_time_amount !== undefined && formData.waiting_time_amount !== null ? formData.waiting_time_amount : ''}
                                                onChange={e => setFormData({
                                                    ...formData, 
                                                    waiting_time_amount: e.target.value,
                                                    waiting_time_manual_fix: true
                                                })}
                                                disabled={isSettled}
                                                className="fw-bold text-success"
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Label className="small text-muted mb-1">Extra Pago Conductor ($)</Form.Label>
                                            <Form.Control 
                                                type="number" size="sm"
                                                value={formData.waiting_time_provider_cost !== undefined && formData.waiting_time_provider_cost !== null ? formData.waiting_time_provider_cost : ''}
                                                onChange={e => setFormData({
                                                    ...formData, 
                                                    waiting_time_provider_cost: e.target.value,
                                                    waiting_time_manual_fix: true
                                                })}
                                                disabled={isSettled}
                                                className="fw-bold text-primary"
                                            />
                                        </Col>
                                    </Row>
                                    {!formData.waiting_time_manual_fix && (
                                        <div className="mt-2" style={{fontSize: '10px'}}>
                                            <span className="text-muted italic">ℹ️ El sistema calcula la espera automáticamente al marcar "Pasajero a Bordo".</span>
                                        </div>
                                    )}
                                </Card>
                            )}

                            {/* SECCIÓN OPERATIVA Y RESUMEN FINANCIERO */}
                            <Card className="mb-3 bg-dark text-white border-0 shadow-sm overflow-hidden">
                                <div className="bg-primary bg-opacity-25 p-2 text-center small fw-bold tracking-widest border-bottom border-secondary">RESUMEN FINANCIERO</div>
                                <Card.Body className="p-3">
                                    <Row className="g-3 text-center align-items-center">
                                        <Col md={2}>
                                            <div className="small opacity-50 text-uppercase" style={{fontSize:'9px'}}>Venta Total</div>
                                            <div className="fs-6 fw-bold text-info">${Math.round(marginAnalysis.ventaTotal || 0).toLocaleString()}</div>
                                        </Col>
                                        <Col md={2} className="border-start border-secondary">
                                            <div className="small opacity-50 text-uppercase" style={{fontSize:'9px'}}>Costo Base</div>
                                            <div className="fs-6 fw-bold text-warning">${Math.round((parseFloat(formData.provider_cost_base) || 0) + (marginAnalysis.totalExtras || 0) + (marginAnalysis.waitCosto || 0)).toLocaleString()}</div>
                                        </Col>
                                        <Col md={3} className="border-start border-secondary">
                                            <div className="small opacity-50 text-uppercase" style={{fontSize:'9px'}}>Pago Total Conductor</div>
                                            <div className="fs-6 fw-bold text-white">
                                                {(() => {
                                                     const displayCurrency = formData.provider_currency_code || formData.local_currency_code || 'CLP';
                                                     const totalEarnings = displayCurrency === 'CLP' 
                                                         ? (marginAnalysis.driverPayment || 0)
                                                         : (parseFloat(formData.provider_cost_local) || 0);
                                                     return displayCurrency === 'CLP' 
                                                         ? `$${Math.round(totalEarnings).toLocaleString()} CLP` 
                                                         : `${parseFloat(totalEarnings).toFixed(2)} ${displayCurrency}`;
                                                 })()}
                                            </div>
                                        </Col>
                                        <Col md={3} className="border-start border-secondary">
                                            <div className="small opacity-50 text-uppercase" style={{fontSize:'9px'}}>Utilidad Bruta</div>
                                            <div className="fs-6 fw-bold text-success">${marginAnalysis.profit.toLocaleString()}</div>
                                        </Col>
                                        <Col md={2} className="border-start border-secondary">
                                            <div className="small opacity-50 text-uppercase" style={{fontSize:'9px'}}>Margen %</div>
                                            <div className={`fs-5 fw-bold ${marginAnalysis.color}`}>{marginAnalysis.pct}%</div>
                                        </Col>
                                    </Row>
                                    {(isAdmin || isLocutor) && (
                                        <Row className="mt-3 g-2">
                                            <Col md={3}>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <Form.Label className="small fw-bold opacity-75 mb-0">Ajustar Venta Base</Form.Label>
                                                    {surchargePct > 0 && (
                                                        <Badge bg="warning" text="dark" style={{fontSize: '9px', fontWeight: 'bold'}}>
                                                            +{surchargePct}% {surchargeLabel}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <Form.Control 
                                                    type="number" 
                                                    size="sm" 
                                                    value={customVentaBase !== undefined && customVentaBase !== null ? customVentaBase : ''} 
                                                    onChange={e => handleVentaBaseChange(e.target.value)} 
                                                    placeholder="Venta base..."
                                                    className="bg-transparent border-secondary text-white" 
                                                    disabled={!canEditPrices}
                                                />
                                                {surchargePct > 0 && parseFloat(customVentaBase) > 0 && (
                                                    <div className="mt-1 text-warning" style={{ fontSize: '10px' }}>
                                                        +{surchargePct}% {surchargeLabel} (${Math.round((parseFloat(customVentaBase) || 0) * (surchargePct / 100)).toLocaleString()}) 
                                                        → <strong>${Math.round(formData.estimated_price || 0).toLocaleString()}</strong> Total
                                                    </div>
                                                )}
                                            </Col>
                                            <Col md={3}>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <Form.Label className="small fw-bold opacity-75 mb-0">Ajustar Costo Base (CLP)</Form.Label>
                                                    {costSurchargePct > 0 && (
                                                        <Badge bg="warning" text="dark" style={{fontSize: '9px', fontWeight: 'bold'}}>
                                                            +{costSurchargePct}% FDS
                                                        </Badge>
                                                    )}
                                                </div>
                                                <Form.Control 
                                                    type="number" 
                                                    size="sm" 
                                                    value={customCostoBase !== undefined && customCostoBase !== null ? customCostoBase : ''} 
                                                    onChange={e => handleCostBaseManualChange(e.target.value)} 
                                                    placeholder="Costo base..."
                                                    className="bg-transparent border-secondary text-white" 
                                                    disabled={!canEditPrices}
                                                />
                                                {costSurchargePct > 0 && parseFloat(customCostoBase) > 0 && (
                                                    <div className="mt-1 text-warning" style={{ fontSize: '10px' }}>
                                                        +{costSurchargePct}% FDS (${Math.round((parseFloat(customCostoBase) || 0) * (costSurchargePct / 100)).toLocaleString()}) 
                                                        → <strong>${Math.round(formData.provider_cost_base || 0).toLocaleString()}</strong> Total
                                                    </div>
                                                )}
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label className="small fw-bold opacity-75 text-success mb-1">Fijar Margen %</Form.Label>
                                                <Form.Control type="number" size="sm" placeholder="Ej: 35" onChange={e => handleMarginChange(e.target.value)} className="bg-transparent border-success text-success fw-bold shadow-sm" disabled={!canEditPrices}/>
                                            </Col>
                                            {((formData.provider_currency_code || formData.local_currency_code) && (formData.provider_currency_code || formData.local_currency_code) !== 'CLP') && (
                                                <Col md={3}>
                                                    <Form.Label className="small fw-bold opacity-75 text-warning mb-1">Pago Local ({formData.provider_currency_code || formData.local_currency_code})</Form.Label>
                                                    <Form.Control type="number" size="sm" value={formData.provider_cost_local !== undefined && formData.provider_cost_local !== null ? formData.provider_cost_local : ''} onChange={e => handleCostLocalChange(e.target.value)} className="bg-dark border-warning text-warning fw-bold" disabled={!canEditPrices}/>
                                                </Col>
                                            )}
                                        </Row>
                                    )}

                                    {/* CHECKBOXES DE RECARGOS DINÁMICOS (FDS, MADRUGADA, URGENCIA, ETC.) */}
                                    {allAppliedRules && allAppliedRules.length > 0 && (
                                        <div className="mt-3 pt-3 border-top border-secondary">
                                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
                                                <span className="small fw-bold text-warning d-flex align-items-center gap-1">
                                                    ⚡ Recargos Detectados ({allAppliedRules.length}):
                                                </span>
                                                <span className="small text-muted" style={{ fontSize: '10px' }}>
                                                    💡 Desmarque si la locutora o el cliente acuerdan no aplicar el recargo
                                                </span>
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {allAppliedRules.map((rule, idx) => {
                                                    const ruleKey = rule.id ? String(rule.id) : (rule.name || `rule-${idx}`);
                                                    const isEnabled = !disabledRuleKeys.includes(ruleKey);
                                                    const displayVal = rule.type === 'percentage' ? `${rule.value}%` : `$${rule.value}`;
                                                    const affectsCost = rule.applies_to_cost;
                                                    const recargoVenta = rule.type === 'percentage' 
                                                        ? Math.round((parseFloat(customVentaBase) || 0) * ((parseFloat(rule.value) || 0) / 100))
                                                        : (parseFloat(rule.value || rule.adjustment) || 0);
                                                    const recargoCosto = affectsCost
                                                        ? (rule.type === 'percentage' 
                                                            ? Math.round((parseFloat(customCostoBase) || 0) * ((parseFloat(rule.value) || 0) / 100))
                                                            : (parseFloat(rule.cost_value || rule.cost_adjustment_clp || 0) || 0))
                                                        : 0;

                                                    return (
                                                        <div 
                                                            key={ruleKey} 
                                                            className={`d-flex align-items-center px-3 py-2 rounded border transition-all user-select-none ${
                                                                isEnabled 
                                                                    ? 'bg-secondary bg-opacity-50 border-warning text-white shadow-sm' 
                                                                    : 'bg-dark bg-opacity-75 border-secondary text-muted'
                                                            }`}
                                                            style={{ 
                                                                cursor: canEditPrices ? 'pointer' : 'default', 
                                                                minHeight: '42px',
                                                                opacity: isEnabled ? 1 : 0.65,
                                                                transition: 'all 0.2s ease-in-out'
                                                            }}
                                                            onClick={() => canEditPrices && handleToggleRule(ruleKey)}
                                                        >
                                                            <Form.Check 
                                                                type="checkbox"
                                                                id={`rule-checkbox-${ruleKey}`}
                                                                checked={isEnabled}
                                                                onChange={() => {}} 
                                                                disabled={!canEditPrices}
                                                                className="me-2 mb-0"
                                                                style={{ pointerEvents: 'none' }}
                                                            />
                                                            <div>
                                                                <div className="fw-bold d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                                                                    <span className={isEnabled ? "text-white" : "text-muted text-decoration-line-through"}>
                                                                        {rule.name}
                                                                    </span>
                                                                    <Badge bg={isEnabled ? "warning" : "secondary"} text={isEnabled ? "dark" : "white"} style={{ fontSize: '10px' }}>
                                                                        +{displayVal}
                                                                    </Badge>
                                                                </div>
                                                                <div style={{ fontSize: '10px', color: isEnabled ? '#f39c12' : '#888' }}>
                                                                    <span>Venta: {isEnabled ? `+$${recargoVenta.toLocaleString()}` : '$0 (Desmarcado)'}</span>
                                                                    {affectsCost ? (
                                                                        <span className="ms-2">| Costo: {isEnabled ? `+$${recargoCosto.toLocaleString()}` : '$0'}</span>
                                                                    ) : (
                                                                        <span className="ms-2 text-info opacity-75">| Solo Venta</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* SECCIÓN 8: SERVICIOS ADICIONALES */}
                            <Card className="mb-3 border-primary border-opacity-25 bg-primary bg-opacity-10 shadow-sm">
                                <Card.Body className="p-2">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="small fw-bold mb-0 text-primary">Servicios Adicionales (Extras)</Form.Label>
                                        <Form.Check 
                                            type="switch" 
                                            id="extra-services-switch"
                                            label={<span className="small">Habilitar Panel</span>}
                                            checked={showExtraServices}
                                            disabled={isSettled}
                                            onChange={e => setShowExtraServices(e.target.checked)}
                                        />
                                    </div>
                                    
                                    {showExtraServices && (
                                        <div className="p-2 bg-white rounded border overflow-auto" style={{maxHeight:'140px'}}>
                                            {EXTRA_OPTIONS.map(opt => {
                                                const existing = (formData.extra_services || []).find(s => s.id === opt.id);
                                                return (
                                                    <div key={opt.id} className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom last:border-0">
                                                        <Form.Check 
                                                            type="checkbox" 
                                                            id={`extra-${opt.id}`}
                                                            label={
                                                                <span style={{fontSize:'11px'}}>
                                                                    {opt.name} 
                                                                    {opt.id === 'parada_intermedia' && suggestedStopPrice > 0 && (
                                                                        <span className="text-primary ms-1 fw-bold">(Sugerido: ${suggestedStopPrice})</span>
                                                                    )}
                                                                </span>
                                                            }
                                                            checked={!!existing}
                                                            className="flex-grow-1"
                                                            disabled={isSettled}
                                                            onChange={e => {
                                                                let newList = [...(formData.extra_services || [])];
                                                                if (e.target.checked) {
                                                                    const defaultPrice = opt.id === 'parada_intermedia' ? suggestedStopPrice : 0;
                                                                    newList.push({ id: opt.id, name: opt.name, price: defaultPrice });
                                                                }
                                                                else newList = newList.filter(s => s.id !== opt.id);
                                                                setFormData(prev => ({...prev, extra_services: newList}));
                                                            }}
                                                        />
                                                        {existing && (
                                                            <InputGroup size="sm" style={{width:'100px'}}>
                                                                <InputGroup.Text className="px-1">$</InputGroup.Text>
                                                                <Form.Control 
                                                                    type="number" 
                                                                    value={existing.price !== undefined && existing.price !== null ? existing.price : ''}
                                                                    disabled={isSettled}
                                                                    placeholder="0"
                                                                    className="text-end"
                                                                    onChange={e => {
                                                                        const newPrice = e.target.value;
                                                                        const newList = formData.extra_services.map(s => s.id === opt.id ? {...s, price: newPrice} : s);
                                                                        setFormData(prev => ({...prev, extra_services: newList}));
                                                                    }}
                                                                />
                                                            </InputGroup>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>

                             {/* GASTOS REEMBOLSABLES (SIN COMISIÓN) */}
                             <Card className="mb-3 border-success border-opacity-25 bg-success bg-opacity-10 shadow-sm">
                                 <Card.Body className="p-2">
                                     <div className="d-flex justify-content-between align-items-center mb-2">
                                         <Form.Label className="small fw-bold mb-0 text-success">Gastos Reembolsables (Sin Comisión)</Form.Label>
                                         <Form.Check 
                                             type="switch" 
                                             id="reimbursable-expense-switch"
                                             label={<span className="small">Habilitar</span>}
                                             checked={formData.has_reimbursable_expense || false}
                                             disabled={isSettled}
                                             onChange={e => setFormData(prev => ({...prev, has_reimbursable_expense: e.target.checked}))}
                                         />
                                     </div>
                                     
                                     {formData.has_reimbursable_expense && (
                                         <Row className="g-2">
                                             <Col xs={6}>
                                                 <Form.Label className="small fw-bold text-slate-600 mb-0" style={{fontSize:'11px'}}>Monto para Cliente</Form.Label>
                                                 <InputGroup size="sm">
                                                     <InputGroup.Text className="px-1">$</InputGroup.Text>
                                                     <Form.Control 
                                                         type="number" 
                                                         value={formData.reimbursable_expense_price !== undefined && formData.reimbursable_expense_price !== null ? formData.reimbursable_expense_price : ''}
                                                         disabled={isSettled}
                                                         placeholder="0"
                                                         className="text-end"
                                                         style={{fontSize:'12px'}}
                                                         onChange={e => setFormData(prev => ({...prev, reimbursable_expense_price: e.target.value}))}
                                                     />
                                                 </InputGroup>
                                             </Col>
                                             <Col xs={6}>
                                                 <Form.Label className="small fw-bold text-slate-600 mb-0" style={{fontSize:'11px'}}>Monto para Conductor</Form.Label>
                                                 <InputGroup size="sm">
                                                     <InputGroup.Text className="px-1">$</InputGroup.Text>
                                                     <Form.Control 
                                                         type="number" 
                                                         value={formData.reimbursable_expense_cost !== undefined && formData.reimbursable_expense_cost !== null ? formData.reimbursable_expense_cost : ''}
                                                         disabled={isSettled}
                                                         placeholder="0"
                                                         className="text-end"
                                                         style={{fontSize:'12px'}}
                                                         onChange={e => setFormData(prev => ({...prev, reimbursable_expense_cost: e.target.value}))}
                                                     />
                                                 </InputGroup>
                                             </Col>
                                         </Row>
                                     )}
                                 </Card.Body>
                             </Card>
 
                             <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Label className="small fw-bold"><Car size={14} className="me-1"/> Conductor y Móvil Asignado</Form.Label>
                                    <div className="d-flex gap-2">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <SearchableSelect 
                                                options={evaluatedDrivers} 
                                                value={formData.driver_id || ''} 
                                                onDisabledSelect={(opt) => {
                                                    if (opt.has_capacity_block) {
                                                        toast.error(`🚫 Asignación Imposible: El móvil [${opt.internal_code}] tiene capacidad para menos pasajeros (${opt.capacity_detail}). No se puede asignar.`);
                                                        return;
                                                    }
                                                    if (opt.has_booking_only_block) {
                                                        toast.error(`🚫 Conductor Restringido: ${opt.full_name} solo puede realizar servicios de Booking.com. No se puede asignar a viajes corporativos.`);
                                                        return;
                                                    }
                                                    setForcedDriverPrompt(opt);
                                                }}
                                                onChange={val => {
                                                    const selectedDriver = drivers.find(d => d.id === parseInt(val));
                                                    const provCurrency = selectedDriver?.provider?.currency_code || formData.local_currency_code || 'CLP';
                                                    const currSt = (formData.status || 'pending').toLowerCase();
                                                    setFormData({
                                                        ...formData, 
                                                        driver_id: val, 
                                                        provider_id: selectedDriver ? (selectedDriver.provider_id || null) : null,
                                                        force_driver_assignment: false,
                                                        provider_currency_code: provCurrency,
                                                        status: (!val || val === '') ? 'pending' : (['pending', 'confirmed'].includes(currSt) ? 'assigned' : formData.status)
                                                    });
                                                }}
                                                disabled={isSettled || !canEditDriver} 
                                                placeholder="Sin Asignar / Central..."
                                            />
                                            {(() => {
                                                const assignedEval = evaluatedDrivers.find(d => String(d.id) === String(formData.driver_id));
                                                if (assignedEval && !assignedEval.is_available) {
                                                    return (
                                                        <div className="mt-1 small alert alert-warning py-1 px-2 mb-0 d-flex align-items-center gap-1 border border-warning shadow-xs">
                                                            <AlertTriangle size={14} className="text-warning flex-shrink-0" />
                                                            <span><strong>Asignación Excepcional:</strong> {assignedEval.unavailability_reason}</span>
                                                        </div>
                                                    );
                                                }
                                                if (assignedEval && assignedEval.is_recommended) {
                                                    return (
                                                        <div className="mt-1 small alert alert-light py-1 px-2 mb-0 d-flex align-items-center gap-1 border border-warning text-dark shadow-xs" style={{ backgroundColor: '#fffbeb' }}>
                                                            <span className="text-warning">⭐</span>
                                                            <span><strong>Conductor Sugerido:</strong> Esta asignación sumará puntos a tu Score de Equidad ({assignedEval.trips_last_30d ?? 0} viajes en 30 días).</span>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                        {formData.driver_id && canEditDriver && !isSettled && (
                                            <Button 
                                                variant="outline-danger" 
                                                size="sm" 
                                                title="Liberar Conductor"
                                                onClick={() => {
                                                    if(window.confirm('¿Liberar este servicio? Se borrará al conductor y volverá a estado PENDIENTE.')) {
                                                        setFormData({...formData, driver_id: '', provider_id: null, force_driver_assignment: false, status: 'pending'});
                                                    }
                                                }}
                                            >
                                                <X size={16} />
                                            </Button>
                                        )}
                                    </div>
                                    {driverConflictWarning && driverConflictWarning.length > 0 && (
                                        <Alert variant="warning" className="mt-2 p-2 mb-0 border border-warning shadow-sm rounded-3">
                                            <div className="fw-bold small text-dark d-flex align-items-center">
                                                <span className="me-2 fs-5">⚠️</span> 
                                                <span><strong>¡Atención: Traslape de Horarios!</strong> El chofer/móvil ya tiene asignado {driverConflictWarning.length} servicio(s) cercano(s):</span>
                                            </div>
                                            <ul className="mb-0 mt-1 ps-3 small text-dark">
                                                {driverConflictWarning.map((c, idx) => (
                                                    <li key={idx} className="mb-1">
                                                        <strong>Viaje #{c.trip_id}</strong> ({c.scheduled_time} hrs) — <em>{c.client_name}</em> ({c.passenger_name}). 
                                                        <span className="text-muted ms-1">({c.origin || 'Origen'} ➔ {c.destination || 'Destino'})</span>
                                                        {c.conflict_reason && (
                                                            <div className="text-danger fw-semibold mt-1" style={{ fontSize: '0.8rem' }}>
                                                                ↳ {c.conflict_reason}
                                                            </div>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                            <div className="small text-muted mt-1 fst-italic">
                                                * Precaución: El chofer podría no llegar a tiempo a la recogida debido a tiempos de traslado y tráfico.
                                            </div>
                                        </Alert>
                                    )}
                                </Col>
                            </Row>
                            
                            {priceBreakdown && (
                                <div className="small text-muted mb-2 px-2">
                                    <div>
                                        <Badge bg="secondary" className="me-1">Tarificación</Badge> Origen detectado en zona: <strong>{priceBreakdown.source}</strong> (Base: ${priceBreakdown.base_price?.toLocaleString()})
                                    </div>
                                    {priceBreakdown.applied_rules && priceBreakdown.applied_rules.length > 0 && (
                                        <div className="mt-2 ps-3 border-start border-2 border-primary" style={{ fontSize: '0.8rem' }}>
                                            <span className="fw-bold text-dark d-block mb-1">Desglose de Recargos / Ajustes:</span>
                                            {priceBreakdown.applied_rules.map((rule, idx) => {
                                                const sign = rule.adjustment >= 0 ? '+' : '';
                                                const displayVal = rule.type === 'percentage' ? `${rule.value}%` : `$${rule.value}`;
                                                return (
                                                    <div key={idx} className="d-flex justify-content-between text-muted" style={{ maxWidth: '350px' }}>
                                                        <span>🔹 {rule.name} ({displayVal}):</span>
                                                        <span className="fw-bold text-dark">{sign}${Math.round(rule.adjustment)?.toLocaleString()}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* SECCIÓN: INCIDENCIAS BOOKING.COM */}
                            {formData.external_reference && formData.booking_incident_status && (
                                <Card className="mb-3 border-danger shadow-sm">
                                    <Card.Header className="bg-danger bg-opacity-10 text-danger fw-bold d-flex justify-content-between align-items-center">
                                        <span className="d-flex align-items-center small">
                                            <AlertTriangle size={16} className="me-2" /> INCIDENCIA BOOKING.COM
                                        </span>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="py-0 px-2"
                                            onClick={handleSyncIncidents}
                                            disabled={submittingIncident}
                                            style={{ fontSize: '11px' }}
                                        >
                                            <RefreshCw size={12} className={`me-1 ${submittingIncident ? 'spin' : ''}`} /> Sincronizar
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-3">
                                        <Row className="g-2 mb-2">
                                            <Col md={6}>
                                                <div className="small text-muted">ID Reserva Booking:</div>
                                                <div className="fw-bold">{formData.external_reference}</div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="small text-muted">Estado Incidencia:</div>
                                                <div className="d-flex flex-wrap align-items-center gap-1">
                                                    {formData.booking_incident_status === 'DISPUTED' && (
                                                        <Badge bg="warning" text="dark" className="d-inline-flex align-items-center">
                                                            <AlertCircle size={12} className="me-1" /> Disputada / En Revisión
                                                        </Badge>
                                                    )}
                                                    {formData.booking_incident_status === 'RESOLVED' && (
                                                        <Badge bg="success" className="d-inline-flex align-items-center">
                                                            <CheckCircle size={12} className="me-1" /> Resuelta (Ganada)
                                                        </Badge>
                                                    )}
                                                    {formData.booking_incident_status === 'PENALIZED' && (
                                                        <Badge bg="danger" className="d-inline-flex align-items-center">
                                                            <AlertTriangle size={12} className="me-1" /> Penalizada (Perdida)
                                                        </Badge>
                                                    )}
                                                    {!formData.booking_incident_status && (
                                                        <span className="text-muted small">Ninguna registrada</span>
                                                    )}
                                                    {manualIncidentLock && (
                                                        <Badge
                                                            bg="dark"
                                                            className="d-inline-flex align-items-center"
                                                            title={manualIncidentLock.notes || 'Resolución manual protegida: la API de Booking no podrá sobrescribirla'}
                                                        >
                                                            <Shield size={11} className="me-1" /> Blindada Manual
                                                        </Badge>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>

                                        {formData.booking_incident_status === 'DISPUTED' && (
                                            <div className="p-2 border rounded bg-warning bg-opacity-10 mb-2 small text-warning-emphasis">
                                                <strong>Liquidación Retenida:</strong> Esta reserva tiene una disputa activa en Booking.com. La liquidación asociada a este viaje está bloqueada hasta que se resuelva o sea confirmada manualmente.
                                            </div>
                                        )}

                                        <div className="mt-2 border-top pt-2">
                                            <Form.Check
                                                type="switch"
                                                id="dispute-sent-switch"
                                                label={<span className="small fw-bold">¿Se enviaron respaldos / disputó en Partner Portal?</span>}
                                                checked={formData.booking_incident_dispute_sent || false}
                                                onChange={(e) => handleDisputeToggle(e.target.checked)}
                                            />
                                        </div>

                                        {isAdmin && (formData.booking_incident_status === 'DISPUTED' || formData.booking_incident_status === 'PENALIZED' || formData.booking_incident_status === 'RESOLVED') && (
                                            <div className="mt-3 border-top pt-3">
                                                <Form.Label className="small fw-bold text-dark mb-2">Acción del Administrador: Resolver o Editar Penalización</Form.Label>
                                                
                                                <div className="d-flex flex-column gap-2">
                                                    {/* OPCIÓN 1: RECLAMO GANADO */}
                                                    <div className="p-2 border rounded bg-success bg-opacity-10">
                                                        <div className="d-flex justify-content-between align-items-center gap-2">
                                                            <div>
                                                                <div className="fw-bold text-success small">🏆 Reclamo Ganado (Sin Penalización)</div>
                                                                <div className="text-muted" style={{ fontSize: '10px' }}>
                                                                    Blinda la resolución frente a la API de Booking y restaura tarifas (o genera abono si ya fue liquidado).
                                                                </div>
                                                            </div>
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={handleOpenReversalPreview}
                                                                disabled={submittingIncident || loadingReversalPreview}
                                                                className="fw-bold px-3 text-nowrap"
                                                            >
                                                                {loadingReversalPreview ? 'Calculando...' : '🏆 Marcar Ganada (0%)'}
                                                            </Button>
                                                        </div>

                                                        {reversalPreview && (
                                                            <div className="mt-2 p-3 bg-white border border-success rounded shadow-sm">
                                                                <div className="fw-bold text-success small mb-1">
                                                                    Confirmar Reversa de Incidencia {reversalPreview.external_reference ? `(#${reversalPreview.external_reference})` : ''}
                                                                </div>
                                                                <div className="small text-dark mb-2">
                                                                    {reversalPreview.summary_message}
                                                                </div>

                                                                <Row className="g-2 mb-2 text-center">
                                                                    <Col xs={6} md={4}>
                                                                        <div className="p-1 border rounded bg-light">
                                                                            <div className="text-muted" style={{ fontSize: '10px' }}>Venta Booking Normal</div>
                                                                            <div className="fw-bold text-dark small">
                                                                                {formatIncidentCurrency(reversalPreview.normal_sale_price, 'CLP')}
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                    <Col xs={6} md={4}>
                                                                        <div className="p-1 border rounded bg-light">
                                                                            <div className="text-muted" style={{ fontSize: '10px' }}>Pago Normal Conductor</div>
                                                                            <div className="fw-bold text-success small">
                                                                                {formatIncidentCurrency(reversalPreview.normal_driver_pay, reversalPreview.currency_code)}
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                    <Col xs={12} md={4}>
                                                                        <div className="p-1 border rounded bg-light">
                                                                            <div className="text-muted" style={{ fontSize: '10px' }}>
                                                                                {reversalPreview.is_settled ? `Liquidado (Liq. #${reversalPreview.settlement_id})` : 'Estado Liquidación'}
                                                                            </div>
                                                                            <div className="fw-bold small text-slate-700">
                                                                                {reversalPreview.is_settled
                                                                                    ? formatIncidentCurrency(
                                                                                          (reversalPreview.settled_driver_amount || 0) - (reversalPreview.settled_penalty_advances_total || 0),
                                                                                          reversalPreview.currency_code
                                                                                      )
                                                                                    : 'Abierta (Sin liquidar)'}
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                </Row>

                                                                {reversalPreview.is_client_invoiced && (
                                                                    <div className="p-2 mb-2 rounded bg-warning bg-opacity-10 border border-warning small text-dark" style={{ fontSize: '11px' }}>
                                                                        ⚠️ Este viaje ya pertenece a una prefactura/factura cerrada (#{reversalPreview.client_invoice_id}); no se alterará la factura histórica.
                                                                    </div>
                                                                )}

                                                                {reversalPreview.existing_settled_credit_total > 0 && (
                                                                    <div className="p-2 mb-2 rounded bg-info bg-opacity-10 border border-info small text-dark" style={{ fontSize: '11px' }}>
                                                                        ℹ️ Ya existe un abono liquidado previamente para esta reserva por +{formatIncidentCurrency(reversalPreview.existing_settled_credit_total, reversalPreview.currency_code)}.
                                                                    </div>
                                                                )}

                                                                {reversalPreview.is_settled && (
                                                                    <div className="p-2 mb-2 rounded bg-success bg-opacity-10 border border-success border-opacity-25">
                                                                        <Form.Check
                                                                            type="switch"
                                                                            id="create-reimbursement-switch"
                                                                            label={
                                                                                <span className="small fw-bold text-success">
                                                                                    Generar Abono / Reintegro para la próxima liquidación del conductor
                                                                                </span>
                                                                            }
                                                                            checked={createReimbursementToggle}
                                                                            onChange={(e) => setCreateReimbursementToggle(e.target.checked)}
                                                                        />
                                                                        {createReimbursementToggle && (
                                                                            <Row className="g-2 mt-1 align-items-center">
                                                                                <Col md={6}>
                                                                                    <Form.Label className="small text-muted mb-1" style={{ fontSize: '11px' }}>
                                                                                        Monto a abonar al conductor ({reversalPreview.currency_code || 'CLP'})
                                                                                    </Form.Label>
                                                                                    <Form.Control
                                                                                        type="number"
                                                                                        size="sm"
                                                                                        min="0"
                                                                                        step="any"
                                                                                        value={reimbursementAmountInput}
                                                                                        onChange={(e) => setReimbursementAmountInput(e.target.value)}
                                                                                    />
                                                                                </Col>
                                                                                <Col md={6}>
                                                                                    <div className="text-muted mt-3" style={{ fontSize: '10px' }}>
                                                                                        No modifica la Liq. #{reversalPreview.settlement_id} cerrada. Se sumará como saldo a favor (+abono) en su siguiente liquidación.
                                                                                    </div>
                                                                                </Col>
                                                                            </Row>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                <Form.Group className="mb-2">
                                                                    <Form.Label className="small text-muted mb-1" style={{ fontSize: '11px' }}>
                                                                        Nota de respaldo (opcional, ej: Correo soporte Booking / Ticket)
                                                                    </Form.Label>
                                                                    <Form.Control
                                                                        type="text"
                                                                        size="sm"
                                                                        placeholder="Ej: Apelación ganada por correo soporte Booking"
                                                                        value={reversalNotesInput}
                                                                        onChange={(e) => setReversalNotesInput(e.target.value)}
                                                                    />
                                                                </Form.Group>

                                                                <div className="d-flex justify-content-end gap-2 mt-2">
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        onClick={() => setReversalPreview(null)}
                                                                        disabled={submittingIncident}
                                                                    >
                                                                        Cancelar
                                                                    </Button>
                                                                    <Button
                                                                        variant="success"
                                                                        size="sm"
                                                                        className="fw-bold"
                                                                        onClick={handleConfirmResolveWon}
                                                                        disabled={submittingIncident}
                                                                    >
                                                                        {submittingIncident ? 'Confirmando...' : '✅ Confirmar Reclamo Ganado'}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* OPCIÓN 2: PENALIZACIÓN EDITABLE */}
                                                    <div className="p-2 border rounded bg-danger bg-opacity-10">
                                                        <div className="fw-bold text-danger small mb-1">⚠️ Modificar / Aplicar Penalidad al Proveedor</div>
                                                        <Row className="g-2 align-items-end">
                                                            <Col md={7}>
                                                                <Form.Label className="small text-muted mb-1" style={{ fontSize: '10px' }}>Porcentaje de Penalidad (%)</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    size="sm"
                                                                    placeholder="Ej: 25 (o 100, 150)"
                                                                    value={customPenaltyPct}
                                                                    onChange={(e) => setCustomPenaltyPct(e.target.value)}
                                                                />
                                                                {customPenaltyPct !== '' && !isNaN(customPenaltyPct) && formData.provider_cost_local && (
                                                                    <div className="text-danger fw-bold mt-1" style={{ fontSize: '11px' }}>
                                                                        Monto a descontar: ${Math.round((parseFloat(formData.provider_cost_local) || 0) * (parseFloat(customPenaltyPct) / 100)).toLocaleString('es-CL')}
                                                                    </div>
                                                                )}
                                                            </Col>
                                                            <Col md={5}>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    className="w-100 fw-bold"
                                                                    onClick={handleConfirmIncident}
                                                                    disabled={submittingIncident}
                                                                >
                                                                    {submittingIncident ? 'Guardando...' : 'Aplicar Penalidad'}
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* APELACIÓN / DESCARGOS DEL PROVEEDOR */}
                                        {formData.provider_appeal_notes && (
                                            <div className="mt-3 p-3 bg-light rounded border border-warning">
                                                <div className="fw-bold text-dark mb-1 d-flex align-items-center gap-1">
                                                    <MessageCircle size={14} className="text-primary" /> Descargo y Versión del Conductor / Proveedor:
                                                </div>
                                                <div className="small text-dark fw-medium mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                                                    "{formData.provider_appeal_notes}"
                                                </div>
                                                {formData.provider_appeal_at && (
                                                    <div className="text-muted" style={{ fontSize: '10px' }}>
                                                        Enviado el {new Date(formData.provider_appeal_at).toLocaleString('es-CL')}
                                                    </div>
                                                )}
                                                {formData.provider_appeal_evidence && formData.provider_appeal_evidence.length > 0 && (
                                                    <div className="mt-2 pt-2 border-top">
                                                        <div className="small fw-bold text-muted mb-2">📸 Fotos / Evidencias Adjuntas por el Proveedor:</div>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {formData.provider_appeal_evidence.map((key, idx) => {
                                                                const fullUrl = key.startsWith('http') ? key : `${process.env.REACT_APP_API_URL || 'https://flujo-app-backend.onrender.com'}/transport/documents/download/${key}`;
                                                                return (
                                                                    <div key={idx} className="d-flex flex-column align-items-center bg-white p-1 border rounded shadow-sm">
                                                                        <img 
                                                                            src={fullUrl} 
                                                                            alt={`Evidencia ${idx + 1}`} 
                                                                            style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '4px' }}
                                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                                        />
                                                                        <a
                                                                            href={fullUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="btn btn-outline-primary btn-sm py-0 px-2 mt-1 text-decoration-none"
                                                                            style={{ fontSize: '10px' }}
                                                                        >
                                                                            🔍 Ver #{idx + 1} 🔗
                                                                        </a>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="mt-3 pt-2 border-top d-flex gap-2 flex-wrap">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleGenerateAiEmail}
                                                disabled={generatingAiEmail}
                                                className="d-flex align-items-center gap-1"
                                            >
                                                {generatingAiEmail ? <RefreshCw size={12} className="spin" /> : <Sparkles size={14} />}
                                                {generatingAiEmail ? "Generando Carta..." : "✨ Generar Carta Formal con IA"}
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}
                        </Col>
                        
                        <Col md={4}>
                            <div className="h-100 border rounded shadow-sm bg-light overflow-hidden position-relative" style={{minHeight:'500px'}}>
                                {isLoaded && <RouteMap pickupLocation={pickupLocation} dropoffLocation={dropoffLocation} stops={formData.stops} onDistanceCalculated={handleDistanceCalculated}/>}
                                {routeDistanceKm > 0 && (
                                    <div className="position-absolute bottom-0 w-100 bg-white bg-opacity-90 p-2 border-top d-flex justify-content-around shadow-sm" style={{zIndex: 10}}>
                                        <div className="text-center small">
                                            <span className="text-muted d-block" style={{fontSize: '10px'}}>Distancia</span>
                                            <span className="fw-bold">{routeDistanceKm.toFixed(1)} km</span>
                                        </div>
                                        <div className="text-center small border-start ps-3">
                                            <span className="text-muted d-block" style={{fontSize: '10px'}}>Tiempo Est.</span>
                                            <span className="fw-bold">{routeDurationMin} min</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between bg-light">
                    <div>
                        {formData.id && currentStatus === 'pending' && <Button variant="outline-primary" onClick={() => handleStatusUpdate('assigned')}>Marcar Asignado</Button>}
                        {formData.id && currentStatus === 'assigned' && <Button variant="info" className="text-white" onClick={() => handleStatusUpdate('confirmed')}>Confirmar por Driver</Button>}
                        {formData.id && currentStatus === 'on_board' && <Button variant="success" onClick={() => handleStatusUpdate('completed')}>Finalizar Servicio</Button>}
                    </div>
                    <div>
                        <Button variant="secondary" onClick={onHide} className="me-2 shadow-sm" disabled={isLoading || isSubmitting}>Cerrar</Button>
                        <Button variant="outline-primary" type="button" onClick={() => handleSubmit(null, true)} className="me-2 shadow-sm fw-bold" disabled={isLocked || isRepeatChecked || isLoading || isSubmitting}>Guardar y Crear Retorno</Button>
                        <Button variant="primary" type="submit" className="px-4 shadow-sm fw-bold" disabled={isLocked || isLoading || isSubmitting}>
                            {isLoading || isSubmitting ? 'Guardando...' : (isRepeatChecked ? 'Crear Servicios Recurrentes' : 'Guardar Cambios')}
                        </Button>
                    </div>
                </Modal.Footer>
            </Form>
        </Modal>

        {/* MODALES DE CREACIÓN RÁPIDA EXTENDIDOS */}
        <Modal show={showQuickReqModal} onHide={() => setShowQuickReqModal(false)} size="md">
            <Modal.Header closeButton><Modal.Title className="h6 fw-bold">{quickReq.id ? 'Editar Solicitante' : 'Añadir Nuevo Solicitante'}</Modal.Title></Modal.Header>
            <Modal.Body>
                <Row className="g-2">
                    <Col md={12}><Form.Label className="small">Nombre Completo</Form.Label><InputGroup size="sm"><InputGroup.Text><User size={14}/></InputGroup.Text><Form.Control placeholder="Ej: Juan Pérez" value={quickReq.full_name} onChange={e => setQuickReq({...quickReq, full_name: e.target.value})}/></InputGroup></Col>
                    <Col md={6}><Form.Label className="small">Email</Form.Label><InputGroup size="sm"><InputGroup.Text><Mail size={14}/></InputGroup.Text><Form.Control placeholder="juan@empresa.cl" value={quickReq.email} onChange={e => setQuickReq({...quickReq, email: e.target.value})}/></InputGroup></Col>
                    <Col md={6}><Form.Label className="small">Teléfono</Form.Label><InputGroup size="sm"><InputGroup.Text><Phone size={14}/></InputGroup.Text><Form.Control placeholder="+569..." value={quickReq.phone} onChange={e => setQuickReq({...quickReq, phone: e.target.value})}/></InputGroup></Col>
                    
                    <Col md={6}>
                        <Form.Label className="small">Área</Form.Label>
                        <div className="d-flex gap-1 align-items-center w-100">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <SearchableSelect
                                    options={contextAreas
                                        .filter(a => a.is_active !== false || String(a.id) === String(quickReq.area_id))
                                        .map(a => ({
                                            id: a.id,
                                            name: a.name + (a.is_active === false ? " (Inactivo)" : "")
                                        }))
                                    }
                                    value={quickReq.area_id}
                                    onChange={val => setQuickReq({...quickReq, area_id: val})}
                                    placeholder="Buscar Área..."
                                />
                            </div>
                            <Button variant="outline-primary" size="sm" onClick={() => handleAddAreaPrompt('req')} title="Nueva Área" style={{ height: '38px' }}>
                                <Plus size={14}/>
                            </Button>
                        </div>
                    </Col>
                    <Col md={6}>
                        <Form.Label className="small">Centro de Costo</Form.Label>
                        <div className="d-flex gap-1 align-items-center w-100">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <SearchableSelect
                                    options={contextCostCenters
                                        .filter(cc => cc.is_active !== false || String(cc.id) === String(quickReq.cost_center_id))
                                        .map(cc => ({
                                            id: cc.id,
                                            name: `${cc.code}${cc.name ? ` - ${cc.name}` : ''}` + (cc.is_active === false ? " (Inactivo)" : "")
                                        }))
                                    }
                                    value={quickReq.cost_center_id}
                                    onChange={val => setQuickReq({...quickReq, cost_center_id: val})}
                                    placeholder="Buscar Centro de Costo..."
                                />
                            </div>
                            <Button variant="outline-primary" size="sm" onClick={() => handleAddCCPrompt('req')} title="Nuevo CC" style={{ height: '38px' }}>
                                <Plus size={14}/>
                            </Button>
                        </div>
                    </Col>

                    <Col md={12}>
                        <Form.Label className="small">Dirección Particular (Casa)</Form.Label>
                        <AddressAutocomplete 
                            value={quickReq.home_address || ''}
                            onChange={(val) => setQuickReq(prev => ({ ...prev, home_address: val }))}
                            onSelect={({ address }) => setQuickReq(prev => ({ ...prev, home_address: address }))}
                            placeholder="Ej: Av. El Bosque 123"
                        />
                    </Col>
                    <Col md={12}>
                        <Form.Label className="small">Dirección Laboral (Oficina)</Form.Label>
                        <AddressAutocomplete 
                            value={quickReq.office_address || ''}
                            onChange={(val) => setQuickReq(prev => ({ ...prev, office_address: val }))}
                            onSelect={({ address }) => setQuickReq(prev => ({ ...prev, office_address: address }))}
                            placeholder="Ej: Av. Providencia 456"
                        />
                    </Col>
                    
                    <Col md={12} className="pt-2"><Form.Check type="switch" label={<span><Shield size={14} className="me-1"/> Habilitar acceso al portal</span>} checked={quickReq.has_portal_access || false} onChange={e => setQuickReq({...quickReq, has_portal_access: e.target.checked})}/></Col>
                    <Col md={12} className="pt-1"><Form.Check type="switch" label={<span><User size={14} className="me-1"/> Crear/Actualizar también como Pasajero Frecuente</span>} checked={quickReq.create_as_passenger || false} onChange={e => setQuickReq({...quickReq, create_as_passenger: e.target.checked})}/></Col>
                </Row>
                <Button className="w-100 mt-3 fw-bold shadow-sm" onClick={handleQuickCreateRequester}>{quickReq.id ? 'Guardar Cambios' : 'Crear Solicitante'}</Button>
            </Modal.Body>
        </Modal>
 
         <Modal show={showQuickPaxModal} onHide={() => setShowQuickPaxModal(false)} size="md">
             <Modal.Header closeButton><Modal.Title className="h6 fw-bold">{quickPax.id ? 'Editar Pasajero' : 'Añadir Pasajero Frecuente'}</Modal.Title></Modal.Header>
             <Modal.Body>
                 <Row className="g-2">
                     <Col md={12}><Form.Label className="small">Nombre Completo</Form.Label><InputGroup size="sm"><InputGroup.Text><User size={14}/></InputGroup.Text><Form.Control placeholder="Ej: Juan Pérez" value={quickPax.full_name} onChange={e => setQuickPax({...quickPax, full_name: e.target.value})}/></InputGroup></Col>
                     <Col md={6}><Form.Label className="small">Email</Form.Label><InputGroup size="sm"><InputGroup.Text><Mail size={14}/></InputGroup.Text><Form.Control placeholder="juan@empresa.cl" value={quickPax.email} onChange={e => setQuickPax({...quickPax, email: e.target.value})}/></InputGroup></Col>
                     <Col md={6}><Form.Label className="small">Teléfono</Form.Label><InputGroup size="sm"><InputGroup.Text><Phone size={14}/></InputGroup.Text><Form.Control placeholder="+569..." value={quickPax.phone} onChange={e => setQuickPax({...quickPax, phone: e.target.value})}/></InputGroup></Col>
                     
                     <Col md={6}>
                         <Form.Label className="small">Área</Form.Label>
                         <div className="d-flex gap-1 align-items-center w-100">
                             <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                 <SearchableSelect
                                     options={contextAreas
                                         .filter(a => a.is_active !== false || String(a.id) === String(quickPax.area_id))
                                         .map(a => ({
                                             id: a.id,
                                             name: a.name + (a.is_active === false ? " (Inactivo)" : "")
                                         }))
                                     }
                                     value={quickPax.area_id}
                                     onChange={val => setQuickPax({...quickPax, area_id: val})}
                                     placeholder="Buscar Área..."
                                 />
                             </div>
                             <Button variant="outline-primary" size="sm" onClick={() => handleAddAreaPrompt('pax')} title="Nueva Área" style={{ height: '38px' }}>
                                 <Plus size={14}/>
                             </Button>
                         </div>
                     </Col>
                     <Col md={6}>
                         <Form.Label className="small">Centro de Costo</Form.Label>
                         <div className="d-flex gap-1 align-items-center w-100">
                             <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                 <SearchableSelect
                                     options={contextCostCenters
                                         .filter(cc => cc.is_active !== false || String(cc.id) === String(quickPax.cost_center_id))
                                         .map(cc => ({
                                             id: cc.id,
                                             name: `${cc.code}${cc.name ? ` - ${cc.name}` : ''}` + (cc.is_active === false ? " (Inactivo)" : "")
                                         }))
                                     }
                                     value={quickPax.cost_center_id}
                                     onChange={val => setQuickPax({...quickPax, cost_center_id: val})}
                                     placeholder="Buscar Centro de Costo..."
                                 />
                             </div>
                             <Button variant="outline-primary" size="sm" onClick={() => handleAddCCPrompt('pax')} title="Nuevo CC" style={{ height: '38px' }}>
                                 <Plus size={14}/>
                             </Button>
                         </div>
                     </Col>
 
                     <Col md={12}>
                         <Form.Label className="small">Dirección Particular (Casa)</Form.Label>
                         <AddressAutocomplete 
                             value={quickPax.home_address || ''}
                             onChange={(val) => setQuickPax(prev => ({ ...prev, home_address: val }))}
                             onSelect={({ address }) => setQuickPax(prev => ({ ...prev, home_address: address }))}
                             placeholder="Ej: Av. El Bosque 123"
                         />
                     </Col>
                     <Col md={12}>
                         <Form.Label className="small">Dirección Laboral (Oficina)</Form.Label>
                         <AddressAutocomplete 
                             value={quickPax.office_address || ''}
                             onChange={(val) => setQuickPax(prev => ({ ...prev, office_address: val }))}
                             onSelect={({ address }) => setQuickPax(prev => ({ ...prev, office_address: address }))}
                             placeholder="Ej: Av. Providencia 456"
                         />
                     </Col>
                     
                     <Col md={12} className="pt-2">
                         <Form.Check 
                             type="switch" 
                             label={<span><Shield size={14} className="me-1"/> Habilitar acceso al portal</span>} 
                             checked={quickPax.has_portal_access || false} 
                             onChange={e => setQuickPax({...quickPax, has_portal_access: e.target.checked})}
                         />
                     </Col>
                 </Row>
                 <Button className="w-100 mt-3 fw-bold shadow-sm" onClick={handleQuickCreatePassenger}>{quickPax.id ? 'Guardar Cambios' : 'Crear Pasajero'}</Button>
             </Modal.Body>
         </Modal>

         <Modal show={showQuickAreaModal} onHide={() => setShowQuickAreaModal(false)} size="md">
              <Modal.Header closeButton>
                  <Modal.Title className="h6 fw-bold">Nueva Área</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                  <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">Nombre</Form.Label>
                      <Form.Control 
                          placeholder="Nombre del Área (ej: Operaciones)" 
                          value={quickArea.name || ''} 
                          onChange={e => setQuickArea({ name: e.target.value })}
                      />
                  </Form.Group>
                  <Button className="w-100 shadow-sm fw-bold" onClick={handleQuickCreateArea}>Crear Área</Button>
              </Modal.Body>
          </Modal>
  
          <Modal show={showQuickCCModal} onHide={() => setShowQuickCCModal(false)} size="md">
              <Modal.Header closeButton>
                  <Modal.Title className="h6 fw-bold">Nuevo Centro de Costo</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                  <Form.Group className="mb-2">
                      <Form.Label className="small fw-bold">Código</Form.Label>
                      <Form.Control 
                          placeholder="Código (ej: CC-100)" 
                          value={quickCC.code || ''} 
                          onChange={e => setQuickCC({ ...quickCC, code: e.target.value })}
                      />
                  </Form.Group>
                  <Form.Group className="mb-2">
                      <Form.Label className="small fw-bold">Nombre</Form.Label>
                      <Form.Control 
                          placeholder="Nombre (ej: Finanzas)" 
                          value={quickCC.name || ''} 
                          onChange={e => setQuickCC({ ...quickCC, name: e.target.value })}
                      />
                  </Form.Group>
                  <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">File (Opcional)</Form.Label>
                      <Form.Control 
                          placeholder="N° de File (ej: F-999)" 
                          value={quickCC.file || ''} 
                          onChange={e => setQuickCC({ ...quickCC, file: e.target.value })}
                      />
                  </Form.Group>
                  <Button className="w-100 shadow-sm fw-bold" onClick={handleQuickCreateCC}>Crear Centro de Costo</Button>
              </Modal.Body>
          </Modal>

        <Modal show={showAirportModal} onHide={() => setShowAirportModal(false)} size="sm"><Modal.Header closeButton className="py-2"><Modal.Title className="h6 fw-bold">Aeropuertos de Chile</Modal.Title></Modal.Header><Modal.Body><div className="d-grid gap-2">{CHILEAN_AIRPORTS.map(a => <Button key={a.code} variant="outline-primary" size="sm" className="text-start" onClick={() => handleAirportSelect(a)}><Plane size={14} className="me-2"/>{a.code} - {a.name}</Button>)}</div></Modal.Body></Modal>

         <Modal 
             show={showAuthCodeModal} 
             onHide={() => {
                 if (!formData.payment_auth_code) {
                     setFormData(prev => ({ ...prev, payment_method: '' }));
                 }
                 setShowAuthCodeModal(false);
             }}
             backdrop="static"
             keyboard={false}
             size="sm"
             centered
         >
             <Modal.Header>
                 <Modal.Title className="h6 fw-bold">Código de Autorización</Modal.Title>
             </Modal.Header>
             <Modal.Body>
                 <Form.Group>
                     <Form.Label className="small">
                         {formData.payment_method === 'DLocal' 
                             ? "Ingrese el código de autorización dLocal (letras, números y guiones):" 
                             : "Ingrese el código de autorización (solo números):"}
                     </Form.Label>
                     <Form.Control
                         type="text"
                         value={authCodeTemp}
                         onChange={e => {
                             const val = e.target.value;
                             if (formData.payment_method === 'DLocal') {
                                 if (/^[a-zA-Z0-9_\-:]*$/.test(val)) {
                                     setAuthCodeTemp(val);
                                 }
                             } else {
                                 if (/^\d*$/.test(val)) {
                                     setAuthCodeTemp(val);
                                 }
                             }
                         }}
                         placeholder={formData.payment_method === 'DLocal' ? "Ej: DP-8617208" : "Ej: 123456"}
                         autoFocus
                     />
                 </Form.Group>
             </Modal.Body>
             <Modal.Footer className="py-2">
                 <Button 
                     variant="secondary" 
                     size="sm" 
                     onClick={() => {
                         if (!formData.payment_auth_code) {
                             setFormData(prev => ({ ...prev, payment_method: '' }));
                         }
                         setShowAuthCodeModal(false);
                     }}
                 >
                     Cancelar
                 </Button>
                 <Button 
                     variant="primary" 
                     size="sm" 
                     disabled={!authCodeTemp}
                     onClick={() => {
                         setFormData(prev => ({ ...prev, payment_auth_code: authCodeTemp }));
                         setShowAuthCodeModal(false);
                     }}
                 >
                     Aceptar
                 </Button>
             </Modal.Footer>
        </Modal>

        {/* Modal de confirmación para pago al conductor en estado Cancelado con Cargo */}
        <Modal
            show={showDriverPaymentConfirm}
            onHide={() => setShowDriverPaymentConfirm(false)}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title className="h5 fw-bold text-danger">⚠️ Cancelado con Cargo</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-3 text-center">
                <p className="fw-bold mb-3" style={{ fontSize: '1.1rem' }}>¿Se le paga al Chofer?</p>
                <p className="text-muted small">
                    Seleccione cómo desea procesar el costo y el pago del conductor para este servicio cancelado con cargo:
                </p>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center flex-wrap gap-2 py-3">
                <Button 
                    variant="success" 
                    onClick={() => handleDriverPaymentChoice('full')}
                    className="fw-bold"
                >
                    Sí, Pagar Completo (100%)
                </Button>
                <Button 
                    variant="warning" 
                    onClick={() => handleDriverPaymentChoice('half')}
                    className="fw-bold text-white"
                >
                    Pagar Mitad (50%)
                </Button>
                <Button 
                    variant="danger" 
                    onClick={() => handleDriverPaymentChoice('none')}
                    className="fw-bold"
                >
                    No Pagar (0%)
                </Button>
                <Button 
                    variant="secondary" 
                    onClick={() => setShowDriverPaymentConfirm(false)}
                >
                    Cancelar Guardar
                </Button>
            </Modal.Footer>
        </Modal>

        {/* MODAL DE CARTA FORMAL IA PARA BOOKING */}
        <Modal show={showAiEmailModal} onHide={() => setShowAiEmailModal(false)} size="lg" centered>
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title className="h6 fw-bold d-flex align-items-center gap-2">
                    <Sparkles size={18} />
                    Carta Formal de Apelación para Booking.com (Generada con IA)
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {aiEmailData && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small text-muted">ASUNTO RECOMENDADO PARA EL CORREO:</Form.Label>
                            <Form.Control type="text" readOnly value={aiEmailData.subject || ''} className="fw-bold bg-light" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small text-muted">CUERPO DEL CORREO (RESPUESTA EJECUTIVA DE DISPUTA):</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={12}
                                readOnly
                                value={aiEmailData.body || ''}
                                className="bg-light font-monospace small"
                                style={{ fontSize: '12px', lineHeight: '1.5' }}
                            />
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={() => setShowAiEmailModal(false)}>
                    Cerrar
                </Button>
                <Button variant="success" onClick={handleCopyAiEmail} className="d-flex align-items-center gap-1 fw-bold">
                    📋 Copiar Asunto y Cuerpo
                </Button>
            </Modal.Footer>
        </Modal>

        {/* MODAL DE CONFIRMACIÓN DE ASIGNACIÓN FORZADA / EXCEPCIONAL */}
        {forcedDriverPrompt && (
            <Modal 
                show={true} 
                onHide={() => setForcedDriverPrompt(null)} 
                centered 
                size="md"
                style={{ zIndex: 1100 }}
            >
                <Modal.Header closeButton className="bg-warning-subtle border-bottom border-warning">
                    <Modal.Title className="fs-6 fw-bold text-dark d-flex align-items-center gap-2">
                        <AlertTriangle className="text-warning" size={20} />
                        Asignación Excepcional de Conductor
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    <p className="mb-2">
                        El conductor <strong>{forcedDriverPrompt.full_name}</strong> {forcedDriverPrompt.internal_code && `[${forcedDriverPrompt.internal_code}]`} presenta las siguientes restricciones para la fecha/hora de este servicio:
                    </p>
                    <div className="bg-light p-2.5 rounded border border-warning-subtle mb-3">
                        <ul className="mb-0 ps-3 text-danger small">
                            {forcedDriverPrompt.reasons && forcedDriverPrompt.reasons.length > 0 ? (
                                forcedDriverPrompt.reasons.map((r, idx) => (
                                    <li key={idx} className="fw-semibold mb-0.5">{r}</li>
                                ))
                            ) : (
                                <li className="fw-semibold mb-0.5">{forcedDriverPrompt.unavailability_reason}</li>
                            )}
                        </ul>
                    </div>
                    <Alert variant="info" className="small mb-0 py-2">
                        ¿Deseas forzar la asignación de este conductor de forma extraordinaria? Esta acción quedará registrada en la auditoría del servicio.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="bg-light p-2 d-flex justify-content-end gap-2">
                    <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={() => setForcedDriverPrompt(null)}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="warning" 
                        size="sm" 
                        className="fw-bold text-dark"
                        onClick={() => {
                            const selectedDriver = drivers.find(d => d.id === parseInt(forcedDriverPrompt.id));
                            const provCurrency = selectedDriver?.provider?.currency_code || formData.local_currency_code || 'CLP';
                            const currSt = (formData.status || 'pending').toLowerCase();
                            setFormData({
                                ...formData,
                                driver_id: forcedDriverPrompt.id,
                                provider_id: selectedDriver ? (selectedDriver.provider_id || null) : null,
                                force_driver_assignment: true,
                                provider_currency_code: provCurrency,
                                status: ['pending', 'confirmed'].includes(currSt) ? 'assigned' : formData.status
                            });
                            setForcedDriverPrompt(null);
                            toast.warning(`⚠️ Asignación forzada de ${forcedDriverPrompt.full_name} aplicada.`);
                        }}
                    >
                        Confirmar Asignación Forzada
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        </>
    );
};

export default TripModal;
